import Bottleneck from "bottleneck";
import queryString from "query-string";
import { isNullOrUndefined } from "util";
import { getJwt, logUserIn } from "../auth";
import { lsGetItem } from "../LocalStorageWrapper";
import store, { ReduxState } from "../redux";
import { CREATE_ALERT, createAlert, REMOVE_ALERT } from "../redux/reducers/Alerts";
import { DELETE_TOKEN } from "../redux/reducers/Auth";
import { openModal } from "../redux/reducers/ModalManager";
import { fetchLastUsedTemplates } from "../redux/reducers/UserRecentTemplateServer";
import {
  fetchWithTimeout,
  goToNewTab,
  postNavigateNewTab,
  sendGAEvent,
  sleep,
} from "../utils/utils";
import {
  ApiError,
  ApiTableParams,
  InvoiceApprovalStatus,
  CustomFormat,
  CustomFormatType,
  DocType,
  DocuClipperBillingCycle,
  DocuClipperPlan,
  DocuClipperPromotions,
  Document,
  EdgeType,
  ExportType,
  FieldType,
  File,
  FilteredItem,
  Fragment,
  InvoiceApprovalRule,
  Job,
  Page,
  PdfCommands,
  Rectangle,
  ReferredUser,
  SortedItem,
  StripeCoupon,
  StripeCustomer,
  StripePlan,
  StripeSubscription,
  TagCategoryRule,
  TagInvoiceRule,
  TagRegexesType,
  TagRule,
  TagTransactionRule,
  Template,
  TemplateAddedColumnMapping,
  TemplateFieldV2,
  Thumbnail,
  ToolType,
  User,
  VariableLocation,
  VariableLocationEdges,
  XPosition,
  YPosition,
  PotentialMatches,
  MatchGroup,
  Tag,
  ApprovalCheck,
  ApprovalProgress,
  TableQueryParams,
  TableFilterConfig,
  ContractAddonsAttributes,
  SubscriptionItemAttributes,
  TagLevelCreate,
  TagLevelUpdate,
  GetDiscountsResponse,
  InputConfig,
  CreateInputConfigRequest,
  OutputConfig,
  CreateOutputConfigRequest,
  WebhookEventType,
  DriveFoldersResponse,
  CreateDriveFolderRequest,
  CreateDriveFolderResponse,
  GDriveWatchQueuedResponse,
  ProjectProcessedFilesResponse,
} from "./DocuclipperTypes";
import { getUiVersion } from "./Logger";
import { fetchOnboardingSteps } from "src/redux/reducers/OnboardingSteps";
import { openSurvey } from "src/redux/reducers/Survey";

const API_PREFIX = "/api/v1/protected";
const UNAUTH_API_PREFIX = "/api/v1";

export const PERMISSIONS_ERROR = "permissions-error";

//
// limiters
//
const limiter1ReqPerSec = new Bottleneck({
  minTime: 1000, // 1 req / sec
  maxConcurrent: 1,
});

const limiter2ReqsPerSec = new Bottleneck({
  minTime: 500, // 2 reqs / sec
  maxConcurrent: 1,
});

// Add this near the other limiters (around line 70)
const uploadLimiter = new Bottleneck({
  maxConcurrent: 1,  // Only one upload at a time
  minTime: 0  // No minimum time between uploads
});

const withTracking = async (
  eventType: string,
  url: string,
  fetchArgs,
  onprogress?: (e) => any,
  timeout?: number
) => {
  try {
    return withTrackingHelper(
      eventType,
      url,
      fetchArgs,
      10,
      onprogress,
      timeout
    );
  } catch (err) {
    if ((err as any).type === PERMISSIONS_ERROR) {
      // try refreshing the permissions
      const rsp = await getToken();
      if (rsp.token) {
        logUserIn(rsp.token);
        return withTrackingHelper(
          eventType,
          url,
          fetchArgs,
          10,
          onprogress,
          timeout
        );
      } else {
        throw err;
      }
    }
  }
};

function isVersionNewer(version1, version2) {
  const parts1 = version1.split(".");
  const parts2 = version2.split(".");

  for (let i = 0; i < 3; i++) {
    const num1 = parseInt(parts1[i]);
    const num2 = parseInt(parts2[i]);

    if (num1 > num2) {
      return true;
    } else if (num1 < num2) {
      return false;
    }
  }

  // If the versions are equal
  return false;
}

const withTrackingHelper = async (
  eventType: string,
  url: string,
  fetchArgs,
  numRetries: number = 10,
  onprogress?: (e) => any,
  timeout?: number
) => {
  if (!("headers" in fetchArgs)) {
    fetchArgs.headers = {};
  }
  fetchArgs.headers["X-Docuclipper-Ui-Version"] = getUiVersion();
  for (let retry = 0; retry < numRetries; retry += 1) {
    // sendGAEvent({ action: eventType, category: "API request", label: "" });
    // const t0 = Math.round(performance.now());
    let status = 500;
    try {
      const response = await fetchWithTimeout(
        url,
        fetchArgs,
        timeout || 30000,
        onprogress
      );
      status = response.status;
      const backendUiVersion = response.headers.get("X-Docuclipper-Ui-Version");
      const uiVersion = getUiVersion();
      const trialLimit = response.headers.get("X-Docuclipper-Trial-Limit");
      if (trialLimit && !isNaN(trialLimit as any)) {
        const totalTxsHeader = response.headers.get(
          "X-Docuclipper-Total-Transactions"
        );
        const totalTxs =
          totalTxsHeader && !isNaN(totalTxsHeader as any)
            ? Number(totalTxsHeader)
            : null;
        store.dispatch({
          type: "TrialLimitation/setLimitContext",
          payload: {
            limit: Number(trialLimit),
            totalTransactions: totalTxs,
          },
        });
        store.dispatch({
          type: "TrialLimitation/setShowModal",
          payload: true,
        });
        store.dispatch({
          type: "TrialLimitation/setShowBanner",
          payload: true,
        });
      }
      if (
        backendUiVersion &&
        new RegExp("^[\\.\\d]+$").exec(backendUiVersion) &&
        uiVersion &&
        new RegExp("^[\\.\\d]+$").exec(uiVersion) &&
        backendUiVersion !== uiVersion &&
        isVersionNewer(backendUiVersion, uiVersion)
      ) {
        // console.log('backendUiVersion: ', backendUiVersion);
        // console.log('uiVersion: ', uiVersion);


        store.dispatch({
          type: "Announcement/setAnnouncement",
          payload: {
            externalLink: "",
            id: "reloadNewVersion",
            imageKey: "reloadNewVersion",
            link: "",
            subtitle: "There's a new version of the software available",
            title: "New Version Available",
          },
        });
      }
      if ([
        "CreateJob",
        "downloadSpreadsheet",
        "getJobMetadataFields",
        "categorizeJobTransactionsAsync",
      ].includes(eventType)) {
        store.dispatch(fetchOnboardingSteps())
      }
      const contentType = response.headers.get("content-type");
      let response2;
      if (
        response.ok &&
        fetchArgs &&
        fetchArgs.headers &&
        fetchArgs.headers.responseType &&
        fetchArgs.headers.responseType.toLowerCase() === "blob"
      ) {
        response2 = await response.blob();
      } else if (
        contentType &&
        contentType.indexOf("application/json") !== -1
      ) {
        response2 = await response.json();
      } else {
        response2 = await response.text();
      }

      // const value = Math.round(performance.now() - t0);
      // sendGATiming(eventType, value);

      if (status === 429 || status === 502) {
        await sleep(100 * 2 ** retry);
        continue;
      }
      if (status >= 400 && status <= 599) {
        if (status === 401) {
          store.dispatch({
            type: DELETE_TOKEN,
            payload: {},
          });
          // window.location.href = "/login";
        } else if (status === 403) {
          if (response2.code === "SANDBOX_READONLY") {
            store.dispatch(openModal("sandboxReadOnly", {}));
            return;
          }
          const error = new Error(
            response2.errorMessage || "You don't have permission to perform this operation"
          );
          (error as any).type = PERMISSIONS_ERROR;
          throw error;
        }
        if (response2.errorCode === 1034) {
          // Suppress the global "You've run out of pages" banner on the
          // job viewer — the JobOutOfPages screen renders a contextual
          // upgrade UI inline and shows its own alert.
          const onJobViewer =
            typeof window !== "undefined" &&
            /\/jobData(\b|\?)/.test(window.location.pathname + window.location.search);
          if (!onJobViewer) {
            store.dispatch(
              createAlert({
                timeout: 0,
                type: "error",
                messageWithUpdate: `You've run out of pages`,
                message: "",
              })
            );
          }
        }
        if (response2.errorCode === 1042) {
          setTimeout(() => {
            store.dispatch(
              // openUpgradePlanModal(
              //   response2.data.neededPlan,
              //   response2.data.feature
              // )
              createAlert({
                timeout: 0,
                type: "error",
                messageWithUpdate: `Your plan doesn't include this feature`,
                message: "",
              })
            );
          }, 1000)

        }
        if (response2.errorCode === 1105) {
          // Storage plan: read-only access. User attempted to upload, create
          // a job, or re-extract. Surface the upgrade CTA via the same
          // UpgradeAlertBanner used for OutOfPages / FeatureNotIncluded.
          // Defer dispatch so the caller's synchronous catch handler can
          // dispatch its own bare-message alert first; ours then overwrites
          // it (single-slot Alerts reducer) and the user sees the upgrade
          // button. Same pattern as the 1042 branch above.
          setTimeout(() => {
            store.dispatch(
              createAlert({
                timeout: 0,
                type: "error",
                messageWithUpdate: `You're on the Storage plan (read-only). Switch to a conversion plan to process new statements.`,
                message: "",
              })
            );
          }, 0);
        }
        if (response2.errorCode === 1061) {
          store.dispatch(openModal("awsOnboarding", {}));
          return;
        }
        if (response2.errorCode !== undefined && response2.errorCode !== null) {
          throw new ApiError(
            status,
            response2.errorCode,
            response2.errorMessage || response2.message || (status === 403
              ? "You don't have permission to perform this operation"
              : "Error performing operation"),
            response2.data || []
          );
        }
        throw Error(response2.errorMessage || response2.message || (status === 403
          ? "You don't have permission to perform this operation"
          : "Error performing operation"));
      }
      return response2;
    } catch (error) {
      sendGAEvent({ action: eventType, category: "API error", label: "" });
      if (![1034, 1042, 1061, 1064, 1105].includes((error as any).errorCode)) {
        // disable for now
        // store.dispatch({
        //   type: CREATE_ALERT,
        //   payload: {
        //     alert: {
        //       type: "error",
        //       timeout: 0,
        //       message: (error as any).message,
        //     },
        //   },
        // });
      }
      throw error;
    }
  }
  sendGAEvent({ action: eventType, category: "API error", label: "" });
  const message = "Max number of retries reached";
  // disable for now

  // store.dispatch({
  //   type: CREATE_ALERT,
  //   payload: {
  //     alert: {
  //       type: "error",
  //       timeout: 0,
  //       message,
  //     },
  //   },
  // });
  throw new Error(message);
};

const uploadFileHelper = (
  file: File["file"],
  // asyncProcessing: boolean,
  // isCamera: boolean,
  // customerId: number | null,
  endpoint: string,
  onprogress?: (e) => any,
  timeout?: number
) => {
  const data = new FormData();
  data.append("document", file as any);

  return withTracking(
    "UploadedDocument",
    `${API_PREFIX}/${endpoint}`,
    {
      method: "POST",
      body: data,
      headers: {
        Authorization: `Bearer ${getJwt()}`,
      },
    },
    onprogress,
    timeout
  );
};

export const uploadFile = (
  file: File["file"],
  endpoint: string,
  onprogress?: (e) => any
): Promise<Document> => {
  return uploadLimiter.schedule(() =>
    uploadFileHelper(file, endpoint, onprogress, 1200000).then(
      (rsp) => rsp.document
    )
  );
};

export const uploadCompressedFile = ({
  content,
  file,
  onprogress,
}: {
  content: string;
  file: File;
  onprogress?: (e) => any;
}) => {
  const data = new FormData();
  data.append("metadata", JSON.stringify(file));
  data.append("content", content);

  return uploadLimiter.schedule(() =>
    withTracking(
      "UploadedCompressedDocument",
      `${API_PREFIX}/document/compressed`,
      {
        method: "POST",
        body: data,
        headers: {
          Authorization: `Bearer ${getJwt()}`,
        },
      },
      onprogress
    ).then((rsp) => rsp.document)
  );
};

export const parseCsv = ({
  documentId,
  dryRun,
  templateId,
}: {
  documentId: number;
  dryRun: boolean;
  templateId: number;
}) => {
  return withTracking(
    "parseCsv",
    `${API_PREFIX}/template/${templateId}/addedColumnMappings/addItemsCsv`,
    {
      method: "POST",
      headers: {
        Authorization: `Bearer ${getJwt()}`,
        Accept: "application/json",
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ documentId, dryRun }),
    }
  )
    .then((rsp) => rsp.csvLineStatuses)
    .catch((err) => {
      throw err;
    });
};

export const deleteDocument = (documentId: number) => {
  return withTracking(
    "DeletedDocument",
    `${API_PREFIX}/document/${documentId}`,
    {
      method: "DELETE",
      headers: {
        Authorization: `Bearer ${getJwt()}`,
      },
    }
  );
};

export const getDocumentFraudSignals = (documentId) => {
  return withTracking(
    "getDocumentFraudSignals",
    `${API_PREFIX}/document/${documentId}/fraudSignals`,
    {
      method: "GET",
      headers: {
        Authorization: `Bearer ${getJwt()}`,
      },
    }
  );
};

export const getDocumentFraudScore = (documentId) => {
  return withTracking(
    "getDocumentFraudScore",
    `${API_PREFIX}/document/${documentId}/fraudScore`,
    {
      method: "GET",
      headers: {
        Authorization: `Bearer ${getJwt()}`,
      },
    }
  ).then(x => x.score);
};

export const getPageUnthrottled = (
  documentId: number,
  pageNumber: number
): Promise<{ page: Page; data: string }> => {
  return withTracking(
    "GotPage",
    `${API_PREFIX}/document/${documentId}/page/${pageNumber}`,
    {
      headers: {
        Authorization: `Bearer ${getJwt()}`,
      },
    }
  );
};
export const getPage = limiter1ReqPerSec.wrap(getPageUnthrottled);

export const getPageImageUnthrottled = (
  documentId: number,
  pageNumber: number
): Promise<{ page: Page; data: string }> => {
  return withTracking(
    "getPageImage",
    `${API_PREFIX}/document/${documentId}/page/${pageNumber}/image`,
    {
      headers: {
        Authorization: `Bearer ${getJwt()}`,
      },
    }
  );
};
export const getPageImage = limiter1ReqPerSec.wrap(getPageImageUnthrottled);

const getThumbnailsUnthrottled = (
  documentId: number,
  offset: number,
  limit: number = 10
): Promise<Thumbnail[]> => {
  return withTracking(
    "GotThumbnails",
    `${API_PREFIX}/document/${documentId}/thumbnails?offset=${offset}&limit=${limit}`,
    {
      headers: {
        Authorization: `Bearer ${getJwt()}`,
      },
    }
  ).then((response) => {
    const thumbnails = response.thumbnails.map((t) => ({
      height: t.page.height,
      width: t.page.width,
      imageSrc: t.data,
      pageNumber: t.page.pageNumber,
    }));
    return thumbnails;
  });
};
export const getThumbnails = limiter1ReqPerSec.wrap(getThumbnailsUnthrottled);

const getTableUnthrottled = ({
  documentId,
  pageNumber,
  jobId,
  tableConfig,
  fragmentId,
  toolType,
  x0,
  x1,
  y0,
  y1,
  prompt,
}: {
  documentId: number;
  pageNumber: number;
  jobId: number | null;
  tableConfig: TemplateFieldV2["tableConfig"];
  fragmentId: number | null;
  toolType: ToolType;
  x0: number;
  x1: number;
  y0: number;
  y1: number;
  prompt?: string;
}) => {
  const body: Record<string, unknown> = {
    x0,
    x1,
    y0,
    y1,
    jobId,
    tableConfig,
    fragmentId: fragmentId ? parseInt(fragmentId.toString(), 10) : null,
    toolType,
  };
  if (prompt != null && prompt !== "") {
    body.prompt = prompt.trim();
  }
  return withTracking(
    "GotTable",
    `${API_PREFIX}/document/${documentId}/page/${pageNumber}/table`,
    {
      method: "POST",
      headers: {
        Authorization: `Bearer ${getJwt()}`,
        Accept: "application/json",
        "Content-Type": "application/json",
      },
      body: JSON.stringify(body),
    }
  ).then(async (res: { asyncJobId?: string } | Record<string, unknown>) => {
    if (res && typeof (res as { asyncJobId?: string }).asyncJobId === "string") {
      return (await pollAsyncTaskForResult((res as { asyncJobId: string }).asyncJobId)) as Record<string, unknown>;
    }
    return res as Record<string, unknown>;
  });
};

const getVTableUnthrottled = ({
  documentId,
  pageNumber,
  variableLocations,
  tableConfig,
  toolType,
}: {
  documentId: number;
  pageNumber: number;
  variableLocations: VariableLocationEdges[];

  tableConfig: TemplateFieldV2["tableConfig"];

  toolType: ToolType;
}) => {
  return withTracking(
    "GotVTable",
    `${API_PREFIX}/document/${documentId}/page/${pageNumber}/vtable?`,
    {
      method: "POST",
      body: JSON.stringify({
        variableLocations,

        tableConfig,
        toolType,
      }),
      headers: {
        Authorization: `Bearer ${getJwt()}`,
        Accept: "application/json",
        "Content-Type": "application/json",
      },
    }
  );
};

const getFragmentUnthrottled = ({
  documentId,
  pageNumber,
  x0,
  x1,
  y0,
  y1,
  toolType,
  jobId,
  fieldType,
  fragmentId,
  checkLabels,
  prompt,
}: {
  documentId: number;
  pageNumber: number;
  x0: number;
  x1: number;
  y0: number;
  y1: number;
  toolType: string;
  jobId: number | null;
  fieldType: FieldType;
  fragmentId: number | null;
  // rectangleId: string,
  checkLabels: Rectangle["checkLabels"];
  prompt?: string;
}): Promise<Fragment> => {
  const body: Record<string, unknown> = {
    x0,
    x1,
    y0,
    y1,
    toolType,
    jobId,
    fieldType,
    fragmentId: fragmentId ? parseInt(fragmentId.toString(), 10) : null,
    checkLabels,
  };
  if (prompt != null && prompt !== "") {
    body.prompt = prompt.trim();
  }
  return withTracking(
    "GotFragment",
    `${API_PREFIX}/document/${documentId}/page/${pageNumber}/fragment`,
    {
      method: "post",
      headers: {
        Authorization: `Bearer ${getJwt()}`,
        Accept: "application/json",
        "Content-Type": "application/json",
      },
      body: JSON.stringify(body),
    }
  ).then(async (res: { asyncJobId?: string } | Fragment) => {
    if (res && typeof (res as { asyncJobId?: string }).asyncJobId === "string") {
      return (await pollAsyncTaskForResult((res as { asyncJobId: string }).asyncJobId)) as Fragment;
    }
    return res as Fragment;
  });
};

const getVFragmentUnthrottled = ({
  documentId,
  pageNumber,
  variableLocations,
  toolType,
  fieldType,
  tableConfig,
  checkLabels,
}: {
  documentId: number;
  pageNumber: number;
  variableLocations: VariableLocation["variableLocations"];
  toolType: ToolType;
  fieldType: FieldType;
  tableConfig: TemplateFieldV2["tableConfig"];
  checkLabels: TemplateFieldV2["checkLabels"];
}): Promise<{ fragments: Fragment[]; errors: string[] }> => {
  return withTracking(
    "GotVFragment",
    `${API_PREFIX}/document/${documentId}/page/${pageNumber}/vfragment`,
    {
      method: "post",
      headers: {
        Authorization: `Bearer ${getJwt()}`,
        Accept: "application/json",
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        variableLocations,
        toolType,
        fieldType,
        tableConfig,
        checkLabels,
      }),
    }
  );
};
export const getTable = limiter1ReqPerSec.wrap(getTableUnthrottled);
export const getVTable = limiter1ReqPerSec.wrap(getVTableUnthrottled);

export const getFragment = limiter1ReqPerSec.wrap(getFragmentUnthrottled);
export const getVFragment = limiter1ReqPerSec.wrap(getVFragmentUnthrottled);

export const getCustomerSupportInfo = ({ email }: { email: string }) => {
  return withTracking(
    "getCustomerSupportInfo",
    `${API_PREFIX}/support/byEmails/`,
    {
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
        Authorization: `Bearer ${getJwt()}`,
      },
      method: "POST",
      body: JSON.stringify({ emails: [email] }),
    }
  );
};

export const customerSupportUpdatePages = ({
  credits,
  purchasedCredits,
  userId,
}: {
  credits: string;
  purchasedCredits: string;
  userId;
}) => {
  return withTracking(
    "customerSupportUpdatePages",
    `${API_PREFIX}/support/updatePages/`,
    {
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
        Authorization: `Bearer ${getJwt()}`,
      },
      method: "POST",
      body: JSON.stringify({ credits, purchasedCredits, userId }),
    }
  );
};
export const customerSupportUpdateContact = ({
  note,
  userId,
}: {
  note: string;
  userId;
}) => {
  return withTracking(
    "customerSupportUpdateContact",
    `${API_PREFIX}/support/updateContact/`,
    {
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
        Authorization: `Bearer ${getJwt()}`,
      },
      method: "POST",
      body: JSON.stringify({ note, userId }),
    }
  );
};

export const customerSupportDownloadJobHistory = ({ email }: { email: string }) => {
  return withTracking(
    "downloadJobHistory",
    `${API_PREFIX}/support/downloadUserJobHistory`,
    {
      headers: {
        Accept: "text/csv",
        Authorization: `Bearer ${getJwt()}`,
      },
      method: "POST",
      body: JSON.stringify({ email }),
    }
  );
};


export const customerSupportUpdateSubscription = ({
  email, subscriptionId,
}: {
  email: string;
  subscriptionId: string;
}) => {
  return withTracking(
    "customerSupportUpdatePages",
    `${API_PREFIX}/support/updateSubscription/`,
    {
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
        Authorization: `Bearer ${getJwt()}`,
      },
      method: "POST",
      body: JSON.stringify({ email, subscriptionId }),
    }
  );
};

export const customerSupportResetPassword = ({ userId }: { userId }) => {
  return withTracking(
    "customerSupportResetPassword",
    `${API_PREFIX}/support/resetPassword`,
    {
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
        Authorization: `Bearer ${getJwt()}`,
      },
      method: "POST",
      body: JSON.stringify({ userId }),
    }
  );
};

export const customerSupportAddToUnlimitedTrial = ({
  domain,
}: {
  domain: string;
}) => {
  return withTracking(
    "customerSupportAddToUnlimitedTrial",
    `${API_PREFIX}/support/unlimitedTrial`,
    {
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
        Authorization: `Bearer ${getJwt()}`,
      },
      method: "POST",
      body: JSON.stringify({ domain }),
    }
  );
};

export const customerSupportDisableEmail = ({
  email,
}: {
  email: string;
}) => {
  return withTracking(
    "customerSupportDisableEmail",
    `${API_PREFIX}/support/disableEmail`,
    {
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
        Authorization: `Bearer ${getJwt()}`,
      },
      method: "POST",
      body: JSON.stringify({ email }),
    }
  );
};

export const customerSupportAddToDontEmail = ({ email }: { email: string }) => {
  return withTracking(
    "customerSupportAddToDontEmail",
    `${API_PREFIX}/support/dontEmail`,
    {
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
        Authorization: `Bearer ${getJwt()}`,
      },
      method: "POST",
      body: JSON.stringify({ email }),
    }
  );
};

export const customerSupportUploadProspectCsv = ({
  documentId,
  campaignId,
}: {
  documentId: number;
  campaignId: number;
}) => {
  return withTracking(
    "customerSupportUploadProspectCsv",
    `${API_PREFIX}/support/prospectCsv`,
    {
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
        Authorization: `Bearer ${getJwt()}`,
      },
      method: "POST",
      body: JSON.stringify({ documentId, campaignId }),
    }
  );
};

export const customerSupportGetProspectCampaigns = () => {
  return withTracking(
    "customerSupportGetProspectCampaigns",
    `${API_PREFIX}/support/prospectCampaigns`,
    {
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
        Authorization: `Bearer ${getJwt()}`,
      },
      method: "GET",
    }
  ).then((rsp) => rsp.campaigns);
};

export const customerSupportGetProspects = (campaignId) => {
  return withTracking(
    "customerSupportGetProspects",
    `${API_PREFIX}/support/prospects/${campaignId}`,
    {
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
        Authorization: `Bearer ${getJwt()}`,
      },
      method: "GET",
    }
  ).then((rsp) => rsp.prospects);
};

export const customerSupportCreateAccount = ({
  firstName,
  lastName,
  email,
}: {
  firstName: string;
  lastName: string;
  email: string;
}) => {
  return withTracking(
    "customerSupportCreateAccount",
    `${API_PREFIX}/support/createAccount`,
    {
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
        Authorization: `Bearer ${getJwt()}`,
      },
      method: "POST",
      body: JSON.stringify({ firstName, lastName, email }),
    }
  );
};

// ── CSM activation + plan switch ─────────────────────────────────────
// Lets Jasmine close + collect payment during a call. Backed by
// /api/v1/protected/support/csm/* endpoints. No proration anywhere.

export type CsmActivationSession = {
  id: number;
  kind: "activation" | "plan_switch";
  status: "pending" | "link_sent" | "completed" | "failed" | "expired" | "canceled";
  contractId: number | null;
  prospectEmail: string | null;
  prospectFirstName: string | null;
  prospectLastName: string | null;
  stripePriceId: string;
  planNickname: string | null;
  couponId: string | null;
  stripeCustomerId: string | null;
  stripeCheckoutSessionId: string | null;
  stripeSubscriptionId: string | null;
  stripePaymentIntentId: string | null;
  failureReason: string | null;
  amountCents: number | null;
  currency: string | null;
  createdByUserId: number;
  completedAt: string | null;
  createdAt: string;
  updatedAt: string;
};

export type CsmOfferablePrice = {
  stripePriceId: string;
  name: string;
  nickName: string;
  billingCycle: string;
  amount: number;
  currency: string;
  country: string;
  numPages: number;
};

const csmJsonHeaders = () => ({
  Accept: "application/json",
  "Content-Type": "application/json",
  Authorization: `Bearer ${getJwt()}`,
});

export const csmListOfferablePrices = () =>
  withTracking("csmListOfferablePrices", `${API_PREFIX}/support/csm/offerablePrices`, {
    headers: csmJsonHeaders(),
    method: "GET",
  });

export const csmStartActivation = (body: {
  userId?: number;
  prospectEmail?: string;
  prospectFirstName?: string;
  prospectLastName?: string;
  stripePriceId: string;
  couponId?: string;
}) =>
  withTracking("csmStartActivation", `${API_PREFIX}/support/csm/startActivation`, {
    headers: csmJsonHeaders(),
    method: "POST",
    body: JSON.stringify(body),
  });

export const csmChargeSavedPm = (body: {
  userId: number;
  stripePriceId: string;
  couponId?: string;
}) =>
  withTracking("csmChargeSavedPm", `${API_PREFIX}/support/csm/chargeSavedPm`, {
    headers: csmJsonHeaders(),
    method: "POST",
    body: JSON.stringify(body),
  });

export const csmPreviewPlanChange = (body: { userId: number; stripePriceId: string }) =>
  withTracking("csmPreviewPlanChange", `${API_PREFIX}/support/csm/previewPlanChange`, {
    headers: csmJsonHeaders(),
    method: "POST",
    body: JSON.stringify(body),
  });

export const csmSwitchPlan = (body: {
  userId: number;
  stripePriceId: string;
  couponId?: string;
}) =>
  withTracking("csmSwitchPlan", `${API_PREFIX}/support/csm/switchPlan`, {
    headers: csmJsonHeaders(),
    method: "POST",
    body: JSON.stringify(body),
  });

export const csmGetActivation = (id: number) =>
  withTracking("csmGetActivation", `${API_PREFIX}/support/csm/activations/${id}`, {
    headers: csmJsonHeaders(),
    method: "GET",
  });

export const csmListActivations = (limit: number = 25) =>
  withTracking(
    "csmListActivations",
    `${API_PREFIX}/support/csm/activations?limit=${limit}`,
    {
      headers: csmJsonHeaders(),
      method: "GET",
    }
  );

export const csmResendActivationLink = (id: number) =>
  withTracking(
    "csmResendActivationLink",
    `${API_PREFIX}/support/csm/activations/${id}/resend`,
    {
      headers: csmJsonHeaders(),
      method: "POST",
    }
  );

export const csmCreateCoupon = (body: {
  percentOff: number;
  durationMonths: number | null;
}) =>
  withTracking("csmCreateCoupon", `${API_PREFIX}/support/csm/createCoupon`, {
    headers: csmJsonHeaders(),
    method: "POST",
    body: JSON.stringify(body),
  });

export const csmCancelActivation = (id: number) =>
  withTracking(
    "csmCancelActivation",
    `${API_PREFIX}/support/csm/activations/${id}/cancel`,
    {
      headers: csmJsonHeaders(),
      method: "POST",
    }
  );

export const updateFragment = ({
  fragmentId,
  editedText,
  rating,
}: {
  fragmentId: number;
  editedText?: string;
  rating?: "bad" | "tracking";
}) => {
  const req = {} as any;
  if (editedText) {
    req.editedText = editedText;
  }
  if (rating) {
    req.rating = rating;
  }
  return withTracking(
    "UpdateFragment",
    `${API_PREFIX}/fragment/${fragmentId}`,
    {
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
        Authorization: `Bearer ${getJwt()}`,
      },
      method: "POST",
      body: JSON.stringify(req),
    }
  );
};

const getFragmentImageUnthrottled = async ({
  fragmentId,
}: {
  fragmentId: number;
}) => {
  const rsp = await withTracking(
    "GotFragment",
    `${API_PREFIX}/fragment/image?fragmentId=${fragmentId}`,
    {
      headers: {
        Authorization: `Bearer ${getJwt()}`,
      },
    }
  );
  return rsp.fragmentImage;
};

export const getFragmentImage = limiter2ReqsPerSec.wrap(
  getFragmentImageUnthrottled
);

const getFragmentPageUnthrottled = async ({
  documentId,
  pageNumber,
}: {
  documentId: number;
  pageNumber: number;
}) => {
  const rsp = await withTracking(
    "GotFragment",
    `${API_PREFIX}/fragment/page?documentId=${documentId}&pageNumber=${pageNumber}`,
    {
      headers: {
        Authorization: `Bearer ${getJwt()}`,
      },
    }
  );
  return rsp.pageImage;
};

export const getFragmentPage = limiter2ReqsPerSec.wrap(
  getFragmentPageUnthrottled
);

export const sendFeedback = (feedback: string) => {
  return withTracking("SentFeedback", `${API_PREFIX}/feedback`, {
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
      Authorization: `Bearer ${getJwt()}`,
    },
    method: "POST",
    body: JSON.stringify({ feedback }),
  });
};

export const cancelContactSupport = (feedback: any) => {
  return withTracking(
    "cancelContactSupport",
    `${API_PREFIX}/cancelContactSupport`,
    {
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
        Authorization: `Bearer ${getJwt()}`,
      },
      method: "POST",
      body: JSON.stringify({ feedback }),
    }
  );
};

export const getBackDoorData = ({
  context,
  offset,
  limit,
  filtered,
  sorted,
  timerange,
}) => {
  return withTracking(
    "GotBackDoorData",
    `${API_PREFIX}/admin/db/${context}?${queryString.stringify({
      offset,
      limit,
      filtered: JSON.stringify(filtered),
      sorted: JSON.stringify(sorted),
    })}&timerange=${timerange}`,
    {
      headers: {
        Authorization: `Bearer ${getJwt()}`,
      },
    }
  ).then((rsp) => rsp.data);
};
export const getPgbossJobs = ({ offset, limit, filtered, sorted }) => {
  return withTracking(
    "GotAuditLog",
    `${API_PREFIX}/admin/pgboss?${queryString.stringify({
      offset,
      limit,
      filtered: JSON.stringify(filtered),
      sorted: JSON.stringify(sorted),
    })}`,
    {
      headers: {
        Authorization: `Bearer ${getJwt()}`,
      },
    }
  ).then((rsp) => rsp.jobs);
};

export const getIntegrations = () => {
  return withTracking("GotIntegrations", `${API_PREFIX}/integration`, {
    headers: {
      Authorization: `Bearer ${getJwt()}`,
    },
  }).then((rsp) => rsp.integrations);
};

export const getIntegrationFeatureFlags = () => {
  return withTracking(
    "GotIntegrationFeatureFlags",
    `${API_PREFIX}/integration/featureFlags`,
    {
      headers: {
        Authorization: `Bearer ${getJwt()}`,
      },
    }
  );
};

export const testIntegration = (integration) => {
  return withTracking(
    "TestIntegration",
    `${API_PREFIX}/integration/${integration}/test`,
    {
      headers: {
        Authorization: `Bearer ${getJwt()}`,
      },
      method: "POST",
    }
  );
};

export const deleteIntegration = (integration) => {
  return withTracking(
    "DeleteIntegration",
    `${API_PREFIX}/integration/${integration}`,
    {
      headers: {
        Authorization: `Bearer ${getJwt()}`,
      },
      method: "DELETE",
    }
  );
};

// Xero Integration APIs
export type XeroBankAccount = {
  accountID: string;
  code: string;
  name: string;
  type: string;
  status: string;
  bankAccountNumber: string;
  currencyCode: string;
};

export type XeroSyncResult = {
  results: any[];
  errors: { batch: number; error: string }[];
  totalFound: number;
  totalSynced: number;
  totalUpdated: number;
  totalErrors: number;
  alreadySynced: number;
};

export const getXeroBankAccounts = (integrationId: string | number): Promise<XeroBankAccount[]> => {
  return withTracking(
    "GetXeroBankAccounts",
    `${API_PREFIX}/xero/bankAccounts?integrationId=${integrationId}`,
    {
      headers: {
        Authorization: `Bearer ${getJwt()}`,
      },
    }
  ).then((rsp) => rsp.accounts);
};

export const syncXeroBankTransactions = ({
  integrationId,
  bankAccountId,
  jobId,
  l1,
  l2,
  filters,
  batchSize = 50,
}: {
  integrationId: string | number;
  bankAccountId: string;
  jobId?: number;
  l1?: string;
  l2?: string;
  filters?: { field: string; operator: string; value: any }[];
  batchSize?: number;
}): Promise<XeroSyncResult> => {
  return withTracking(
    "SyncXeroBankTransactions",
    `${API_PREFIX}/xero/bankTransactions/sync`,
    {
      method: "POST",
      headers: {
        Authorization: `Bearer ${getJwt()}`,
        Accept: "application/json",
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        integrationId,
        bankAccountId,
        jobId,
        l1,
        l2,
        filters,
        batchSize,
      }),
    }
  );
};

export const getTemplates = ({
  offset = 0,
  limit = 100,
  visibility = "private",
  search = "",
  category = "",
  geo = "",
  time = "",
}: {
  offset: number;
  limit: number;
  visibility: "private" | "public";
  search: string;
  category: string;
  geo: string;
  time: string;
}) => {
  return withTracking(
    "GotTemplates",
    `${API_PREFIX}/template?offset=${offset}&limit=${limit}&search=${search}&visibility=${visibility}&category=${category}&geo=${geo}&time=${time}`,
    {
      headers: {
        Authorization: `Bearer ${getJwt()}`,
      },
    }
  ).then((rsp) => rsp.templates);
};

export const getLastUsedTemplates = ({
  offset = 0,
  limit = 100,
}: {
  offset: number;
  limit: number;
}) => {
  return withTracking(
    "getLastUsedTemplates",
    `${API_PREFIX}/template/lastUsed?offset=${offset}&limit=${limit}`,
    {
      headers: {
        Authorization: `Bearer ${getJwt()}`,
      },
    }
  ).then((rsp) => rsp.templates);
};

export const getLastUsedBids = () => {
  return withTracking("getLastUsedBids", `${API_PREFIX}/bid/lastUsed`, {
    headers: {
      Authorization: `Bearer ${getJwt()}`,
    },
  }).then((rsp) => rsp.bids);
};

export const getTemplateByName = (templateName, offset = 0, limit = 10) => {
  return withTracking(
    "GotTemplateByName",
    `${API_PREFIX}/template?name=${templateName}&visibility=private&offset=${offset}&limit=${limit}`,
    {
      headers: {
        Authorization: `Bearer ${getJwt()}`,
      },
    }
  ).then((rsp) => rsp.templates);
};

export const getAllTemplatesByName = (
  templateName: string,
  userId: number | null,
  offset = 0,
  limit = 10
) => {
  return withTracking(
    "GotTemplateByName",
    `${API_PREFIX}/template?all=1&name=${templateName}&userId=${userId}&offset=${offset}&limit=${limit}&visibility=private`,
    {
      headers: {
        Authorization: `Bearer ${getJwt()}`,
      },
    }
  ).then((rsp) => rsp.templates);
};
export const getTemplate = (templateId): Promise<Template> => {
  return withTracking("GotTemplate", `${API_PREFIX}/template/${templateId}`, {
    headers: {
      Authorization: `Bearer ${getJwt()}`,
    },
  });
};

export const importTemplate = (
  templateId: number,
  templateName: string,
  templateDescription: string,
  documentId: number | null
) => {
  return withTracking(
    "ImportTemplate",
    `${API_PREFIX}/template/${templateId}/import`,
    {
      method: "POST",
      headers: {
        Authorization: `Bearer ${getJwt()}`,
        Accept: "application/json",
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        name: templateName,
        description: templateDescription,
        documentId,
      }),
    }
  );
};

export const createTemplate = (data) => {
  return withTracking("CreateTemplate", `${API_PREFIX}/template`, {
    method: "POST",
    body: JSON.stringify(data),
    headers: {
      Authorization: `Bearer ${getJwt()}`,
      Accept: "application/json",
      "Content-Type": "application/json",
    },
  });
};
export const updateTemplate = (templateId, data) => {
  return withTracking(
    "UpdateTemplate",
    `${API_PREFIX}/template/${templateId}`,
    {
      method: "POST",
      body: JSON.stringify(data),
      headers: {
        Authorization: `Bearer ${getJwt()}`,
        Accept: "application/json",
        "Content-Type": "application/json",
      },
    }
  );
};

export const updateTemplateOwner = ({
  templateId,
  userId,
}: {
  templateId: number;
  userId: number;
}) => {
  return withTracking(
    "updateTemplateOwner",
    `${API_PREFIX}/template/${templateId}/updateOwner`,
    {
      method: "POST",
      body: JSON.stringify({
        userId,
      }),
      headers: {
        Authorization: `Bearer ${getJwt()}`,
        Accept: "application/json",
        "Content-Type": "application/json",
      },
    }
  );
};

export const deleteTemplate = (templateId) => {
  return withTracking(
    "DeleteTemplate",
    `${API_PREFIX}/template/${templateId}`,
    {
      method: "DELETE",
      headers: {
        Authorization: `Bearer ${getJwt()}`,
        Accept: "application/json",
        "Content-Type": "application/json",
      },
    }
  );
};

export const createJob = (data) => {
  const userEmail = (store.getState() as ReduxState).User.user?.email;

  const combineIntoSingleDocument = data.enableMultiPage
    ? "?combineIntoSingleDocument=1"
    : "";
  return withTracking(
    "CreateJob",
    `${API_PREFIX}/job${combineIntoSingleDocument}`,
    {
      method: "POST",
      body: JSON.stringify(data),
      headers: {
        Authorization: `Bearer ${getJwt()}`,
        Accept: "application/json",
        "Content-Type": "application/json",
      },
    },
    () => null,
    60000
  ).then((rsp) => {
    // Add dataLayer push
    if ((window as any).dataLayer) {
      (window as any).dataLayer.push({
        event: 'start_pw',
        pw_user_email: userEmail
      });
      // console.log((window as any).dataLayer);

    }

    getPendingSurvey()
      .then((rsp) => {
        if (rsp?.survey === "jobInProgress") {
          const SURVEY_SHOWN_KEY = 'docuclipper.survey.jobInProgress.shown'
          // Check if survey has already been shown
          const surveyShown = localStorage.getItem(SURVEY_SHOWN_KEY);
          if (!surveyShown) {
            store.dispatch(openSurvey(rsp.survey));
            // Save in localStorage that survey has been shown
            localStorage.setItem(SURVEY_SHOWN_KEY, 'true');
          }
        }
      })
      .catch((err) => null);
    setTimeout(() => fetchLastUsedTemplates()(store.dispatch), 5000);
    return rsp;
  });
};

export const writePdf = (data: {
  templateId: number | string;
  excelDocumentId: number | string;
  jobName: string;
}) => {
  return withTracking("WritePdf", `${API_PREFIX}/document/writePdf`, {
    method: "POST",
    body: JSON.stringify(data),
    headers: {
      Authorization: `Bearer ${getJwt()}`,
      Accept: "application/json",
      "Content-Type": "application/json",
    },
  }).then((rsp) => {
    // store.dispatch(fetchLastUsedTemplates());
    return rsp;
  });
};

export const sendPageViewToBackend = async (path: string, kv = {}) => {
  try {
    await fetch(`${API_PREFIX}/analytics/pageview`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${getJwt()}`,

      },
      body: JSON.stringify({
        path,
        timestamp: new Date().toISOString(),
        ...kv
      }),
    });
  } catch (error) {
    console.error("Failed to send page view:", error);
  }
};

export const updateJob = (jobId: number, data) => {
  return withTracking("CreateJob", `${API_PREFIX}/job/${jobId}`, {
    method: "POST",
    body: JSON.stringify(data),
    headers: {
      Authorization: `Bearer ${getJwt()}`,
      Accept: "application/json",
      "Content-Type": "application/json",
    },
  });
};

export const getJobDailyBalances = (jobId: number, account: string, startDate: string, endDate: string) => {
  return withTracking("CreateJob", `${API_PREFIX}/job/${jobId}/dailyBalances?account=${encodeURIComponent(account)}&startDate=${encodeURIComponent(startDate)}&endDate=${encodeURIComponent(endDate)}`, {
    method: "GET",
    headers: {
      Authorization: `Bearer ${getJwt()}`,
      Accept: "application/json",
      "Content-Type": "application/json",
    },
  });
};



export const getJobDailyBalancesTagLevel = (l1, l2, account: string, startDate: string, endDate: string) => {
  return withTracking("CreateJob", `${API_PREFIX}/job/undefined/dailyBalances?l1=${encodeURIComponent(l1)}&l2=${encodeURIComponent(l2)}&account=${encodeURIComponent(account)}&startDate=${encodeURIComponent(startDate)}&endDate=${encodeURIComponent(endDate)}`, {
    method: "GET",
    headers: {
      Authorization: `Bearer ${getJwt()}`,
      Accept: "application/json",
      "Content-Type": "application/json",
    },
  });
};

export const getJobDailyIncomeExpenses = (jobId: number, account: string, startDate: string, endDate: string) => {
  return withTracking("GetIncomeExpenses", `${API_PREFIX}/job/${jobId}/dailyIncomeExpenses?account=${encodeURIComponent(account)}&startDate=${encodeURIComponent(startDate)}&endDate=${encodeURIComponent(endDate)}`, {
    method: "GET",
    headers: {
      Authorization: `Bearer ${getJwt()}`,
      Accept: "application/json",
      "Content-Type": "application/json",
    },
  });
};

export const getJobDailyIncomeExpensesTagLevel = (l1, l2, account: string, startDate: string, endDate: string) => {
  return withTracking("GetIncomeExpenses", `${API_PREFIX}/job/undefined/dailyIncomeExpenses?l1=${encodeURIComponent(l1)}&l2=${encodeURIComponent(l2)}&account=${encodeURIComponent(account)}&startDate=${encodeURIComponent(startDate)}&endDate=${encodeURIComponent(endDate)}`, {
    method: "GET",
    headers: {
      Authorization: `Bearer ${getJwt()}`,
      Accept: "application/json",
      "Content-Type": "application/json",
    },
  });
};

export const getDailyBalanceReconciliation = (
  jobId: number | string,
  documentId: number | string,
  accountName: string,
  chunk: string
) => {
  return withTracking(
    "GetDailyBalanceReconciliation",
    `${API_PREFIX}/job/${jobId}/dailyBalanceReconciliation?documentId=${encodeURIComponent(
      documentId
    )}&accountName=${encodeURIComponent(accountName)}&chunk=${encodeURIComponent(chunk)}`,
    {
      method: "GET",
      headers: {
        Authorization: `Bearer ${getJwt()}`,
        Accept: "application/json",
        "Content-Type": "application/json",
      },
    }
  );
};

export const getDailyBalanceReconciliationTagLevel = (
  l1: string,
  l2: string,
  documentId: number | string,
  accountName: string,
  chunk: string
) => {
  return withTracking(
    "GetDailyBalanceReconciliation",
    `${API_PREFIX}/job/undefined/dailyBalanceReconciliation?l1=${encodeURIComponent(
      l1
    )}&l2=${encodeURIComponent(l2)}&documentId=${encodeURIComponent(
      documentId
    )}&accountName=${encodeURIComponent(accountName)}&chunk=${encodeURIComponent(
      chunk
    )}`,
    {
      method: "GET",
      headers: {
        Authorization: `Bearer ${getJwt()}`,
        Accept: "application/json",
        "Content-Type": "application/json",
      },
    }
  );
};

export const getTemplateExtractedData = (jobId: number, documentId, pageNumber) => {
  return withTracking("getTemplateExtractedData", `${API_PREFIX}/job/${jobId}/templateExtractedData?documentId=${documentId}&pageNumber=${pageNumber}`, {
    method: "GET",
    headers: {
      Authorization: `Bearer ${getJwt()}`,
      Accept: "application/json",
      "Content-Type": "application/json",
    },
  });
};

export const getTemplateExtractedDataTagLevel = (l1, l2, documentId, pageNumber) => {
  return withTracking("getTemplateExtractedDataTagLevel", `${API_PREFIX}/job/undefined/templateExtractedData?l1=${encodeURIComponent(l1)}&l2=${encodeURIComponent(l2)}&documentId=${documentId}&pageNumber=${pageNumber}`, {
    method: "GET",
    headers: {
      Authorization: `Bearer ${getJwt()}`,
      Accept: "application/json",
      "Content-Type": "application/json",
    },
  });
};

export const downloadFraudReport = (jobId) => {
  return withTracking("downloadFraudReport", `${API_PREFIX}/job/${jobId}/downloadFraudReport`, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${getJwt()}`,
      Accept: "application/json",
      "Content-Type": "application/json",
    },
  });
  // .then((rsp) => {
  //   const url = window.URL.createObjectURL(rsp);
  //   const a = document.createElement("a");
  //   a.style.display = "none";
  //   a.href = url;
  //   a.download = "fraud-report-docuclipper.csv";

  //   document.body.appendChild(a);
  //   a.click();

  //   window.URL.revokeObjectURL(url);

  // });
};


export const downloadFraudReportTagLevel = (l1, l2) => {
  return withTracking("downloadFraudReportTagLevel", `${API_PREFIX}/job/undefined/downloadFraudReport?l1=${encodeURIComponent(l1)}&l2=${encodeURIComponent(l2)}`, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${getJwt()}`,
      Accept: "application/json",
      "Content-Type": "application/json",
    },
  });
  // .then((rsp) => {
  //   const url = window.URL.createObjectURL(rsp);
  //   const a = document.createElement("a");
  //   a.style.display = "none";
  //   a.href = url;
  //   a.download = "fraud-report-docuclipper.csv";

  //   document.body.appendChild(a);
  //   a.click();

  //   window.URL.revokeObjectURL(url);

  // });;
};




export const deleteJob = (jobId: number) => {
  return withTracking("DeleteJob", `${API_PREFIX}/job/${jobId}`, {
    method: "DELETE",
    headers: {
      Authorization: `Bearer ${getJwt()}`,
    },
  });
};

export const resumeJob = (jobId: number) => {
  return withTracking("ResumeJob", `${API_PREFIX}/job/${jobId}/resume`, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${getJwt()}`,
    },
  });
};

export const upgradeSubscriptionInPlace = (
  plan: string,
  billingCycle: string,
) => {
  return withTracking(
    "UpgradeSubscriptionInPlace",
    `${API_PREFIX}/stripe/subscription/upgrade-in-place`,
    {
      method: "POST",
      headers: {
        Authorization: `Bearer ${getJwt()}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ plan, billingCycle }),
    },
  );
};

export const redoJob = (jobId: number) => {
  return withTracking("RedoJob", `${API_PREFIX}/job/${jobId}/redo`, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${getJwt()}`,
    },
  });
};

export const addDocumentsToJob = (jobId: number, documents: number[]) => {
  return withTracking("RedoJob", `${API_PREFIX}/job/${jobId}/addDocuments`, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${getJwt()}`,
    },
    body: JSON.stringify({ documents })
  });
};

export const debugJob = (jobId: number) => {
  return withTracking("debugJob", `${API_PREFIX}/job/${jobId}/debug`, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${getJwt()}`,
    },
  });
};

export const redoJobInBankMode = (jobId: number, useV2: boolean, disableAutoRotation: boolean) => {
  return withTracking(
    "RedoJob",
    `${API_PREFIX}/job/${jobId}/redoInBankMode?useV2=${useV2}&disableAutoRotation=${disableAutoRotation}`,
    {
      method: "POST",
      headers: {
        Authorization: `Bearer ${getJwt()}`,
      },
    }
  );
};

export const redoJobInOldMode = (jobId: number) => {
  return withTracking("RedoJob", `${API_PREFIX}/job/${jobId}/redoInOldMode`, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${getJwt()}`,
    },
  });
};

export const emailJob = (jobId: number) => {
  return withTracking("EmailJob", `${API_PREFIX}/job/${jobId}/email`, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${getJwt()}`,
    },
  });
};

export const exportJob = (
  jobId: number,
  {
    jobType,
    integrationId,
    flattenTables,
    documentIds,
  }: {
    jobType: "FieldsToExcel1" | "FieldsToQuickbooks1";
    integrationId: number | string;
    flattenTables: boolean;
    documentIds: string[];
  }
) => {
  return withTracking(
    "ExportJob",
    `${API_PREFIX}/job/${jobId}/export`,
    {
      method: "POST",
      body: JSON.stringify({ jobType, integrationId, flattenTables }),
      headers: {
        Authorization: `Bearer ${getJwt()}`,
        Accept: "application/json",
        "Content-Type": "application/json",
      },
    },
    undefined,
    300000
  );
};

export const rateJob = (data) => {
  return withTracking("RateJob", `${API_PREFIX}/job/${data.jobId}/rate`, {
    method: "POST",
    body: JSON.stringify(data),
    headers: {
      Authorization: `Bearer ${getJwt()}`,
      Accept: "application/json",
      "Content-Type": "application/json",
    },
  });
};

export const rateJobByDocumentId = (data) => {
  return withTracking("RateJob", `${API_PREFIX}/job/undefined/rate`, {
    method: "POST",
    body: JSON.stringify(data),
    headers: {
      Authorization: `Bearer ${getJwt()}`,
      Accept: "application/json",
      "Content-Type": "application/json",
    },
  });
};

export const getJobs = ({
  type,
  status,
  all,
  top,
  paying,
  templateId,
  offset = 0,
  limit = 10,
  tags = "",
  unassigned = false,
  filters = [],
  sorting = [],
}: {
  type: Job["type"];
  status: Job["status"] | Job["status"][] | null;
  all: boolean;
  top: string;
  paying: boolean;
  templateId?: number;
  offset: number;
  limit: number;
  tags: string;
  unassigned: boolean;
  filters?: TableFilterConfig[];
  sorting?: { field: string; direction: string }[];
}) => {
  // Normalize job type(s) to match backend enum (e.g. "invoice" -> "Invoice", "bank" -> "ExtractData")
  // "generic" and "template" are UI-only distinctions; both are ExtractData on the backend.
  // Also folds in legacy/stale aliases ("BankStatement", "bank_statement", "FieldsToExcel1") that
  // older callers still emit; "all" is treated as "no filter" and gets dropped.
  const JOB_TYPE_MAP: Record<string, string> = {
    invoice: "Invoice",
    extractdata: "ExtractData",
    bank: "ExtractData",
    bankstatement: "ExtractData",
    bank_statement: "ExtractData",
    fieldstoexcel1: "ExtractData",
    fieldstoquickbooks1: "ExtractData",
    generic: "ExtractData",
    template: "ExtractData",
    csv2qbo: "Csv2Qbo",
    form: "Form",
    editdocument: "EditDocument",
    writepdf: "WritePdf",
    all: "",
  };
  const normalizedType = (type || "")
    .split(",")
    .map((t) => t.trim())
    .map((t) => {
      const key = t.toLowerCase();
      return key in JOB_TYPE_MAP ? JOB_TYPE_MAP[key] : t;
    })
    .filter(Boolean)
    .join(",");

  const params: Record<string, string> = {
    offset: offset.toString(),
    limit: limit.toString(),
    tags,
    status: Array.isArray(status) ? status.join(",") : status || "",
  };
  if (normalizedType) {
    params.type = normalizedType;
  }

  if (!isNullOrUndefined(templateId)) {
    params.templateId = templateId.toString();
  }
  if (all) {
    params.all = "1";
  }
  if (top) {
    params.top = top;
  }
  if (paying) {
    params.paying = "1";
  }
  if (unassigned) {
    params.unassigned = "true";
  }

  // Add filters
  if (filters.length > 0) {
    params.filters = JSON.stringify(filters);
  }

  // Add sorting
  if (sorting.length > 0) {
    params.sorting = JSON.stringify(sorting);
  }

  const queryParams = new URLSearchParams(params);

  return withTracking(
    "GotJobs",
    `${API_PREFIX}/job?${queryParams.toString()}`,
    {
      headers: {
        Authorization: `Bearer ${getJwt()}`,
      },
    }
  ).then((rsp) => rsp.jobs);
};

export const getAllTags = (jobId?: any) => {
  return withTracking("getAllTags", `${API_PREFIX}/job/tags2?jobId=${jobId || ''}`, {
    headers: {
      Authorization: `Bearer ${getJwt()}`,
    },
  }).then((rsp) => rsp);
};

export const editJobTags = (jobId: number, newTags: string[]) => {
  return withTracking("editJobTags", `${API_PREFIX}/job/${jobId}/tags2/`, {
    method: "POST",
    body: JSON.stringify({
      tags: newTags,
    }),
    headers: {
      Authorization: `Bearer ${getJwt()}`,
      Accept: "application/json",
      "Content-Type": "application/json",
    },
  });
};

export const getAsyncTask = (asyncTaskId) => {
  return withTracking(
    "getAsyncJobStatus",
    `${API_PREFIX}/asyncTask/${asyncTaskId}`,
    {
      headers: {
        Authorization: `Bearer ${getJwt()}`,
      },
    }
  ).then((rsp) => rsp.asyncTask);
};

const POLL_INTERVAL_MS = 1000;
const ASYNC_TASK_TIMEOUT_MS = 5 * 60 * 1000; // 5 min

/**
 * Poll GET /asyncTask/:asyncJobId until status is Succeeded or Failed.
 * When Succeeded, returns JSON.parse(asyncTask.data).
 * When Failed, throws with asyncTask.errorMessage or a generic message.
 */
export async function pollAsyncTaskForResult(asyncJobId: string): Promise<unknown> {
  const deadline = Date.now() + ASYNC_TASK_TIMEOUT_MS;
  const poll = async (): Promise<unknown> => {
    const asyncTask = await getAsyncTask(asyncJobId);
    if (asyncTask.status === "Succeeded") {
      const data = asyncTask.data;
      if (data == null || data === "") {
        throw new Error("Async task succeeded but had no data");
      }
      return typeof data === "string" ? JSON.parse(data) : data;
    }
    if (asyncTask.status === "Failed") {
      const msg = asyncTask.errorMessage ?? asyncTask.message ?? "Async task failed";
      throw new Error(msg);
    }
    if (Date.now() >= deadline) {
      throw new Error("Async task timed out");
    }
    await sleep(POLL_INTERVAL_MS);
    return poll();
  };
  return poll();
}

export const categorizeJobTransactions = (
  jobId: number,
  tagRegexesSetName: string
) => {
  return withTracking(
    "categorizeJobTransactions",
    `${API_PREFIX}/job/${jobId}/categorize/`,
    {
      method: "POST",
      body: JSON.stringify({
        tagRegexesSetName,
      }),
      headers: {
        Authorization: `Bearer ${getJwt()}`,
        Accept: "application/json",
        "Content-Type": "application/json",
      },
    }
  );
};

export const getCategorizedTransactions = (jobId: number, selectedAccount?: string) => {
  return withTracking(
    "getCategorizedTransactions",
    `${API_PREFIX}/job/${jobId}/categorizedTransactions2/${selectedAccount ? `?selectedAccount=${selectedAccount}` : ''}`,
    {
      method: "get",
      headers: {
        Authorization: `Bearer ${getJwt()}`,
        Accept: "application/json",
        "Content-Type": "application/json",
      },
    }
  );
};

export const getCategorizedTransactionsWithTagLevels = (l1, l2, selectedAccount?: string) => {
  let query = `${selectedAccount ? `?selectedAccount=${selectedAccount}` : ''}`
  query += `${query ? '&' : '?'}l1=${encodeURIComponent(l1)}&l2=${encodeURIComponent(l2)}`
  return withTracking(
    "getCategorizedTransactions",
    `${API_PREFIX}/job/undefined/categorizedTransactions2/${query}`,
    {
      method: "get",
      headers: {
        Authorization: `Bearer ${getJwt()}`,
        Accept: "application/json",
        "Content-Type": "application/json",
      },
    }
  );
};


export const categorizeJobTransactionsAsync = (
  jobId: number,
  tagRegexesSetName: string,
  selectedAccount: string,
  tagRegexIds?: string[],
) => {
  const body: any = {
    tagRegexesSetName,
    selectedAccount
  };
  if (tagRegexIds && tagRegexIds.length > 0) {
    body.tagRegexIds = tagRegexIds;
  }
  return withTracking(
    "categorizeJobTransactionsAsync",
    `${API_PREFIX}/job/${jobId}/categorizeAsync/`,
    {
      method: "POST",
      body: JSON.stringify(body),
      headers: {
        Authorization: `Bearer ${getJwt()}`,
        Accept: "application/json",
        "Content-Type": "application/json",
      },
    }
  );
};

export const fixBankPageWithAIAsync = (
  documentId,
  pageNumber
) => {
  return withTracking(
    "fixPageWithAI",
    `${API_PREFIX}/job/${undefined}/fixPageWithAI/`,
    {
      method: "POST",
      body: JSON.stringify({
        documentId,
        pageNumber
      }),
      headers: {
        Authorization: `Bearer ${getJwt()}`,
        Accept: "application/json",
        "Content-Type": "application/json",
      },
    }
  );
};

export const resetEditedCategories = (
  jobId: number,
  account: string,
  deleteCategory: boolean = false
) => {
  return withTracking(
    "resetEditedCategories",
    `${API_PREFIX}/job/${jobId}/resetEditedCategories/?deleteCategory=${deleteCategory}`,
    {
      method: "POST",
      body: JSON.stringify({
        account
      }),
      headers: {
        Authorization: `Bearer ${getJwt()}`,
        Accept: "application/json",
        "Content-Type": "application/json",
      },
    }
  );
};

export const resetEditedCategoriesTagLevels = (
  l1, l2, account: string, deleteCategory: boolean = false
) => {
  if (!l1 || !l2) {
    return Promise.reject(new Error('l1 and l2 are required'));
  }
  return withTracking(
    "resetEditedCategoriesTagLevels",
    `${API_PREFIX}/job/undefined/resetEditedCategories/?l1=${encodeURIComponent(l1)}&l2=${encodeURIComponent(l2)}&deleteCategory=${deleteCategory}`,
    {
      method: "POST",
      body: JSON.stringify({
        account
      }),
      headers: {
        Authorization: `Bearer ${getJwt()}`,
        Accept: "application/json",
        "Content-Type": "application/json",
      },
    }
  );
};

export const categorizeJobTransactionsAsyncWithTagLevels = (
  l1, l2,
  tagRegexesSetName: string,
  selectedAccount: string,
  tagRegexIds?: string[],
) => {
  const body: any = {
    tagRegexesSetName,
    selectedAccount
  };
  if (tagRegexIds && tagRegexIds.length > 0) {
    body.tagRegexIds = tagRegexIds;
  }
  if (!l1 || !l2) {
    return Promise.reject(new Error('l1 and l2 are required'));
  }
  return withTracking(
    "categorizeJobTransactionsAsync",
    `${API_PREFIX}/job/undefined/categorizeAsync/?l1=${encodeURIComponent(l1)}&l2=${encodeURIComponent(l2)}`,
    {
      method: "POST",
      body: JSON.stringify(body),
      headers: {
        Authorization: `Bearer ${getJwt()}`,
        Accept: "application/json",
        "Content-Type": "application/json",
      },
    }
  );
};

export const payeeCleanupAsync = (
  jobId: number,
  config: any
) => {
  return withTracking(
    "cleanupPayeeAsync",
    `${API_PREFIX}/job/${jobId}/cleanupPayeeAsync/`,
    {
      method: "POST",
      body: JSON.stringify({
        config
      }),
      headers: {
        Authorization: `Bearer ${getJwt()}`,
        Accept: "application/json",
        "Content-Type": "application/json",
      },
    }
  );
};

export const previewAutomaticModeAsync = (
  jobId: number,
  T: any,
  T2: any,
  T3: any,
  T4: any
) => {
  return withTracking(
    "previewAutomaticModeAsync",
    `${API_PREFIX}/job/${jobId}/previewAutomaticModeAsync/`,
    {
      method: "POST",
      body: JSON.stringify({
        T,
        T2,
        T3,
        T4,
      }),
      headers: {
        Authorization: `Bearer ${getJwt()}`,
        Accept: "application/json",
        "Content-Type": "application/json",
      },
    }
  );
};


export const previewAutomaticModeAsyncTagLevel = (
  l1, l2,
  T: any,
  T2: any,
  T3: any,
  T4: any
) => {
  return withTracking(
    "previewAutomaticModeAsync",
    `${API_PREFIX}/job/undefined/previewAutomaticModeAsync/?l1=${encodeURIComponent(l1)}&l2=${encodeURIComponent(l2)}`,
    {
      method: "POST",
      body: JSON.stringify({
        T,
        T2,
        T3,
        T4,
      }),
      headers: {
        Authorization: `Bearer ${getJwt()}`,
        Accept: "application/json",
        "Content-Type": "application/json",
      },
    }
  );
};

export const getTransactionCategories = (jobId: number) => {
  return withTracking(
    "getTransactionCategories",
    `${API_PREFIX}/job/${jobId}/categories/`,
    {
      method: "GET",
      headers: {
        Authorization: `Bearer ${getJwt()}`,
        Accept: "application/json",
        "Content-Type": "application/json",
      },
    }
  );
};

export const getFlowOfFunds = (jobId: number) => {
  return withTracking(
    "getFlowOfFunds",
    `${API_PREFIX}/job/${jobId}/flowOfFunds/`,
    {
      method: "GET",

      headers: {
        Authorization: `Bearer ${getJwt()}`,
        Accept: "application/json",
        "Content-Type": "application/json",
      },
    }
  ).then((x) => x.data);
};


export const getFlowOfFundsTagLevel = (l1, l2) => {
  if (!l1 || !l2) {
    return Promise.reject(new Error('l1 and l2 are required'));
  }
  return withTracking(
    "getFlowOfFunds",
    `${API_PREFIX}/job/undefined/flowOfFunds/?l1=${encodeURIComponent(l1)}&l2=${encodeURIComponent(l2)}`,
    {
      method: "GET",

      headers: {
        Authorization: `Bearer ${getJwt()}`,
        Accept: "application/json",
        "Content-Type": "application/json",
      },
    }
  ).then((x) => x.data);
};
export const updateAccountName = ({
  jobId,
  documentId,
  newAccountName,
  oldAccountName,
}) => {
  return withTracking(
    "updateAccountName",
    `${API_PREFIX}/job/${jobId}/transaction/updateAccountName/`,
    {
      method: "POST",
      body: JSON.stringify({
        documentId,
        newAccountName,
        oldAccountName,
      }),
      headers: {
        Authorization: `Bearer ${getJwt()}`,
        Accept: "application/json",
        "Content-Type": "application/json",
      },
    }
  );
};

export const updateAccountNameTagLevels = ({
  l1, l2,
  documentId,
  newAccountName,
  oldAccountName,
}) => {
  return withTracking(
    "updateAccountName",
    `${API_PREFIX}/job/undefined/transaction/updateAccountName/`,
    {
      method: "POST",
      body: JSON.stringify({
        documentId,
        newAccountName,
        oldAccountName,
        l1, l2
      }),
      headers: {
        Authorization: `Bearer ${getJwt()}`,
        Accept: "application/json",
        "Content-Type": "application/json",
      },
    }
  );
};

export const updateAccountName2 = ({
  jobId,
  chunks,
  newAccountName,
}: {
  jobId: any;
  chunks: { accountName; chunk; documentId }[];
  newAccountName: string;
}) => {
  return withTracking(
    "updateAccountName2",
    `${API_PREFIX}/job/${jobId}/transaction/updateAccountName2/`,
    {
      method: "POST",
      body: JSON.stringify({
        chunks,
        newAccountName,
      }),
      headers: {
        Authorization: `Bearer ${getJwt()}`,
        Accept: "application/json",
        "Content-Type": "application/json",
      },
    }
  );
};

export const updateAccountNameByChunk = ({
  jobId,
  chunk,
  oldAccountName,
  newAccountName,
}) => {
  return withTracking(
    "updateAccountNameByChunk",
    `${API_PREFIX}/job/${jobId}/transaction/updateAccountNameByChunk/`,
    {
      method: "POST",
      body: JSON.stringify({
        chunk,
        newAccountName,
        oldAccountName,
      }),
      headers: {
        Authorization: `Bearer ${getJwt()}`,
        Accept: "application/json",
        "Content-Type": "application/json",
      },
    }
  );
};

export const performBulkEditTagLevel = ({
  l1, l2,
  transactionIds,
  operation,
  params,
}) => {
  return withTracking(
    "updateAccountNameByChunk",
    `${API_PREFIX}/job/undefined/transactions/bulkEdit/?l1=${encodeURIComponent(l1)}&l2=${encodeURIComponent(l2)}`,
    {
      method: "POST",
      body: JSON.stringify({
        transactionIds,
        operation,
        params,
      }),
      headers: {
        Authorization: `Bearer ${getJwt()}`,
        Accept: "application/json",
        "Content-Type": "application/json",
      },
    }
  );
}
export const performBulkEdit = ({
  jobId,
  transactionIds,
  operation,
  params,
}) => {
  return withTracking(
    "updateAccountNameByChunk",
    `${API_PREFIX}/job/${jobId}/transactions/bulkEdit/`,
    {
      method: "POST",
      body: JSON.stringify({
        transactionIds,
        operation,
        params,
      }),
      headers: {
        Authorization: `Bearer ${getJwt()}`,
        Accept: "application/json",
        "Content-Type": "application/json",
      },
    }
  );
};


// Extended response: the handler now returns per-statement metadata and a
// rollup summary alongside the legacy `dateGaps` map. The full object is
// returned so the DateGaps view can render reconciliation status, delta,
// and statement-level detail. Legacy callers that only touched `dateGaps`
// are unaffected because that field is still present.
export type DateGapStatement = {
  documentId: number;
  accountName: string;
  chunk: string;
  startDate: string | null;
  endDate: string | null;
  startBalance: number | null;
  endBalance: number | null;
  isReconciled: boolean;
  reconciliationStatus:
    | "matched"
    | "mismatch"
    | "reconciled"
    | "unreconciled"
    | "unknown";
  delta: number | null;
  expectedEndBalance: number | null;
  txCount: number;
};

export type DateGapsSummary = {
  totalMonths: number;
  coveredMonths: number;
  missingMonths: number;
  totalStatements: number;
  reconciledStatements: number;
  unreconciledStatements: number;
  perAccount: Array<{
    account: string;
    statements: number;
    reconciledStatements: number;
    coveredMonths: number;
    totalMonths: number;
    reconciledPct: number;
    coveragePct: number;
  }>;
};

export type DateGapsResponse = {
  dateGaps: Record<string, Record<string, boolean>>;
  statements: DateGapStatement[];
  summary: DateGapsSummary;
};

export const getJobTransactionsDateGaps = (
  jobId: number,
  startDate: string,
  endDate: string
): Promise<DateGapsResponse> => {
  return withTracking(
    "getJobTransactionsDateGaps",
    `${API_PREFIX}/job/${jobId}/dateGaps?start=${startDate}&end=${endDate}`,
    {
      method: "GET",
      headers: {
        Authorization: `Bearer ${getJwt()}`,
        Accept: "application/json",
        "Content-Type": "application/json",
      },
    }
  );
};

export const getJobTransactionsDateGapsTagLevel = (
  l1, l2,
  startDate: string,
  endDate: string
): Promise<DateGapsResponse> => {
  return withTracking(
    "getJobTransactionsDateGaps",
    `${API_PREFIX}/job/undefined/dateGaps?start=${startDate}&end=${endDate}&l1=${encodeURIComponent(l1)}&l2=${encodeURIComponent(l2)}`,
    {
      method: "GET",
      headers: {
        Authorization: `Bearer ${getJwt()}`,
        Accept: "application/json",
        "Content-Type": "application/json",
      },
    }
  );
};

// ---------------------------------------------------------------------------
// Duplicate bank-transactions detection
// ---------------------------------------------------------------------------
// Surfaces overlapping statements across the same job — i.e. transactions
// that look like duplicates because two imported statements covered an
// overlapping date window for the same account. The backend groups likely
// duplicates by (account, date, amount) with a similarity score and a
// human-readable reason; the UI renders them as a warning chip on the
// transactions page and as a section in the Date Gaps view.
export type DuplicateBankTransactionMember = {
  transactionLineId: number;
  documentId: number;
  description: string;
  pageNumber?: number;
  bbox?: {
    page: number;
    x0: number;
    y0: number;
    x1: number;
    y1: number;
    system?: string;
  } | null;
  // Project-scope responses (jobId='undefined' + l1/l2) include the source
  // jobId so the UI can navigate cross-job. Job-scope responses omit it
  // (or send null), since every member belongs to the requested job.
  jobId?: string | null;
};

export type DuplicateBankTransactionGroup = {
  key: string;
  account: string;
  date: string;
  amount: number;
  similarity: number;
  confidence: number;
  reason: string;
  // True when the group was matched across different account labels — the
  // backend "softens" the account bucket when the (date, amount, description)
  // signature is strong enough that an account-name mismatch is more likely
  // a labeling difference than a real distinction.
  accountSoftened?: boolean;
  members: DuplicateBankTransactionMember[];
};

export type DuplicateBankTransactionsResponse = {
  totalGroups: number;
  totalDuplicateLines: number;
  groups: DuplicateBankTransactionGroup[];
};

export const getDuplicateBankTransactions = (
  jobId: number | string
): Promise<DuplicateBankTransactionsResponse> => {
  return withTracking(
    "getDuplicateBankTransactions",
    `${API_PREFIX}/job/${jobId}/duplicate-bank-transactions`,
    {
      method: "GET",
      headers: {
        Authorization: `Bearer ${getJwt()}`,
        Accept: "application/json",
        "Content-Type": "application/json",
      },
    }
  );
};

// Project-scope variant. Hits the same endpoint with jobId='undefined' and
// the l1/l2 query params, which the backend interprets as "every job in
// this folder/project" and may return groups whose members span multiple
// jobs. Each member carries a jobId so the UI can route cross-job.
export const getDuplicateBankTransactionsForProject = (
  l1: string,
  l2: string
): Promise<DuplicateBankTransactionsResponse> => {
  const qs = `?l1=${encodeURIComponent(l1)}&l2=${encodeURIComponent(l2)}`;
  return withTracking(
    "getDuplicateBankTransactionsForProject",
    `${API_PREFIX}/job/undefined/duplicate-bank-transactions${qs}`,
    {
      method: "GET",
      headers: {
        Authorization: `Bearer ${getJwt()}`,
        Accept: "application/json",
        "Content-Type": "application/json",
      },
    }
  );
};

export const getJob = (jobId: number) => {
  return withTracking("GetJob", `${API_PREFIX}/job/${jobId}`, {
    headers: {
      Authorization: `Bearer ${getJwt()}`,
    },
  });
};

export const getJobBadRatings = (jobId: number) => {
  return withTracking("getJobBadRatings", `${API_PREFIX}/job/${jobId}/ratings`, {
    headers: {
      Authorization: `Bearer ${getJwt()}`,
    },
  }).then(rsp => rsp.jobRatings);;
};

export const getSupersetDashboard = (jobId: number, dashboardName) => {
  return withTracking(
    "GetJob",
    `${API_PREFIX}/job/${jobId}/superset?dashboardName=${dashboardName}`,
    {
      headers: {
        Authorization: `Bearer ${getJwt()}`,
      },
    }
  );
};

export const getJobTransactions = (
  jobId: number
) => {
  return withTracking(
    "getJobTransactions",
    `${API_PREFIX}/job/${jobId}/transactions`,
    {
      headers: {
        Authorization: `Bearer ${getJwt()}`,
      },
    }
  );
};

export const getJobTransactionsWithTagLevels = (
  invoiceId: number
) => {
  return withTracking(
    "getJobTransactions",
    `${API_PREFIX}/job/undefined/transactions?invoiceId=${invoiceId}`,
    {
      headers: {
        Authorization: `Bearer ${getJwt()}`,
      },
    }
  );
};


export const getJobTransactions2 = (
  jobId: number,
  options?: TableQueryParams & {
    callback?: () => void;
  }
) => {
  const queryParams = options ? `?options=${encodeURIComponent(JSON.stringify(options))}` : '';
  return withTracking(
    "getJobTransactions2",
    `${API_PREFIX}/job/${jobId}/transactions2${queryParams}`,
    {
      headers: {
        Authorization: `Bearer ${getJwt()}`,
      },
    }
  );
};

export const getJobTransactions2TagLevels = (
  l1: string,
  l2: string,
  options?: TableQueryParams & {
    callback?: () => void;
  }
) => {
  let queryParams = options ? `?options=${encodeURIComponent(JSON.stringify(options))}` : '';
  queryParams += `${queryParams ? '&' : '?'}l1=${encodeURIComponent(l1)}&l2=${encodeURIComponent(l2)}`
  return withTracking(
    "getJobTransactions2",
    `${API_PREFIX}/job/undefined/transactions2${queryParams}`,
    {
      headers: {
        Authorization: `Bearer ${getJwt()}`,
      },
    }
  );
};

export const getInvoicesTagLevels = (
  l1: string,
  l2: string,
  options?: TableQueryParams & {
    callback?: () => void;
  }
) => {
  let queryParams = options ? `?options=${encodeURIComponent(JSON.stringify(options))}` : '';
  queryParams += `${queryParams ? '&' : '?'}l1=${encodeURIComponent(l1)}&l2=${encodeURIComponent(l2)}`
  return withTracking(
    "getJobTransactions2",
    `${API_PREFIX}/job/undefined/invoices${queryParams}`,
    {
      headers: {
        Authorization: `Bearer ${getJwt()}`,
      },
    }
  );
};

export const getInvoiceWorkflowSummaryTagLevels = (l1: string, l2: string) => {
  return withTracking(
    "getInvoiceWorkflowSummary",
    `${API_PREFIX}/job/undefined/invoices/workflowSummary?l1=${encodeURIComponent(l1)}&l2=${encodeURIComponent(l2)}`,
    {
      headers: {
        Authorization: `Bearer ${getJwt()}`,
      },
    }
  );
};

export const getInvoiceWorkflowTagLevels = (
  l1: string,
  l2: string,
  tab: "inbox" | "exceptions" | "approved" | "paid" | "all" = "inbox",
  page = 1,
  pageSize = 100
) => {
  return withTracking(
    "getInvoiceWorkflow",
    `${API_PREFIX}/job/undefined/invoices/workflow?l1=${encodeURIComponent(l1)}&l2=${encodeURIComponent(l2)}&tab=${encodeURIComponent(tab)}&page=${page}&pageSize=${pageSize}`,
    {
      headers: {
        Authorization: `Bearer ${getJwt()}`,
      },
    }
  );
};

export const getInvoiceAnalyticsTagLevels = (
  l1: string,
  l2: string,
  options?: {
    interval?: "week" | "month";
    startDate?: string;
    endDate?: string;
    topVendors?: number;
  }
) => {
  const query = new URLSearchParams();
  query.set("l1", l1);
  query.set("l2", l2);
  if (options?.interval) query.set("interval", options.interval);
  if (options?.startDate) query.set("startDate", options.startDate);
  if (options?.endDate) query.set("endDate", options.endDate);
  if (options?.topVendors) query.set("topVendors", String(options.topVendors));

  return withTracking(
    "getInvoiceAnalytics",
    `${API_PREFIX}/job/undefined/invoices/analytics?${query.toString()}`,
    {
      headers: {
        Authorization: `Bearer ${getJwt()}`,
      },
    }
  );
};

export const getJobMatchingTransactions = (jobId: number) => {
  return withTracking(
    "getJobMatchingTransactions",
    `${API_PREFIX}/job/${jobId}/transactions/matching`,
    {
      headers: {
        Authorization: `Bearer ${getJwt()}`,
      },
    }
  );
};

export const getJobMatchingTransactionsTagLevel = (l1, l2) => {
  return withTracking(
    "getJobMatchingTransactions",
    `${API_PREFIX}/job/undefined/transactions/matching?l1=${encodeURIComponent(l1)}&l2=${encodeURIComponent(l2)}`,
    {
      headers: {
        Authorization: `Bearer ${getJwt()}`,
      },
    }
  );
};

export const createMatchingTransactions = (jobId: number, matches: any[]) => {
  return withTracking(
    "getJobMatchingTransactions",
    `${API_PREFIX}/job/${jobId}/transactions/createMatching`,
    {
      headers: {
        Authorization: `Bearer ${getJwt()}`,
      },
      method: "POST",
      body: JSON.stringify({ matches }),
    }
  );
};

export const createMatchingTransactionsTagLevel = (l1, l2, matches: any[]) => {
  return withTracking(
    "getJobMatchingTransactions",
    `${API_PREFIX}/job/undefined/transactions/createMatching?l1=${encodeURIComponent(l1)}&l2=${encodeURIComponent(l2)}`,
    {
      headers: {
        Authorization: `Bearer ${getJwt()}`,
      },
      method: "POST",
      body: JSON.stringify({ matches }),
    }
  );
};

export const approveMatchingTransactions = (jobId: number, matchId) => {
  return withTracking(
    "getJobMatchingTransactions",
    `${API_PREFIX}/job/${jobId}/transactions/matching/${matchId}/approve`,
    {
      headers: {
        Authorization: `Bearer ${getJwt()}`,
      },
      method: "POST",
      body: JSON.stringify({}),
    }
  );
};

export const rejectMatchingTransactions = (jobId: number, matchId) => {
  return withTracking(
    "getJobMatchingTransactions",
    `${API_PREFIX}/job/${jobId}/transactions/matching/${matchId}/reject`,
    {
      headers: {
        Authorization: `Bearer ${getJwt()}`,
      },
      method: "POST",
      body: JSON.stringify({}),
    }
  );
};

export const matchTransactions = (jobId: number, days: number) => {
  return withTracking(
    "matchTransactions",
    `${API_PREFIX}/job/${jobId}/transactions/matching`,
    {
      headers: {
        Authorization: `Bearer ${getJwt()}`,
      },
      method: "POST",
      body: JSON.stringify({ days }),
    }
  );
};

export const matchTransactionsTagLevel = (l1, l2, days: number) => {
  return withTracking(
    "matchTransactions",
    `${API_PREFIX}/job/undefined/transactions/matching?l1=${encodeURIComponent(l1)}&l2=${encodeURIComponent(l2)}`,
    {
      headers: {
        Authorization: `Bearer ${getJwt()}`,
      },
      method: "POST",
      body: JSON.stringify({ days }),
    }
  );
};

// Recurring transactions
export const getRecurringTransactions = (jobId: number) => {
  return withTracking(
    "getRecurringTransactions",
    `${API_PREFIX}/job/${jobId}/recurring-transactions`,
    {
      headers: {
        Authorization: `Bearer ${getJwt()}`,
      },
    }
  );
};

export const getRecurringTransactionsTagLevel = (l1: string, l2: string) => {
  return withTracking(
    "getRecurringTransactions",
    `${API_PREFIX}/job/undefined/recurring-transactions?l1=${encodeURIComponent(l1)}&l2=${encodeURIComponent(l2)}`,
    {
      headers: {
        Authorization: `Bearer ${getJwt()}`,
      },
    }
  );
};

export const detectRecurringTransactions = (jobId: number) => {
  return withTracking(
    "detectRecurringTransactions",
    `${API_PREFIX}/job/${jobId}/recurring-transactions/detect`,
    {
      headers: {
        Authorization: `Bearer ${getJwt()}`,
      },
      method: "POST",
    }
  );
};

export const detectRecurringTransactionsTagLevel = (l1: string, l2: string) => {
  return withTracking(
    "detectRecurringTransactions",
    `${API_PREFIX}/job/undefined/recurring-transactions/detect?l1=${encodeURIComponent(l1)}&l2=${encodeURIComponent(l2)}`,
    {
      headers: {
        Authorization: `Bearer ${getJwt()}`,
      },
      method: "POST",
    }
  );
};

export const getRecurringTransactionTransactions = (jobId: number, recurringTransactionId: number) => {
  return withTracking(
    "getRecurringTransactionTransactions",
    `${API_PREFIX}/job/${jobId}/recurring-transactions/${recurringTransactionId}/transactions`,
    {
      headers: {
        Authorization: `Bearer ${getJwt()}`,
      },
    }
  );
};

export const getRecurringTransactionTransactionsTagLevel = (
  l1: string,
  l2: string,
  recurringTransactionId: number
) => {
  return withTracking(
    "getRecurringTransactionTransactions",
    `${API_PREFIX}/job/undefined/recurring-transactions/${recurringTransactionId}/transactions?l1=${encodeURIComponent(l1)}&l2=${encodeURIComponent(l2)}`,
    {
      headers: {
        Authorization: `Bearer ${getJwt()}`,
      },
    }
  );
};

export const updateJobTransaction = (
  jobId: number,
  transactionId: number,
  params: {
    included?: boolean;
    col?: number;
    newValue?: string;
    editedCategory?: string;
    tags?: string[];
    account?: string;
  }
) => {
  return withTracking(
    "updateJobTransaction",
    `${API_PREFIX}/job/${jobId}/transactions/${transactionId}`,
    {
      method: "POST",
      body: JSON.stringify({
        ...params,
        newValue: params.newValue?.toString(),
      }),
      headers: {
        Authorization: `Bearer ${getJwt()}`,
        Accept: "application/json",
        "Content-Type": "application/json",
      },
    }
  );
};

export const updateJobTransactionTagLevel = (
  transactionId: number,
  params: {
    included?: boolean;
    col?: number;
    newValue?: string;
    editedCategory?: string;
    tags?: string[];
    account?: string;
  }
) => {
  return withTracking(
    "updateJobTransaction",
    `${API_PREFIX}/job/undefined/transactions/${transactionId}`,
    {
      method: "POST",
      body: JSON.stringify({
        ...params,
        newValue: params.newValue?.toString(),
      }),
      headers: {
        Authorization: `Bearer ${getJwt()}`,
        Accept: "application/json",
        "Content-Type": "application/json",
      },
    }
  );
};

export const addTagToTransactions = (
  jobId: number,
  tag: string,
  transactionIds: any[]
) => {
  return withTracking(
    "addTagToTransactions",
    `${API_PREFIX}/job/${jobId}/transactions/addTagToTransactions`,
    {
      method: "POST",
      body: JSON.stringify({
        tag,
        transactionIds,
      }),
      headers: {
        Authorization: `Bearer ${getJwt()}`,
        Accept: "application/json",
        "Content-Type": "application/json",
      },
    }
  );
};

export const removeTagFromTransactions = (
  jobId: number,
  tag: string,
  transactionIds: any[]
) => {
  return withTracking(
    "removeTagFromTransactions",
    `${API_PREFIX}/job/${jobId}/transactions/removeTagFromTransactions`,
    {
      method: "POST",
      body: JSON.stringify({
        tag,
        transactionIds,
      }),
      headers: {
        Authorization: `Bearer ${getJwt()}`,
        Accept: "application/json",
        "Content-Type": "application/json",
      },
    }
  );
};

export const getJobMetadataFields = (jobId: number) => {
  return withTracking(
    "getJobMetadataFields",
    `${API_PREFIX}/job/${jobId}/metadataFields`,
    {
      headers: {
        Authorization: `Bearer ${getJwt()}`,
      },
    }
  );
};

export const getJobMetadataFieldsWithTagLevels = (l1, l2) => {
  return withTracking(
    "getJobMetadataFields",
    `${API_PREFIX}/job/undefined/metadataFields?l1=${encodeURIComponent(l1)}&l2=${encodeURIComponent(l2)}`,
    {
      headers: {
        Authorization: `Bearer ${getJwt()}`,
      },
    }
  );
};

export const updateJobMetadataField = (
  jobId: number,
  documentId: number,
  name: string,
  newValue: string,
  accountName: string,
  chunk: string
) => {
  return withTracking(
    "updateJobMetadataField",
    `${API_PREFIX}/job/${jobId}/updateMetadataField`,
    {
      method: "POST",
      body: JSON.stringify({
        documentId,
        name,
        newValue: newValue.toString(),
        accountName,
        chunk,
      }),
      headers: {
        Authorization: `Bearer ${getJwt()}`,
        Accept: "application/json",
        "Content-Type": "application/json",
      },
    }
  );
};

export const updateJobMetadataFieldTagLevel = (
  documentId: number,
  name: string,
  newValue: string,
  accountName: string,
  chunk: string
) => {
  return withTracking(
    "updateJobMetadataField",
    `${API_PREFIX}/job/undefined/updateMetadataField`,
    {
      method: "POST",
      body: JSON.stringify({
        documentId,
        name,
        newValue: newValue.toString(),
        accountName,
        chunk,
      }),
      headers: {
        Authorization: `Bearer ${getJwt()}`,
        Accept: "application/json",
        "Content-Type": "application/json",
      },
    }
  );
};



export const getJobDocuments = (jobId: number) => {
  return withTracking(
    "GetJobDocuments",
    `${API_PREFIX}/job/${jobId}/documents`,
    {
      headers: {
        Authorization: `Bearer ${getJwt()}`,
      },
    }
  ).then((rsp) => rsp.documents);
};

export const getJobDocumentsTagLevel = (l1: string, l2: string, fieldName: 'bankMode' | 'invoiceMode' | 'formMode' | 'generic' | 'template' | 'brokerageMode') => {
  return withTracking(
    "GetJobDocuments",
    `${API_PREFIX}/job/undefined/documents?l1=${encodeURIComponent(l1)}&l2=${encodeURIComponent(l2)}&fieldName=${fieldName}`,
    {
      headers: {
        Authorization: `Bearer ${getJwt()}`,
      },
    }
  ).then((rsp) => rsp.documents);
};

export const getJobDocumentsByTagLevels = (jobId: number) => {
  return Promise.resolve([{
    documentId: '2606742',
    originalname: 'last.csv',
    mimetype: 'text/csv'
  }, {
    documentId: '2606740',
    originalname: 'last.pdf',
    mimetype: 'application/pdf'
  }])
};

export const getJobData = ({
  jobId,
  pageNumber,
  documentId,
  offset = 0,
  limit = 10,
}: {
  jobId: number;
  pageNumber?: number;
  documentId?: number;
  offset: number;
  limit: number;
}) => {
  return withTracking(
    "GetJobData",
    `${API_PREFIX}/job/${jobId}/data?offset=${offset}&limit=${limit}${pageNumber !== undefined ? `&pageNumber=${pageNumber}` : ""
    }${documentId !== undefined ? `&documentId=${documentId}` : ""}`,
    {
      headers: {
        Authorization: `Bearer ${getJwt()}`,
      },
    }
  ).then((rsp) => rsp.fragments);
};

/**
 * Brokerage holdings — returns positions + per-document summary metadata.
 * Only meaningful when the job's metadata.subType === 'brokerage'.
 */
export const getJobPositions = (jobId: number) => {
  return withTracking(
    "GetJobPositions",
    `${API_PREFIX}/job/${jobId}/positions`,
    {
      headers: {
        Authorization: `Bearer ${getJwt()}`,
      },
    }
  );
};

/**
 * Brokerage holdings Excel download. Returns a Blob for use with file-saver.
 * Calls GET /protected/job/:jobId/positions/excel and reads the response as
 * a binary blob (NOT JSON, which is what withTracking does).
 */
export const downloadJobPositionsExcel = async (jobId: number): Promise<Blob> => {
  const url = `${API_PREFIX}/job/${jobId}/positions/excel`;
  const rsp = await fetch(url, {
    headers: { Authorization: `Bearer ${getJwt()}` },
  });
  if (!rsp.ok) {
    throw new Error(`Download failed: HTTP ${rsp.status}`);
  }
  return await rsp.blob();
};

export const getJobExports = (jobId, offset = 0, limit = 10) => {
  return withTracking(
    "GetJobData",
    `${API_PREFIX}/job/${jobId}/exports?offset=${offset}&limit=${limit}`,
    {
      headers: {
        Authorization: `Bearer ${getJwt()}`,
      },
    }
  ).then((rsp) => rsp.exports);
};

export const createContract = ({
  adminId,
  name,
  logo,
}: {
  adminId: string;
  name: string;
  logo: string;
}) => {
  return withTracking("CreatedContract", `${API_PREFIX}/admin/contracts`, {
    method: "POST",
    body: JSON.stringify({
      adminId,
      name,
      logo,
    }),
    headers: {
      Authorization: `Bearer ${getJwt()}`,
      Accept: "application/json",
      "Content-Type": "application/json",
    },
  });
};

export const switchToEnterprise = ({ adminId }: { adminId: string }) => {
  return withTracking(
    "CreatedContract",
    `${API_PREFIX}/admin/contracts/switchToEnterprise`,
    {
      method: "POST",
      body: JSON.stringify({
        adminId,
      }),
      headers: {
        Authorization: `Bearer ${getJwt()}`,
        Accept: "application/json",
        "Content-Type": "application/json",
      },
    }
  );
};

// export const createSeat = (
//   contractId: number,
//   email: string,
//   isAdmin: boolean
// ) => {
//   return withTracking("CreatedSeat", `${API_PREFIX}/admin/seats`, {
//     method: "POST",
//     body: JSON.stringify({
//       contractId,
//       email,
//       isAdmin
//     }),
//     headers: {
//       Authorization: `Bearer ${getJwt()}`,
//       Accept: "application/json",
//       "Content-Type": "application/json"
//     }
//   });
// };

export const addSeat = ({
  firstName,
  lastName,
  email,
  plan,
}: {
  firstName: string;
  lastName: string;
  email: string;
  plan: string;
}) => {
  return withTracking("addSeat", `${API_PREFIX}/stripe/seat`, {
    method: "POST",
    body: JSON.stringify({
      firstName,
      lastName,
      email,
      plan,
    }),
    headers: {
      Authorization: `Bearer ${getJwt()}`,
      Accept: "application/json",
      "Content-Type": "application/json",
    },
  });
};

export const disableSeat = (seatId: number) => {
  return withTracking(
    "disableSeat",
    `${API_PREFIX}/stripe/seat/${seatId}/disable`,
    {
      method: "POST",
      headers: {
        Authorization: `Bearer ${getJwt()}`,
        Accept: "application/json",
        "Content-Type": "application/json",
      },
    }
  );
};

export const enableSeat = (seatId: number) => {
  return withTracking(
    "disableSeat",
    `${API_PREFIX}/stripe/seat/${seatId}/enable`,
    {
      method: "POST",
      headers: {
        Authorization: `Bearer ${getJwt()}`,
        Accept: "application/json",
        "Content-Type": "application/json",
      },
    }
  );
};

export const markReviewed = (jobRatingId: number) => {
  return withTracking(
    "markReviewed",
    `${API_PREFIX}/job/rating/${jobRatingId}/markReviewed`,
    {
      method: "POST",
      headers: {
        Authorization: `Bearer ${getJwt()}`,
        Accept: "application/json",
        "Content-Type": "application/json",
      },
    }
  );
};

export const markJobReviewed = (jobId: number) => {
  return withTracking(
    "markJobReviewed",
    `${API_PREFIX}/job/${jobId}/markReviewed`,
    {
      method: "POST",
      headers: {
        Authorization: `Bearer ${getJwt()}`,
        Accept: "application/json",
        "Content-Type": "application/json",
      },
    }
  );
};

export const getSeats = ({
  offset,
  limit,
  filtered,
  sorted,
}: {
  offset: number;
  limit: number;
  filtered: FilteredItem[];
  sorted: SortedItem[];
}) => {
  return withTracking(
    "getSeats",
    `${API_PREFIX}/stripe/seat?${queryString.stringify({
      offset,
      limit,
      filtered: JSON.stringify(filtered),
      sorted: JSON.stringify(sorted),
    })}`,
    {
      method: "GET",
      headers: {
        Authorization: `Bearer ${getJwt()}`,
        Accept: "application/json",
        "Content-Type": "application/json",
      },
    }
  ).then((rsp) => rsp.seats);
};



export const getBadRatings = ({
  offset,
  limit,
  filtered,
  sorted,
}: {
  offset: number;
  limit: number;
  filtered: FilteredItem[];
  sorted: SortedItem[];
}) => {
  return withTracking(
    "getBadRatings",
    `${API_PREFIX}/job/badRatings?${queryString.stringify({
      offset,
      limit,
      filtered: JSON.stringify(filtered),
      sorted: JSON.stringify(sorted),
    })}`,
    {
      method: "GET",
      headers: {
        Authorization: `Bearer ${getJwt()}`,
        Accept: "application/json",
        "Content-Type": "application/json",
      },
    }
  ).then((rsp) => rsp.badRatings);
};

export const createGoogleDocsSpreadsheet = (csvText: string) => {
  return withTracking(
    "CreateGoogleDocsSpreadsheet",
    `${API_PREFIX}/integration/googleDocsSpreadsheets`,
    {
      method: "POST",
      body: JSON.stringify({
        csvText,
      }),
      headers: {
        Authorization: `Bearer ${getJwt()}`,
        Accept: "application/json",
        "Content-Type": "application/json",
      },
    }
  );
};

export const exportTable = (csvText: string, format: "Csv" | "Xlsx") => {
  return withTracking(
    `exportAs${format}`,
    `${API_PREFIX}/integration/exportAs${format}`,
    {
      method: "POST",
      body: JSON.stringify({
        csvText,
      }),
      headers: {
        Authorization: `Bearer ${getJwt()}`,
        Accept: "application/json",
        "Content-Type": "application/json",
        responseType: "blob",
      },
    }
  );
};

export const getOnboardingSteps = () => {
  return withTracking("getOnboardingSteps", `${API_PREFIX}/onboardingSteps`, {
    method: "GET",
    headers: {
      Authorization: `Bearer ${getJwt()}`,
      Accept: "application/json",
      "Content-Type": "application/json",
    },
  }).then((rsp) => {
    rsp.steps.sort((a, b) => a.order - b.order);
    return rsp.steps;
  });
};

export const getTour = (tourId: string) => {
  const key = `docuclipper.tours.${tourId}`;
  const cachedTour = lsGetItem(key);
  if (cachedTour) {
    return Promise.resolve({
      completed: true,
    });
  }

  return withTracking("getTour", `${API_PREFIX}/tour/${tourId}`, {
    method: "GET",
    headers: {
      Authorization: `Bearer ${getJwt()}`,
      Accept: "application/json",
      "Content-Type": "application/json",
    },
  });
};

export const markTourCompleted = (tourId: string, completed: boolean) => {
  return withTracking(
    "MarkTourCompleted",
    `${API_PREFIX}/tour/${tourId}/${completed ? "markCompleted" : "markNotCompleted"
    }`,
    {
      method: "POST",
      headers: {
        Authorization: `Bearer ${getJwt()}`,
        Accept: "application/json",
        "Content-Type": "application/json",
      },
    }
  );
};

//
// stripe
//
export const updateSubscription = (subscription: {
  source: string;
  plan: DocuClipperPlan;
  billingCycle: DocuClipperBillingCycle;
  coupon?: string;
  lm_data?: any;
}) => {
  return withTracking(
    "updateSubscription",
    `${API_PREFIX}/stripe/subscription`,
    {
      method: "POST",
      headers: {
        Authorization: `Bearer ${getJwt()}`,
        Accept: "application/json",
        "Content-Type": "application/json",
      },
      body: JSON.stringify(subscription),
    }
  );
};

export const addCard = (source: string, setAsDefault: boolean) => {
  return withTracking("addCard", `${API_PREFIX}/stripe/card`, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${getJwt()}`,
      Accept: "application/json",
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ source, setAsDefault }),
  });
};

export const removeCard = (source: string) => {
  return withTracking("removeCard", `${API_PREFIX}/stripe/card`, {
    method: "DELETE",
    headers: {
      Authorization: `Bearer ${getJwt()}`,
      Accept: "application/json",
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ source }),
  });
};

export const setDefaultCard = (source: string) => {
  return withTracking("addCard", `${API_PREFIX}/stripe/card/setDefault`, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${getJwt()}`,
      Accept: "application/json",
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ source }),
  });
};

export const buyCredits = (charge: {
  source: string;
  plan: "100" | "250" | "500" | "1000";
}) => {
  return withTracking("BuyCredits", `${API_PREFIX}/stripe/buyCredits`, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${getJwt()}`,
      Accept: "application/json",
      "Content-Type": "application/json",
    },
    body: JSON.stringify(charge),
  });
};

export interface CancelSaveOfferResponse {
  attemptId: number | string;
  offerType:
    | "rebuttal"
    | "downgrade"
    | "downgrade_starter_60"
    | "downgrade_starter_300"
    | "discount"
    | "concierge"
    | "pause"
    | "allow_cancel"
    | "switch_to_annual"
    | "storage_swap"
    | "credit_month";
  offer: {
    headline?: string;
    body?: string;
    ctaLabel?: string;
    declineLabel?: string;
    talkingPoints?: string[];
    downgradePlanName?: string;
    downgradeBillingCycle?: string;
    // Server-authoritative target price for downgrade_starter_* offers; the UI
    // does not echo it back, the backend reads its own attempt record on apply.
    downgradeStripePriceId?: string;
    // Stripe price id used by the new dynamic-downgrade flow; when present the
    // backend can swap the subscription item in place and applied.type comes
    // back as 'downgrade'. When absent, the backend falls back to a redirect
    // to /account/changePlans (applied.type='downgrade_redirect').
    downgradePriceId?: string;
    downgradeCurrency?: string;
    routedByCostOverride?: boolean;
    currentPlanName?: string;
    currentMonthlyAmountFormatted?: string;
    newMonthlyAmountFormatted?: string;
    newPages?: number;
    monthlySavingsFormatted?: string;
    discountPercentOff?: number;
    discountDurationMonths?: number;
    discountedMonthlyAmountFormatted?: string;
    totalSavingsFormatted?: string;
    conciergeTopic?: string;
    conciergeResponseSla?: string;
    pauseDurationMonths?: number;
    pauseResumeDateFormatted?: string;
    pauseDefaultMonths?: number;
    pauseAllowedMonths?: number[];
    // Reconciliation-fix concierge variant
    reason?: string;
    reconciliationFixJobId?: number;
    reconciliationFixJobName?: string;
    annualPlanName?: string;
    annualTotalFormatted?: string;
    annualMonthlyEquivalentFormatted?: string;
    annualSavingsPercent?: number;
    annualStackPercentOff?: number;
    // Storage swap (cancel-rescue $5/mo plan)
    storagePriceId?: string;
    storageMonthlyAmountFormatted?: string;
    storageCurrency?: string;
    // Fix-the-job credit (100% off for 1-2 months while support manually
    // fixes whatever the user flagged). resumeOn is the formatted next-charge
    // date the credit covers up to.
    creditMonths?: number;
    fixSummary?: string;
    resumeOn?: string;
  };
}

// Switch the caller's subscription to the Storage rescue plan ($5/mo).
// Backend returns one of two outcomes:
//   { outcome: 'swapped', storagePriceId, currency } — already done, refresh
//      the page to see the new plan.
//   { outcome: 'checkout', url, storagePriceId, currency } — redirect the
//      browser to `url` to complete a Stripe Checkout flow (used when the
//      user has no active subscription to swap in-place).
export interface SwapToStorageResponse {
  outcome: "swapped" | "checkout";
  url?: string;
  storagePriceId: string;
  currency: string;
}
export const swapToStorage = async (): Promise<SwapToStorageResponse | null> => {
  const resp = await fetch(`${API_PREFIX}/stripe/subscription/swap-to-storage`, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${getJwt()}`,
      Accept: "application/json",
      "Content-Type": "application/json",
    },
    body: JSON.stringify({}),
  });
  if (!resp.ok) return null;
  return (await resp.json()) as SwapToStorageResponse;
};

export const proposeCancelSaveOffer = async (
  cancelReason: string,
  reasonCategory: string | null
): Promise<CancelSaveOfferResponse | null> => {
  const resp = await fetch(`${API_PREFIX}/stripe/subscription/cancel-save-offer`, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${getJwt()}`,
      Accept: "application/json",
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ cancelReason, reasonCategory }),
  });
  if (resp.status === 404) return null; // Feature disabled on backend
  if (!resp.ok) return null;
  return resp.json();
};

export const recordCancelSaveOutcome = async (
  attemptId: number | string,
  outcome: "accepted" | "rejected" | "cancelled_anyway",
  extra?: { pauseMonths?: number }
): Promise<{
  ok: boolean;
  applied?: {
    type: string;
    couponId?: string;
    resumeAt?: string;
    newPriceId?: string;
  };
} | null> => {
  const resp = await fetch(
    `${API_PREFIX}/stripe/subscription/cancel-save-offer/${attemptId}/outcome`,
    {
      method: "POST",
      headers: {
        Authorization: `Bearer ${getJwt()}`,
        Accept: "application/json",
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ outcome, ...(extra || {}) }),
    }
  );
  if (!resp.ok) return null;
  return resp.json();
};

export const cancelSubscription = (reason: string, feedback: string) => {
  return withTracking(
    "CancelSubscription",
    `${API_PREFIX}/stripe/subscription/cancel`,
    {
      method: "POST",
      headers: {
        Authorization: `Bearer ${getJwt()}`,
        Accept: "application/json",
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ reason, feedback }),
    }
  );
};

export const pauseSubscription = (pauseFor: string) => {
  return withTracking(
    "CancelSubscription",
    `${API_PREFIX}/stripe/subscription/pause`,
    {
      method: "POST",
      headers: {
        Authorization: `Bearer ${getJwt()}`,
        Accept: "application/json",
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ pauseFor }),
    }
  );
};

export const resumeSubscription = () => {
  return withTracking(
    "CancelSubscription",
    `${API_PREFIX}/stripe/subscription/resume`,
    {
      method: "POST",
      headers: {
        Authorization: `Bearer ${getJwt()}`,
        Accept: "application/json",
        "Content-Type": "application/json",
      },
      body: JSON.stringify({}),
    }
  );
};

export const reactivateSubscription = () => {
  return withTracking(
    "ReactivateSubscription",
    `${API_PREFIX}/stripe/subscription/reactivate`,
    {
      method: "POST",
      headers: {
        Authorization: `Bearer ${getJwt()}`,
        Accept: "application/json",
        "Content-Type": "application/json",
      },
      body: JSON.stringify({}),
    }
  );
};

export const previewSubscription = (data: { plan: string, billingCycle: string, itemIds: any[] }) => {
  return withTracking(
    "PreviewSubscription",
    `${API_PREFIX}/stripe/previewSubscription`,
    {
      method: "POST",
      headers: {
        Authorization: `Bearer ${getJwt()}`,
        Accept: "application/json",
        "Content-Type": "application/json",
      },
      body: JSON.stringify(data),
    }
  );
};


export const getSubscriptionUnthrottled = (): Promise<StripeSubscription> => {
  return withTracking("GetSubscription", `${API_PREFIX}/stripe/subscription`, {
    method: "GET",
    headers: {
      Authorization: `Bearer ${getJwt()}`,
    },
  });
};

export const getOveragePricing = (): Promise<{
  pages: number;
  pricePerPage: number;
  currency: string;
  enabled: boolean;
  limitPercent: number | null;
  limitMinorMax: number | null;
  limitCurrency: string | null;
  maxAdditionalPages: number | null;
}> => {
  return withTracking("GetSubscription", `${API_PREFIX}/stripe/overagePricing`, {
    method: "GET",
    headers: {
      Authorization: `Bearer ${getJwt()}`,
    },
  });
};

export type OverageStatus =
  | { inOverage: false; currency?: string }
  | {
      inOverage: true;
      enabled: boolean;
      overagePages: number;
      maxOveragePages: number | null;
      amountMinor: number;
      currency: string;
      perPageMinor: number;
      thresholdMinor: number;
      limitPercent: number | null;
      limitMinorMax: number | null;
      limitCurrency: string | null;
      periodEnd: number;
    };

export const getOverageStatus = (): Promise<OverageStatus> => {
  return withTracking("getOverageStatus", `${API_PREFIX}/stripe/overageStatus`, {
    method: "GET",
    headers: {
      Authorization: `Bearer ${getJwt()}`,
    },
  });
};

export type OverageBumpInfo =
  | { eligible: false; reason?: string }
  | {
      eligible: boolean;
      alreadyFired: boolean;
      inOverage: boolean;
      pctUsed: number;
      includedPages: number;
      remainingPages: number;
      planName: string;
      planAmountCents: number;
      currency: string;
      billingCycle: string;
      periodEnd: number;
      canUpgradeTier: boolean;
      nextTier: { stripePriceId: string; name: string; numPages: number; amountCents: number } | null;
    };

export const getOverageBumpInfo = (): Promise<OverageBumpInfo> => {
  return withTracking("getOverageBumpInfo", `${API_PREFIX}/stripe/overageBumpInfo`, {
    method: "GET",
    headers: { Authorization: `Bearer ${getJwt()}` },
  });
};

export const postOverageBump = (body: {
  choice: "reset_cycle" | "upgrade_tier";
  planId?: string;
}): Promise<{ ok: boolean; choice: string; subscriptionId: string }> => {
  return withTracking("postOverageBump", `${API_PREFIX}/stripe/overageBump`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${getJwt()}`,
    },
    body: JSON.stringify(body),
  });
};


export const getDiscounts = (): Promise<GetDiscountsResponse> => {
  return withTracking("getDiscounts", `${API_PREFIX}/stripe/discounts`, {
    method: "GET",
    headers: {
      Authorization: `Bearer ${getJwt()}`,
    },
  });
};

export const getOneOffPricing = (): Promise<{
  pages: number;
  amount: number;
  description: string;
}[]> => {
  return withTracking("GetSubscription", `${API_PREFIX}/stripe/oneOffPricing`, {
    method: "GET",
    headers: {
      Authorization: `Bearer ${getJwt()}`,
    },
  });
};

export const getSubscription = limiter1ReqPerSec.wrap(
  getSubscriptionUnthrottled
);

export const getStripeCustomer = (): Promise<StripeCustomer> => {
  return withTracking("getStripeCustomer", `${API_PREFIX}/stripe/customer`, {
    method: "GET",
    headers: {
      Authorization: `Bearer ${getJwt()}`,
    },
  });
};

export const getInvoices = (
  endingBefore: string,
  startingAfter: string,
  limit = 10
) => {
  return withTracking(
    "GetInvoices",
    `${API_PREFIX}/stripe/invoices?endingBefore=${endingBefore}&startingAfter=${startingAfter}&limit=${limit}`,
    {
      method: "GET",
      headers: {
        Authorization: `Bearer ${getJwt()}`,
      },
    }
  );
};

export const getPlans = ({
  coupon,
}: {
  coupon?: string;
}): Promise<{ plans: StripePlan[]; coupon: StripeCoupon; contractAddons: ContractAddonsAttributes[], subscriptionItems: SubscriptionItemAttributes[] }> => {
  return withTracking(
    "getPlans",
    `${API_PREFIX}/stripe/plans${coupon ? `?coupon=${coupon}` : ""}`,
    {
      method: "GET",
      headers: {
        Authorization: `Bearer ${getJwt()}`,
        Accept: "application/json",
        "Content-Type": "application/json",
      },
    }
  ).then((rsp) => rsp);
};

export const getReferredUsers = (): Promise<{
  referredUsers: ReferredUser[];
  referringUser: ReferredUser | null;
}> => {
  return withTracking(
    "getReferredUsers",
    `${API_PREFIX}/referrals?offset=0&limit=100`,
    {
      method: "GET",
      headers: {
        Authorization: `Bearer ${getJwt()}`,
        Accept: "application/json",
        "Content-Type": "application/json",
      },
    }
  );
};

export const getPromotions = (): Promise<{
  promotion: DocuClipperPromotions | null;
}> => {
  return withTracking("getPromotions", `${API_PREFIX}/promotions`, {
    method: "GET",
    headers: {
      Authorization: `Bearer ${getJwt()}`,
      Accept: "application/json",
      "Content-Type": "application/json",
    },
  });
};

export const getAnnouncements = (): Promise<any> => {
  return withTracking("getAnnouncements", `${API_PREFIX}/announcements`, {
    method: "GET",
    headers: {
      Authorization: `Bearer ${getJwt()}`,
      Accept: "application/json",
      "Content-Type": "application/json",
    },
  }).then((rsp) => rsp.announcement);
};

export const trackAnnouncementEvent = (event: {
  [k: string]: any;
}): Promise<StripePlan[]> => {
  return withTracking(
    "postAnnouncementEvent",
    `${API_PREFIX}/announcements/track`,
    {
      method: "POST",
      headers: {
        Authorization: `Bearer ${getJwt()}`,
        Accept: "application/json",
        "Content-Type": "application/json",
      },
      body: JSON.stringify(event),
    }
  );
};

export const triggerFBConversion = (): Promise<any> => {
  return withTracking("triggerFBConversion", `${API_PREFIX}/fbconversion`, {
    method: "GET",
    headers: {
      Authorization: `Bearer ${getJwt()}`,
      Accept: "application/json",
      "Content-Type": "application/json",
    },
  });
};

export const talkToSales = (): Promise<StripePlan[]> => {
  return withTracking("talkToSales", `${API_PREFIX}/talkToSales`, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${getJwt()}`,
      Accept: "application/json",
      "Content-Type": "application/json",
    },
  });
};

export const getFragments = ({ offset, limit, sorted, filtered }) => {
  const url = `${API_PREFIX}/fragment?${queryString.stringify({
    offset,
    limit,
    filtered: JSON.stringify(filtered),
    sorted: JSON.stringify(sorted),
  })}`;
  return withTracking("getFragments", url, {
    method: "GET",
    headers: {
      Authorization: `Bearer ${getJwt()}`,
      Accept: "application/json",
      "Content-Type": "application/json",
    },
  }).then((rsp) => rsp.fragments);
};

const getToken = () => {
  return withTracking("getToken", `${API_PREFIX}/auth/token`, {
    method: "GET",
    headers: {
      Authorization: `Bearer ${getJwt()}`,
      Accept: "application/json",
      "Content-Type": "application/json",
    },
  });
};

type Credits = {
  credits: number;
  purchasedCredits: number;
};

export const getCredits = (): Promise<Credits> => {
  return withTracking("GetCredits", `${API_PREFIX}/credits`, {
    headers: {
      Authorization: `Bearer ${getJwt()}`,
    },
  });
};

export const superLogin = (userId: string, email: string) => {
  return withTracking("SuperLogin", `${API_PREFIX}/auth/superlogin`, {
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
      Authorization: `Bearer ${getJwt()}`,
    },
    method: "POST",
    body: JSON.stringify({ userId, email }),
  }).then((rsp) => rsp.token);
};


export const doLogin = ({
  email,
  password,
}: {
  email: string;
  password: string;
}) => {
  return withTracking("Login", `${UNAUTH_API_PREFIX}/auth/jwt`, {
    method: "POST",
    body: JSON.stringify({
      email,
      password,
    }),
    headers: {
      Authorization: `Bearer ${getJwt()}`,
      Accept: "application/json",
      "Content-Type": "application/json",
    },
  });
};

export const doSignup = ({
  email,
  password,
  firstName,
  lastName,
  utm_source,
  utm_campaign,
  utm_medium,
  utm_term,
  utm_content,
  companyName,
  jobTitle,
  coupon,
  recaptchaToken,
  guestToken
}: {
  email: string;
  password: string;
  firstName: string;
  lastName: string;
  utm_source?: string;
  utm_campaign?: string;
  utm_medium?: string;
  utm_term?: string;
  utm_content?: string;
  companyName: string;
  jobTitle: string;
  coupon?: string;
  recaptchaToken?: string;
  guestToken?: string;
}) => {
  return withTracking("Signup", `${UNAUTH_API_PREFIX}/auth/signup`, {
    method: "POST",
    body: JSON.stringify({
      email,
      password,
      firstName,
      lastName,
      utm_source,
      utm_campaign,
      utm_medium,
      utm_term,
      utm_content,
      companyName,
      jobTitle,
      coupon: coupon || lsGetItem("docuclipper.coupon"),
      recaptchaToken,
      guestToken
    }),
    headers: {
      Authorization: `Bearer ${getJwt()}`,
      Accept: "application/json",
      "Content-Type": "application/json",
    },
  });
};

export const doEnterpriseSignup = ({
  password,
  token,
}: {
  password: string;
  token: string;
}) => {
  return withTracking("Register", `${UNAUTH_API_PREFIX}/register`, {
    method: "POST",
    body: JSON.stringify({
      password,
      token,
    }),
    headers: {
      Authorization: `Bearer ${getJwt()}`,
      Accept: "application/json",
      "Content-Type": "application/json",
    },
  });
};

export const doResetPassword = ({
  password,
  token,
}: {
  password: string;
  token: string;
}) => {
  return withTracking("Register", `${UNAUTH_API_PREFIX}/resetPassword`, {
    method: "POST",
    body: JSON.stringify({
      password,
      token,
    }),
    headers: {
      Authorization: `Bearer ${getJwt()}`,
      Accept: "application/json",
      "Content-Type": "application/json",
    },
  });
};

export const sendPasswordResetRequest = (email: string) => {
  return withTracking(
    "PasswordResetRequest",
    `${UNAUTH_API_PREFIX}/requestPasswordReset`,
    {
      method: "POST",
      body: JSON.stringify({
        email,
      }),
      headers: {
        Authorization: `Bearer ${getJwt()}`,
        Accept: "application/json",
        "Content-Type": "application/json",
      },
    }
  );
};

export const getDocuments = ({ offset, limit, sorted, filtered }) => {
  return withTracking(
    "GetDocuments",
    `${API_PREFIX}/document?${queryString.stringify({
      offset,
      limit,
      filtered: JSON.stringify(filtered),
      sorted: JSON.stringify(sorted),
    })}`,
    {
      headers: {
        Authorization: `Bearer ${getJwt()}`,
      },
    }
  ).then((rsp) => rsp.documents);
};

export const getAdminDocuments = ({ offset, limit, sorted, filtered }) => {
  return withTracking(
    "GetAdminDocuments",
    `${API_PREFIX}/admin/documents?${queryString.stringify({
      offset,
      limit,
      filtered: JSON.stringify(filtered),
      sorted: JSON.stringify(sorted),
    })}`,
    {
      headers: {
        Authorization: `Bearer ${getJwt()}`,
      },
    }
  ).then((rsp) => rsp.documents);
};

export const downloadDocumentAsAdmin = (documentId: number) => {
  const url = `${process.env.NODE_ENV === "production" ? "" : "http://localhost:8080"
    }${API_PREFIX}/admin/document/${documentId}/download`;
  postNavigateNewTab(url);
};

export const downloadSpreadsheet = (
  jobId: number,
  jobType: Job["jobType"],
  format: ExportType,
  flattenTables: boolean,
  documentIds: string[],
  extraValues: {},
  selectedFieldNames: string[],
  filteredTlIds: string[],
  filters: TableFilterConfig[],
  sortBy: string,
  sortDirection: string,
  fileType?: "CSV" | "XLSX",
  preview?: boolean,
  selectedExportFields?: any
) => {
  let url = `${API_PREFIX}/job/${jobId}/export`
  if (preview) url += "?preview=true"

  const headers = {
    Authorization: `Bearer ${getJwt()}`,
    Accept: "application/json",
    "Content-Type": "application/json",
  }

  if (!preview) {
    headers['responseType'] = "blob"
  }

  return withTracking(
    "downloadSpreadsheet",
    url,
    {
      method: "POST",
      body: JSON.stringify({
        format,
        flattenTables,
        jobType,
        documentIds,
        ...extraValues,
        selectedFieldNames,
        filteredTlIds,
        filters,
        sortBy,
        sortDirection,
        fileType,
        selectedExportFields
      }),
      headers
    }
  );
};

export const downloadSpreadsheetWithTagLevels = (
  l1, l2,
  jobType: Job["jobType"],
  format: ExportType,
  flattenTables: boolean,
  documentIds: string[],
  extraValues: {},
  selectedFieldNames: string[],
  filteredTlIds: string[],
  filters: TableFilterConfig[],
  sortBy: string,
  sortDirection: string,
  fileType?: "CSV" | "XLSX",
  preview?: boolean,
  selectedExportFields?: any
) => {
  let url = `${API_PREFIX}/job/undefined/export?l1=${encodeURIComponent(l1)}&l2=${encodeURIComponent(l2)}`
  if (preview) url += "&preview=true"

  const headers = {
    Authorization: `Bearer ${getJwt()}`,
    Accept: "application/json",
    "Content-Type": "application/json",
  }

  if (!preview) {
    headers['responseType'] = "blob"
  }

  return withTracking(
    "downloadSpreadsheet",
    url,
    {
      method: "POST",
      body: JSON.stringify({
        format,
        flattenTables,
        jobType,
        documentIds,
        ...extraValues,
        selectedFieldNames,
        filteredTlIds,
        filters,
        sortBy,
        sortDirection,
        fileType,
        selectedExportFields
      }),
      headers
    }
  );
};

export const downloadJobHistory = () => {
  return withTracking("downloadJobHistory", `${API_PREFIX}/job/history`, {
    method: "POST",
    body: JSON.stringify({}),
    headers: {
      Authorization: `Bearer ${getJwt()}`,
      responseType: "blob",
      Accept: "application/json",
      "Content-Type": "application/json",
    },
  }).then((rsp) => {
    const url = window.URL.createObjectURL(rsp);
    const a = document.createElement("a");
    a.style.display = "none";
    a.href = url;
    a.download = "docuclipper_job_history.csv";

    document.body.appendChild(a);
    a.click();

    window.URL.revokeObjectURL(url);
  });
};



export const getTotals = (jobId: number, cleanCsvDocumentId: string | null) => {
  return withTracking("getTotals", `${API_PREFIX}/job/${jobId}/getTotals`, {
    method: "POST",
    body: JSON.stringify({
      cleanCsvDocumentId,
    }),
    headers: {
      Authorization: `Bearer ${getJwt()}`,
      Accept: "application/json",
      "Content-Type": "application/json",
    },
  });
};

export const getTotalsByTagLevels = (l1, l2) => {
  return withTracking("getTotals", `${API_PREFIX}/job/undefined/getTotals?l1=${encodeURIComponent(l1)}&l2=${encodeURIComponent(l2)}`, {
    method: "POST",
    body: JSON.stringify({

    }),
    headers: {
      Authorization: `Bearer ${getJwt()}`,
      Accept: "application/json",
      "Content-Type": "application/json",
    },
  });
};

export const downloadDocument = (documentId: number) => {
  return withTracking(
    "downloadDocument",
    `${API_PREFIX}/document/${documentId}/download`,
    {
      method: "POST",
      headers: {
        Authorization: `Bearer ${getJwt()}`,
        responseType: "blob",
        Accept: "application/json",
        "Content-Type": "application/json",
      },
    }
  );
};

export const createCorrection = (
  jobId,
  documentId: string,
  name: string,
  row: number,
  col: number | null,
  newValue: string | null,
  included: boolean,
  toggleSign: boolean,
  divideBy100: boolean,
  multilineIndex?: number,

) => {
  return withTracking(
    "createCorrection",
    `${API_PREFIX}/job/${jobId}/correction`,
    {
      method: "POST",
      headers: {
        Authorization: `Bearer ${getJwt()}`,
        Accept: "application/json",
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        documentId,
        name,
        row,
        col,
        newValue: newValue?.toString(),
        included,
        toggleSign,
        divideBy100,
        multilineIndex,

      }),
    }
  );
};

export const createCorrectionTagLevel = (
  documentId: string,
  l1,
  l2,
  name: string,
  row: number,
  col: number | null,
  newValue: string | null,
  included: boolean,
  toggleSign: boolean,
  divideBy100: boolean,
  multilineIndex?: number,

) => {
  return withTracking(
    "createCorrection",
    `${API_PREFIX}/job/undefined/correction`,
    {
      method: "POST",
      headers: {
        Authorization: `Bearer ${getJwt()}`,
        Accept: "application/json",
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        documentId,
        name,
        row,
        col,
        newValue: newValue?.toString(),
        included,
        toggleSign,
        divideBy100,
        multilineIndex,
        l1,
        l2
      }),
    }
  );
};

export const switchTransactionSigns = (
  jobId: string,
  documentId: string,
  scope: string,
  pageNumber: number
) => {
  return withTracking("switchSigns", `${API_PREFIX}/job/${jobId}/switchSigns`, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${getJwt()}`,
      Accept: "application/json",
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ documentIds: [documentId], scope, pageNumber }),
  });
};

export const switchIncludedExcluded = (
  jobId: string,
  documentId: string,
  scope: string,
  pageNumber: number
) => {
  return withTracking(
    "switchSigns",
    `${API_PREFIX}/job/${jobId}/switchIncludedExcluded`,
    {
      method: "POST",
      headers: {
        Authorization: `Bearer ${getJwt()}`,
        Accept: "application/json",
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ documentIds: [documentId], scope, pageNumber }),
    }
  );
};

export const createTransactions = (
  jobId: string,
  documentId: string,
  transactions: any[]
) => {
  return withTracking(
    "createCorrection",
    `${API_PREFIX}/job/${jobId}/transaction`,
    {
      method: "POST",
      headers: {
        Authorization: `Bearer ${getJwt()}`,
        Accept: "application/json",
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ documentId, transactions }),
    }
  );
};


export const createTransactionsTagLevel = (
  l1, l2,
  documentId: string,
  transactions: any[]
) => {
  return withTracking(
    "createCorrection",
    `${API_PREFIX}/job/undefined/transaction?l1=${encodeURIComponent(l1)}&l2=${encodeURIComponent(l2)}`,
    {
      method: "POST",
      headers: {
        Authorization: `Bearer ${getJwt()}`,
        Accept: "application/json",
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ documentId, transactions }),
    }
  );
};

export const loadPreferenceServer = ({ key }: { key: string }) => {
  return withTracking("loadPreference", `${API_PREFIX}/preference/${key}`, {
    headers: {
      Authorization: `Bearer ${getJwt()}`,
    },
  }).then((rsp) => rsp.preference);
};

export const saveContractdPreferenceServer = ({
  key,
  value,
}: {
  key: string;
  value: string;
}) => {
  return withTracking("saveContractdPreferenceServer", `${API_PREFIX}/contractPreference/${key}`, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${getJwt()}`,
      Accept: "application/json",
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ value }),
  });
};




export const loadContractPreferenceServer = ({ key }: { key: string }) => {
  return withTracking("loadContractPreferenceServer", `${API_PREFIX}/contractPreference/${key}`, {
    headers: {
      Authorization: `Bearer ${getJwt()}`,
    },
  }).then((rsp) => rsp.preference);
};

export const savePreferenceServer = ({
  key,
  value,
}: {
  key: string;
  value: string;
}) => {
  return withTracking("savePreference", `${API_PREFIX}/preference/${key}`, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${getJwt()}`,
      Accept: "application/json",
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ value }),
  });
};

export const editDocumentAsync = ({
  pdfCommands,
}: {
  pdfCommands: PdfCommands;
}) => {
  return withTracking("editDocument", `${API_PREFIX}/document/editAsync`, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${getJwt()}`,
      Accept: "application/json",
      "Content-Type": "application/json",
    },
    body: JSON.stringify(pdfCommands),
  });
};

export const getTemplateAddedColumnMapping = ({
  templateId,
  offset,
  limit,
  sorted,
  filtered,
}: {
  templateId: number;
} & ApiTableParams) => {
  return withTracking(
    "addTemplateAddedColumnMapping",
    `${API_PREFIX}/template/${templateId}/addedColumnMappings?${queryString.stringify(
      {
        offset,
        limit,
        filtered: JSON.stringify(filtered),
        sorted: JSON.stringify(sorted),
      }
    )}`,
    {
      method: "GET",
      headers: {
        Authorization: `Bearer ${getJwt()}`,
        Accept: "application/json",
        "Content-Type": "application/json",
      },
    }
  );
};

export const downloadExampleSpreadsheet = ({
  templateId,
}: {
  templateId: number;
}) => {
  return withTracking(
    "downloadExampleSpreadsheet",
    `${API_PREFIX}/template/${templateId}/downloadExampleSpreadsheet`,
    {
      method: "post",
      headers: {
        Authorization: `Bearer ${getJwt()}`,
        Accept: "application/json",
        "Content-Type": "application/json",
        responseType: "blob",
      },
    }
  );
};

export const addTemplateAddedColumnMapping = ({
  templateId,
  mappings,
}: {
  templateId: number;
  mappings: TemplateAddedColumnMapping[];
}) => {
  return withTracking(
    "addTemplateAddedColumnMapping",
    `${API_PREFIX}/template/${templateId}/addedColumnMappings/addItems`,
    {
      method: "POST",
      headers: {
        Authorization: `Bearer ${getJwt()}`,
        Accept: "application/json",
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ mappings }),
    }
  );
};

export const updateTemplateAddedColumnMapping = ({
  templateId,
  id,
  columnName,
  addedColumnName,
  value,
  addedValue,
}: {
  templateId: number;
  id: number;
  columnName: string;
  value: string;
  addedColumnName: string;
  addedValue: string;
}) => {
  return withTracking(
    "updateTemplateAddedColumnMapping",
    `${API_PREFIX}/template/${templateId}/addedColumnMappings/update/${id}`,
    {
      method: "POST",
      headers: {
        Authorization: `Bearer ${getJwt()}`,
        Accept: "application/json",
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ columnName, addedColumnName, value, addedValue }),
    }
  );
};

export const deleteTemplateAddedColumnMapping = ({
  templateId,
  mappingId,
}: {
  templateId: number;
  mappingId: number;
}) => {
  return withTracking(
    "addTemplateAddedColumnMapping",
    `${API_PREFIX}/template/${templateId}/addedColumnMappings/${mappingId}`,
    {
      method: "DELETE",
      headers: {
        Authorization: `Bearer ${getJwt()}`,
        Accept: "application/json",
        "Content-Type": "application/json",
      },
    }
  );
};

export const updateDocument = ({
  documentId,
  tags,
}: {
  documentId: string;
  tags: { [k: string]: string };
}) => {
  return withTracking(
    "updateDocument",
    `${API_PREFIX}/document/${documentId}/update`,
    {
      method: "POST",
      headers: {
        Authorization: `Bearer ${getJwt()}`,
        Accept: "application/json",
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ tags }),
    }
  );
};

export const getUser = (): Promise<User> => {
  return withTracking("getUser", `${API_PREFIX}/user/`, {
    method: "GET",
    headers: {
      Authorization: `Bearer ${getJwt()}`,
      Accept: "application/json",
      "Content-Type": "application/json",
    },
  });
};

export const resendConfirmationLink = () => {
  return withTracking(
    "resendConfirmationLink",
    `${API_PREFIX}/user/resendConfirmationLink`,
    {
      method: "POST",
      headers: {
        Authorization: `Bearer ${getJwt()}`,
        Accept: "application/json",
        "Content-Type": "application/json",
      },
    }
  );
};

export const updateUser = ({
  userId,
  tags,
}: {
  userId: string;
  tags: { [k: string]: string };
}) => {
  return withTracking("updateUser", `${API_PREFIX}/user/${userId}`, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${getJwt()}`,
      Accept: "application/json",
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ tags }),
  });
};

export const updatePayeeCleanupConfig = (jobId, config) => {
  return withTracking("updatePayeeCleanupConfig", `${API_PREFIX}/jobs/${jobId}/updatePayeeCleanupConfig`, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${getJwt()}`,
      Accept: "application/json",
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ jobId, config }),
  });
};

export const getTemplateFieldNames = ({
  templateId,
}: {
  templateId: number;
}) => {
  return withTracking(
    "getTemplateFieldNames",
    `${API_PREFIX}/template/${templateId}/fields`,
    {
      method: "GET",
      headers: {
        Authorization: `Bearer ${getJwt()}`,
        Accept: "application/json",
        "Content-Type": "application/json",
      },
    }
  ).then((rsp) => rsp.fields);
};

export const getWordsInRectangle = ({
  documentId,
  pageNumber,
  x0,
  x1,
  y0,
  y1,
  edge,
  position,
}: {
  documentId: number;
  pageNumber: number;
  x0: number;
  x1: number;
  y0: number;
  y1: number;
  edge: EdgeType;
  position: XPosition | YPosition;
}): Promise<{ words: string[]; edges: number[] }> => {
  return withTracking(
    "getWordsInRectangle",
    `${API_PREFIX}/document/${documentId}/page/${pageNumber}/wordsInRectangle`,
    {
      method: "post",
      headers: {
        Authorization: `Bearer ${getJwt()}`,
        Accept: "application/json",
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        x0,
        x1,
        y0,
        y1,
        edge,
        position,
      }),
    }
  );
};

export const getEdgesFromWords = ({
  documentId,
  pageNumber,
  edge,
  words,
  position,
  offset,
}: {
  documentId: number;
  pageNumber: number;
  edge: EdgeType;
  words: string[];
  position: YPosition | XPosition;
  offset: number;
}): Promise<{ edges: number[] }> => {
  return withTracking(
    "getEdgesFromWords",
    `${API_PREFIX}/document/${documentId}/page/${pageNumber}/edgesFromWords`,
    {
      method: "post",
      headers: {
        Authorization: `Bearer ${getJwt()}`,
        Accept: "application/json",
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        words,
        edge,
        position,
        offset,
      }),
    }
  );
};

export const updateProfile = ({
  firstName,
  lastName,
  email,
  password,
}: {
  firstName: string;
  lastName: string;
  email: string;
  password: string;
}): Promise<{ token: string }> => {
  return withTracking("updateProfile", `${API_PREFIX}/user/updateProfile`, {
    method: "post",
    headers: {
      Authorization: `Bearer ${getJwt()}`,
      Accept: "application/json",
      "Content-Type": "application/json",
    },
    body: JSON.stringify({
      firstName,
      lastName,
      email,
      password,
    }),
  });
};

export const generatePayingUsersReport = (): Promise<any> => {
  return withTracking(
    "generatePayingUsersReport",
    `${API_PREFIX}/stripe/report/paying`,
    {
      method: "post",
      headers: {
        Authorization: `Bearer ${getJwt()}`,
        Accept: "application/json",
        "Content-Type": "application/json",
        responseType: "blob",
      },
      body: JSON.stringify({}),
    }
  );
};

export const generateMauReport = (): Promise<any> => {
  return withTracking("generateMauReport", `${API_PREFIX}/stripe/report/mau`, {
    method: "post",
    headers: {
      Authorization: `Bearer ${getJwt()}`,
      Accept: "application/json",
      "Content-Type": "application/json",
      responseType: "blob",
    },
    body: JSON.stringify({}),
  });
};

export const createQboInvoice = (
  integrationId: string,
  {
    date,
    customerName,
    lineItems,
  }: {
    date: string;
    customerName: string;
    lineItems: string;
  }
): Promise<any> => {
  return withTracking(
    "createQboInvoice",
    `${API_PREFIX}/job/createQboInvoice`,
    {
      method: "post",
      headers: {
        Authorization: `Bearer ${getJwt()}`,
        Accept: "application/json",
        "Content-Type": "application/json",
        responseType: "blob",
      },
      body: JSON.stringify({
        integrationId,
        invoice: { date, customerName, lineItems },
      }),
    }
  );
};

export const inviteByEmail = (emails: string): Promise<any> => {
  return withTracking("invite", `${API_PREFIX}/invite`, {
    method: "post",
    headers: {
      Authorization: `Bearer ${getJwt()}`,
      Accept: "application/json",
      "Content-Type": "application/json",
    },
    body: JSON.stringify({
      emails,
    }),
  });
};

export const deleteMyAccount = (): Promise<any> => {
  return withTracking("deleteMyAccount", `${API_PREFIX}/user/deleteMyAccount`, {
    method: "post",
    headers: {
      Authorization: `Bearer ${getJwt()}`,
      Accept: "application/json",
      "Content-Type": "application/json",
    },
  });
};

export const deleteUser = (userId: string): Promise<any> => {
  return withTracking("deleteUser", `${API_PREFIX}/user/deleteUser`, {
    method: "post",
    headers: {
      Authorization: `Bearer ${getJwt()}`,
      Accept: "application/json",
      "Content-Type": "application/json",
    },
    body: JSON.stringify({
      userId,
    }),
  });
};

export const restartTrial = (): Promise<any> => {
  return withTracking(
    "restartTrial",
    `${API_PREFIX}/stripe/subscription/restartTrial`,
    {
      method: "post",
      headers: {
        Authorization: `Bearer ${getJwt()}`,
        Accept: "application/json",
        "Content-Type": "application/json",
      },
      body: JSON.stringify({}),
    }
  );
};

export const getColumnConfigs = (templateId: number): Promise<any> => {
  return withTracking(
    "getColumnConfigs",
    `${API_PREFIX}/columnConfig/${templateId}`,
    {
      method: "get",
      headers: {
        Authorization: `Bearer ${getJwt()}`,
        Accept: "application/json",
        "Content-Type": "application/json",
      },
    }
  ).then((x) => x.columnConfigs);
};

export const getCustomFormats = ({
  type,
}): Promise<any> => {
  return withTracking("getCustomFormats", `${API_PREFIX}/customFormat?type=${type}`, {
    method: "get",
    headers: {
      Authorization: `Bearer ${getJwt()}`,
      Accept: "application/json",
      "Content-Type": "application/json",
    },
  }).then((x) => x.customFormats);
};

export const previewCsv = (documentId: number): Promise<any> => {
  // return Promise.resolve({
  //   mapping: {
  //     0: "date",
  //     1: "name",
  //     2: "amount",
  //     3: "amount",
  //     4: "amount",
  //     5: "amount",
  //     6: "amount",
  //     7: "amount",
  //     8: "amount",
  //   },
  //   preview: [
  //     [
  //       "date",
  //       "description",
  //       "amount",
  //       "desc 1",
  //       "100.23",
  //       "desc 1",
  //       "100.23",
  //       "desc 1",
  //       "100.23",
  //     ],
  //     [
  //       "2022-01-02",
  //       "desc 1",
  //       "100.23",
  //       "desc 1",
  //       "100.23",
  //       "desc 1",
  //       "100.23",
  //       "desc 1",
  //       "100.23",
  //     ],
  //     [
  //       "2022-01-03",
  //       "desc 2",
  //       "100.78",
  //       "desc 1",
  //       "100.23",
  //       "desc 1",
  //       "100.23",
  //       "desc 1",
  //       "100.23",
  //     ],
  //   ],
  // });
  return withTracking(
    "previewCsv",
    `${API_PREFIX}/document/${documentId}/previewCsv`,
    {
      method: "post",
      headers: {
        Authorization: `Bearer ${getJwt()}`,
        Accept: "application/json",
        "Content-Type": "application/json",
      },
    }
  ).then((x) => x);
};

export const previewProspectCsv = (documentId: number): Promise<any> => {
  return withTracking(
    "previewProspectCsv",
    `${API_PREFIX}/document/${documentId}/previewProspectCsv`,
    {
      method: "post",
      headers: {
        Authorization: `Bearer ${getJwt()}`,
        Accept: "application/json",
        "Content-Type": "application/json",
      },
    }
  ).then((x) => x);
};

export const previewVendorsCsv = (documentId: number): Promise<any> => {
  return withTracking(
    "previewCsv",
    `${API_PREFIX}/document/${documentId}/previewVendorsCsv`,
    {
      method: "post",
      headers: {
        Authorization: `Bearer ${getJwt()}`,
        Accept: "application/json",
        "Content-Type": "application/json",
      },
    }
  ).then((x) => x);
};

export const createCustomFormat = (
  name: CustomFormat["name"],
  data: CustomFormat["data"],
  type: CustomFormatType
): Promise<any> => {
  return withTracking("createCustomFormat", `${API_PREFIX}/customFormat`, {
    method: "post",
    headers: {
      Authorization: `Bearer ${getJwt()}`,
      Accept: "application/json",
      "Content-Type": "application/json",
    },
    body: JSON.stringify({
      name,
      data: JSON.stringify(data),
      type,
    }),
  });
};

export const updateCustomFormat = (
  customFormat: CustomFormat
): Promise<any> => {
  // Caller sometimes passes a freshly-built CustomFormat that has no id yet.
  // Sending /customFormat/undefined causes a Postgres bigint cast error
  // (DOCUCLIPPER-1JT, ~6.5k events). Route to create instead.
  if (customFormat.id == null || String(customFormat.id) === "undefined") {
    return createCustomFormat(customFormat);
  }
  return withTracking(
    "updateCustomFormat",
    `${API_PREFIX}/customFormat/${customFormat.id}`,
    {
      method: "post",
      headers: {
        Authorization: `Bearer ${getJwt()}`,
        Accept: "application/json",
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        name: customFormat.name,
        data: JSON.stringify(customFormat.data),
      }),
    }
  ).then(() => {
    store.dispatch({
      type: "DownloadInvoiceOptions/setReloadExportPreview",
      payload: true,
    });
  });
};

export const deleteCustomFormat = (id: string): Promise<any> => {
  if (id == null || String(id) === "undefined") {
    return Promise.resolve();
  }
  return withTracking(
    "deleteCustomFormat",
    `${API_PREFIX}/customFormat/${id}`,
    {
      method: "delete",
      headers: {
        Authorization: `Bearer ${getJwt()}`,
        Accept: "application/json",
        "Content-Type": "application/json",
      },
    }
  );
};

export const updateColumnConfigServer = (
  templateId: number,
  fieldName: string,
  data: { [k: string]: any }
): Promise<any> => {
  return withTracking(
    "updateColumnConfig",
    `${API_PREFIX}/columnConfig/${templateId}`,
    {
      method: "post",
      headers: {
        Authorization: `Bearer ${getJwt()}`,
        Accept: "application/json",
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        fieldName,
        data,
      }),
    }
  );
};

export const getBullMqJobs = ({ jobId }: { jobId: string }) => {
  return withTracking(
    "getBullMqJobs",
    `${API_PREFIX}/bullmq?jobId=${jobId}`,
    {
      headers: {
        Authorization: `Bearer ${getJwt()}`,
      },
    },
    undefined,
    120000
  );
};

export const trackPurchase = (amount: number) => {
  if (window && (window as any).gtag) {
    (window as any).gtag("event", "purchase", {
      value: amount,
      currency: "USD",
      send_to: "G-TMN0RY3CC7",
      // debug_mode: true,
    });
  }
};

export const trackSignup = () => {
  if (window && (window as any).gtag) {
    (window as any).gtag("event", "sign_up", {
      send_to: "G-TMN0RY3CC7",
      // debug_mode: true,
    });
  }
};

export const getQboTaxCodes = (integrationId: string) => {
  return withTracking(
    "getQboTaxCodes",
    `${API_PREFIX}/qbo/taxCodes?integrationId=${integrationId}`,
    {
      method: "GET",
      headers: {
        Authorization: `Bearer ${getJwt()}`,
        Accept: "application/json",
        "Content-Type": "application/json",
      },
    }
  ).then((rsp) => rsp.taxCodes);
};

export const getQboCustomers = (integrationId: number): Promise<any> => {
  return withTracking(
    "getQboCustomers",
    `${API_PREFIX}/qbo/customers?integrationId=${integrationId}`,
    {
      method: "get",
      headers: {
        Authorization: `Bearer ${getJwt()}`,
        Accept: "application/json",
        "Content-Type": "application/json",
      },
    }
  ).then((rsp) => rsp.customers);
};

export const getQboVendors = (integrationId: number): Promise<any> => {
  return withTracking(
    "getQboVendors",
    `${API_PREFIX}/qbo/vendors?integrationId=${integrationId}`,
    {
      method: "get",
      headers: {
        Authorization: `Bearer ${getJwt()}`,
        Accept: "application/json",
        "Content-Type": "application/json",
      },
    }
  ).then((rsp) => rsp.vendors);
};

export const getQboAccounts = (integrationId: number, paymentType: boolean): Promise<any> => {
  return withTracking(
    "getQboAccounts",
    `${API_PREFIX}/qbo/accounts?integrationId=${integrationId}&paymentType=${paymentType}`,
    {
      method: "get",
      headers: {
        Authorization: `Bearer ${getJwt()}`,
        Accept: "application/json",
        "Content-Type": "application/json",
      },
    }
  ).then((rsp) => rsp.accounts);
};


export const getQboItems = (integrationId: number): Promise<any> => {
  return withTracking(
    "getQboAccounts",
    `${API_PREFIX}/qbo/items?integrationId=${integrationId}`,
    {
      method: "get",
      headers: {
        Authorization: `Bearer ${getJwt()}`,
        Accept: "application/json",
        "Content-Type": "application/json",
      },
    }
  ).then((rsp) => rsp.items);
};

// Search functions with filtering support for type-ahead/autocomplete
export const searchQboCustomers = (integrationId: number, searchTerm: string): Promise<any> => {
  return withTracking(
    "searchQboCustomers",
    `${API_PREFIX}/qbo/customers?integrationId=${integrationId}&search=${encodeURIComponent(searchTerm)}`,
    {
      method: "get",
      headers: {
        Authorization: `Bearer ${getJwt()}`,
        Accept: "application/json",
        "Content-Type": "application/json",
      },
    }
  ).then((rsp) => rsp.customers);
};

export const searchQboVendors = (integrationId: number, searchTerm: string): Promise<any> => {
  return withTracking(
    "searchQboVendors",
    `${API_PREFIX}/qbo/vendors?integrationId=${integrationId}&search=${encodeURIComponent(searchTerm)}`,
    {
      method: "get",
      headers: {
        Authorization: `Bearer ${getJwt()}`,
        Accept: "application/json",
        "Content-Type": "application/json",
      },
    }
  ).then((rsp) => rsp.vendors);
};

export const searchQboAccounts = (integrationId: number, paymentType: boolean, searchTerm: string): Promise<any> => {
  return withTracking(
    "searchQboAccounts",
    `${API_PREFIX}/qbo/accounts?integrationId=${integrationId}&paymentType=${paymentType}&search=${encodeURIComponent(searchTerm)}`,
    {
      method: "get",
      headers: {
        Authorization: `Bearer ${getJwt()}`,
        Accept: "application/json",
        "Content-Type": "application/json",
      },
    }
  ).then((rsp) => rsp.accounts);
};

export const searchQboItems = (integrationId: number, searchTerm: string): Promise<any> => {
  return withTracking(
    "searchQboItems",
    `${API_PREFIX}/qbo/items?integrationId=${integrationId}&search=${encodeURIComponent(searchTerm)}`,
    {
      method: "get",
      headers: {
        Authorization: `Bearer ${getJwt()}`,
        Accept: "application/json",
        "Content-Type": "application/json",
      },
    }
  ).then((rsp) => rsp.items);
};

export const searchQboTaxCodes = (integrationId: string, searchTerm: string): Promise<any> => {
  return withTracking(
    "searchQboTaxCodes",
    `${API_PREFIX}/qbo/taxCodes?integrationId=${integrationId}&search=${encodeURIComponent(searchTerm)}`,
    {
      method: "GET",
      headers: {
        Authorization: `Bearer ${getJwt()}`,
        Accept: "application/json",
        "Content-Type": "application/json",
      },
    }
  ).then((rsp) => rsp.taxCodes);
};

export const getQboPurchaseOrderMatch = (
  integrationId: number,
  transactionId: number
): Promise<any> => {
  return withTracking(
    "getQboPurchaseOrderMatch",
    `${API_PREFIX}/qbo/purchaseOrders/match?integrationId=${integrationId}&transactionId=${transactionId}`,
    {
      method: "get",
      headers: {
        Authorization: `Bearer ${getJwt()}`,
        Accept: "application/json",
        "Content-Type": "application/json",
      },
    }
  );
};

export const searchQboPurchaseOrders = (
  integrationId: number,
  transactionId: number,
  search: string
): Promise<any> => {
  return withTracking(
    "searchQboPurchaseOrders",
    `${API_PREFIX}/qbo/purchaseOrders/search?integrationId=${integrationId}&transactionId=${transactionId}&search=${encodeURIComponent(
      search
    )}`,
    {
      method: "get",
      headers: {
        Authorization: `Bearer ${getJwt()}`,
        Accept: "application/json",
        "Content-Type": "application/json",
      },
    }
  );
};

export const confirmQboPurchaseOrderMatch = ({
  integrationId,
  transactionId,
  purchaseOrderId,
  acceptWithDifferences = false,
}: {
  integrationId: number;
  transactionId: number;
  purchaseOrderId: string;
  acceptWithDifferences?: boolean;
}): Promise<any> => {
  return withTracking(
    "confirmQboPurchaseOrderMatch",
    `${API_PREFIX}/qbo/purchaseOrders/match/confirm`,
    {
      method: "post",
      headers: {
        Authorization: `Bearer ${getJwt()}`,
        Accept: "application/json",
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        integrationId,
        transactionId,
        purchaseOrderId,
        acceptWithDifferences,
      }),
    }
  );
};

export const removeQboPurchaseOrderMatch = (
  integrationId: number,
  transactionId: number,
  purchaseOrderId?: string
): Promise<any> => {
  return withTracking(
    "removeQboPurchaseOrderMatch",
    `${API_PREFIX}/qbo/purchaseOrders/match/remove`,
    {
      method: "post",
      headers: {
        Authorization: `Bearer ${getJwt()}`,
        Accept: "application/json",
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        integrationId,
        transactionId,
        purchaseOrderId,
      }),
    }
  );
};

export const getQboBankTransactionMatch = (
  integrationId: number,
  transactionId: number
): Promise<any> => {
  return withTracking(
    "getQboBankTransactionMatch",
    `${API_PREFIX}/qbo/bankTransactions/match?integrationId=${integrationId}&transactionId=${transactionId}`,
    {
      method: "get",
      headers: {
        Authorization: `Bearer ${getJwt()}`,
        Accept: "application/json",
        "Content-Type": "application/json",
      },
    }
  );
};

export const searchQboBankTransactions = (
  integrationId: number,
  transactionId: number,
  search: string
): Promise<any> => {
  return withTracking(
    "searchQboBankTransactions",
    `${API_PREFIX}/qbo/bankTransactions/search?integrationId=${integrationId}&transactionId=${transactionId}&search=${encodeURIComponent(
      search
    )}`,
    {
      method: "get",
      headers: {
        Authorization: `Bearer ${getJwt()}`,
        Accept: "application/json",
        "Content-Type": "application/json",
      },
    }
  );
};

export const confirmQboBankTransactionMatch = ({
  integrationId,
  transactionId,
  bankTransactionId,
}: {
  integrationId: number;
  transactionId: number;
  bankTransactionId: string;
}): Promise<any> => {
  return withTracking(
    "confirmQboBankTransactionMatch",
    `${API_PREFIX}/qbo/bankTransactions/match/confirm`,
    {
      method: "post",
      headers: {
        Authorization: `Bearer ${getJwt()}`,
        Accept: "application/json",
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        integrationId,
        transactionId,
        bankTransactionId,
      }),
    }
  );
};

export const removeQboBankTransactionMatch = (
  integrationId: number,
  transactionId: number,
  bankTransactionId?: string
): Promise<any> => {
  return withTracking(
    "removeQboBankTransactionMatch",
    `${API_PREFIX}/qbo/bankTransactions/match/remove`,
    {
      method: "post",
      headers: {
        Authorization: `Bearer ${getJwt()}`,
        Accept: "application/json",
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        integrationId,
        transactionId,
        bankTransactionId,
      }),
    }
  );
};

export const getXeroBankTransactionMatch = (
  integrationId: number,
  transactionId: number
): Promise<any> => {
  return withTracking(
    "getXeroBankTransactionMatch",
    `${API_PREFIX}/xero/bankTransactions/match?integrationId=${integrationId}&transactionId=${transactionId}`,
    {
      method: "get",
      headers: {
        Authorization: `Bearer ${getJwt()}`,
        Accept: "application/json",
        "Content-Type": "application/json",
      },
    }
  );
};

export const searchXeroBankTransactions = (
  integrationId: number,
  transactionId: number,
  search: string
): Promise<any> => {
  return withTracking(
    "searchXeroBankTransactions",
    `${API_PREFIX}/xero/bankTransactions/search?integrationId=${integrationId}&transactionId=${transactionId}&search=${encodeURIComponent(
      search
    )}`,
    {
      method: "get",
      headers: {
        Authorization: `Bearer ${getJwt()}`,
        Accept: "application/json",
        "Content-Type": "application/json",
      },
    }
  );
};

export const confirmXeroBankTransactionMatch = ({
  integrationId,
  transactionId,
  bankTransactionId,
}: {
  integrationId: number;
  transactionId: number;
  bankTransactionId: string;
}): Promise<any> => {
  return withTracking(
    "confirmXeroBankTransactionMatch",
    `${API_PREFIX}/xero/bankTransactions/match/confirm`,
    {
      method: "post",
      headers: {
        Authorization: `Bearer ${getJwt()}`,
        Accept: "application/json",
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        integrationId,
        transactionId,
        bankTransactionId,
      }),
    }
  );
};

export const removeXeroBankTransactionMatch = (
  integrationId: number,
  transactionId: number,
  bankTransactionId?: string
): Promise<any> => {
  return withTracking(
    "removeXeroBankTransactionMatch",
    `${API_PREFIX}/xero/bankTransactions/match/remove`,
    {
      method: "post",
      headers: {
        Authorization: `Bearer ${getJwt()}`,
        Accept: "application/json",
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        integrationId,
        transactionId,
        bankTransactionId,
      }),
    }
  );
};

export const createQboInvoice2 = (
  integrationId: number,
  transactionId: number,
  customer: { name: string; value: string },
  options: { forceImport?: boolean } = {}
): Promise<any> => {
  return withTracking("createQboInvoice2", `${API_PREFIX}/qbo/invoice`, {
    method: "post",
    headers: {
      Authorization: `Bearer ${getJwt()}`,
      Accept: "application/json",
      "Content-Type": "application/json",
    },
    body: JSON.stringify({
      integrationId,
      transactionId,
      customer,
      ...options
    }),
  });
};

export const createQboBill2 = (
  integrationId: number,
  transactionId: number,
  vendor: { name: string; value: string },
  options: { forceImport?: boolean } = {}
): Promise<any> => {
  return withTracking("createQboBill2", `${API_PREFIX}/qbo/bill`, {
    method: "post",
    headers: {
      Authorization: `Bearer ${getJwt()}`,
      Accept: "application/json",
      "Content-Type": "application/json",
    },
    body: JSON.stringify({
      integrationId,
      transactionId,
      vendor,
      ...options
    }),
  });
};

export const createQboExpense2 = (
  integrationId: number,
  transactionId: number,
  account: { name: string; value: string },
  customer: { name: string; value: string },
  options: { forceImport?: boolean } = {}
): Promise<any> => {
  return withTracking("createQboBill2", `${API_PREFIX}/qbo/purchase`, {
    method: "post",
    headers: {
      Authorization: `Bearer ${getJwt()}`,
      Accept: "application/json",
      "Content-Type": "application/json",
    },
    body: JSON.stringify({
      integrationId,
      transactionId,
      account,
      customer,
      ...options
    }),
  });
};

// Xero-specific invoice/bill creation functions
// Note: Backend reads all invoice data from the transaction, only needs integrationId, transactionId, and forceImport
export const createXeroInvoice2 = (
  integrationId: number,
  transactionId: number,
  options: { forceImport?: boolean } = {}
): Promise<any> => {
  return withTracking("createXeroInvoice2", `${API_PREFIX}/xero/invoice`, {
    method: "post",
    headers: {
      Authorization: `Bearer ${getJwt()}`,
      Accept: "application/json",
      "Content-Type": "application/json",
    },
    body: JSON.stringify({
      integrationId: String(integrationId),
      transactionId: String(transactionId),
      forceImport: options.forceImport || false,
    }),
  });
};

export const createXeroBill2 = (
  integrationId: number,
  transactionId: number,
  options: { forceImport?: boolean } = {}
): Promise<any> => {
  return withTracking("createXeroBill2", `${API_PREFIX}/xero/bill`, {
    method: "post",
    headers: {
      Authorization: `Bearer ${getJwt()}`,
      Accept: "application/json",
      "Content-Type": "application/json",
    },
    body: JSON.stringify({
      integrationId: String(integrationId),
      transactionId: String(transactionId),
      forceImport: options.forceImport || false,
    }),
  });
};

export const createQboCustomer = (
  integrationId: number,
  name: string
): Promise<any> => {
  return withTracking("createQboCustomer", `${API_PREFIX}/qbo/customer`, {
    method: "post",
    headers: {
      Authorization: `Bearer ${getJwt()}`,
      Accept: "application/json",
      "Content-Type": "application/json",
    },
    body: JSON.stringify({
      integrationId,
      name,
    }),
  });
};

export const saveSurveyResults = (surveyId, data): Promise<any> => {
  return withTracking("saveSurveyResults", `${API_PREFIX}/user/saveSurvey`, {
    method: "post",
    headers: {
      Authorization: `Bearer ${getJwt()}`,
      Accept: "application/json",
      "Content-Type": "application/json",
    },
    body: JSON.stringify({
      surveyId,
      data,
    }),
  });
};

export const getPendingSurvey = (): Promise<any> => {
  return withTracking("getPendingSurvey", `${API_PREFIX}/user/pendingSurvey`, {
    method: "get",
    headers: {
      Authorization: `Bearer ${getJwt()}`,
      Accept: "application/json",
      "Content-Type": "application/json",
    },
  });
};

export const createQboVendor = (
  integrationId: number,
  name: string
): Promise<any> => {
  return withTracking("createQboVendor", `${API_PREFIX}/qbo/vendor`, {
    method: "post",
    headers: {
      Authorization: `Bearer ${getJwt()}`,
      Accept: "application/json",
      "Content-Type": "application/json",
    },
    body: JSON.stringify({
      integrationId,
      name,
    }),
  });
};

export const createQboAccount = (
  integrationId: number,
  name: string
): Promise<any> => {
  return withTracking("createQboAccount", `${API_PREFIX}/qbo/account`, {
    method: "post",
    headers: {
      Authorization: `Bearer ${getJwt()}`,
      Accept: "application/json",
      "Content-Type": "application/json",
    },
    body: JSON.stringify({
      integrationId,
      name,
    }),
  });
};

// ============================================================
// Xero API Functions
// ============================================================

export const getXeroContacts = (
  integrationId: number,
  search?: string
): Promise<any> => {
  const searchParam = search ? `&search=${encodeURIComponent(search)}` : "";
  return withTracking(
    "getXeroContacts",
    `${API_PREFIX}/xero/contacts?integrationId=${integrationId}${searchParam}`,
    {
      method: "get",
      headers: {
        Authorization: `Bearer ${getJwt()}`,
        Accept: "application/json",
        "Content-Type": "application/json",
      },
    }
  ).then((rsp) => rsp.contacts);
};

export const createXeroContact = (
  integrationId: number,
  name: string
): Promise<any> => {
  return withTracking("createXeroContact", `${API_PREFIX}/xero/contact`, {
    method: "post",
    headers: {
      Authorization: `Bearer ${getJwt()}`,
      Accept: "application/json",
      "Content-Type": "application/json",
    },
    body: JSON.stringify({
      integrationId: String(integrationId),
      name,
    }),
  });
};

export const getXeroAccounts = (integrationId: number, search?: string): Promise<any> => {
  const searchParam = search ? `&search=${encodeURIComponent(search)}` : "";
  return withTracking(
    "getXeroAccounts",
    `${API_PREFIX}/xero/accounts?integrationId=${integrationId}${searchParam}`,
    {
      method: "get",
      headers: {
        Authorization: `Bearer ${getJwt()}`,
        Accept: "application/json",
        "Content-Type": "application/json",
      },
    }
  ).then((rsp) => rsp.accounts);
};

export const getXeroItems = (integrationId: number, search?: string): Promise<any> => {
  const searchParam = search ? `&search=${encodeURIComponent(search)}` : "";
  return withTracking(
    "getXeroItems",
    `${API_PREFIX}/xero/items?integrationId=${integrationId}${searchParam}`,
    {
      method: "get",
      headers: {
        Authorization: `Bearer ${getJwt()}`,
        Accept: "application/json",
        "Content-Type": "application/json",
      },
    }
  ).then((rsp) => rsp.items);
};

// Search functions for async dropdown loading
export const searchXeroContacts = (integrationId: number, searchTerm: string): Promise<any> => {
  return getXeroContacts(integrationId, searchTerm);
};

export const searchXeroAccounts = (integrationId: number, searchTerm: string): Promise<any> => {
  return getXeroAccounts(integrationId, searchTerm);
};

export const searchXeroItems = (integrationId: number, searchTerm: string): Promise<any> => {
  return getXeroItems(integrationId, searchTerm);
};

// Helper function to generate a code from a name (up to 10 characters)
const generateCode = (name: string): string => {
  // Remove special characters, convert to uppercase, take first 10 chars
  return name
    .replace(/[^a-zA-Z0-9]/g, "")
    .toUpperCase()
    .substring(0, 10) || "NEW";
};

// Create functions
export const createXeroAccount = (
  integrationId: number,
  name: string,
  options?: { code?: string; type?: string; description?: string }
): Promise<any> => {
  const code = options?.code || generateCode(name);
  const type = options?.type || "EXPENSE"; // Default to EXPENSE if not specified
  const body: any = {
    integrationId: String(integrationId),
    name,
    code,
    type,
  };
  if (options?.description) {
    body.description = options.description;
  }
  return withTracking("createXeroAccount", `${API_PREFIX}/xero/account`, {
    method: "post",
    headers: {
      Authorization: `Bearer ${getJwt()}`,
      Accept: "application/json",
      "Content-Type": "application/json",
    },
    body: JSON.stringify(body),
  });
};

export const createXeroItem = (
  integrationId: number,
  name: string,
  options?: { code?: string }
): Promise<any> => {
  const code = options?.code || generateCode(name);
  return withTracking("createXeroItem", `${API_PREFIX}/xero/item`, {
    method: "post",
    headers: {
      Authorization: `Bearer ${getJwt()}`,
      Accept: "application/json",
      "Content-Type": "application/json",
    },
    body: JSON.stringify({
      integrationId: String(integrationId),
      code,
      name,
    }),
  });
};

export const getXeroTaxRates = (integrationId: number): Promise<any> => {
  return withTracking(
    "getXeroTaxRates",
    `${API_PREFIX}/xero/taxRates?integrationId=${integrationId}`,
    {
      method: "get",
      headers: {
        Authorization: `Bearer ${getJwt()}`,
        Accept: "application/json",
        "Content-Type": "application/json",
      },
    }
  ).then((rsp) => rsp.taxRates);
};

export const getTagRegexes = (type: TagRegexesType): Promise<any> => {
  return withTracking(
    "getTagRegexes",
    `${API_PREFIX}/tagRegexes?type=${type}`,
    {
      method: "get",
      headers: {
        Authorization: `Bearer ${getJwt()}`,
        Accept: "application/json",
        "Content-Type": "application/json",
      },
    }
  );
};

export const getAdminTagRegexes = (type: TagRegexesType): Promise<any> => {
  return withTracking(
    "getTagRegexes",
    `${API_PREFIX}/tagRegexes/admin?type=${type}`,
    {
      method: "get",
      headers: {
        Authorization: `Bearer ${getJwt()}`,
        Accept: "application/json",
        "Content-Type": "application/json",
      },
    }
  );
};

export const getTaxRegexesByType = (type: TagRegexesType, integrationId: number | null, docType: DocType): Promise<any> => {
  return withTracking(
    "getTagRegexesByType",
    `${API_PREFIX}/tagRegexes/${type}?integrationId=${integrationId}&docType=${docType}`,
    {
      method: "get",
      headers: {
        Authorization: `Bearer ${getJwt()}`,
        Accept: "application/json",
        "Content-Type": "application/json",
      },
    }
  );
};

export const uploadVendorsCsv = ({
  name,
  documentId,
  mapping,
}): Promise<any> => {
  return withTracking(
    "uploadVendorsCsv",
    `${API_PREFIX}/tagRegexes/uploadVendorsCsv`,
    {
      method: "post",
      headers: {
        Authorization: `Bearer ${getJwt()}`,
        Accept: "application/json",
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        name,
        documentId,
        mapping,
      }),
    }
  );
};

export const getFeatureGates = (): Promise<any> => {
  return withTracking("getFeatureGates", `${API_PREFIX}/featureGate`, {
    method: "get",
    headers: {
      Authorization: `Bearer ${getJwt()}`,
      Accept: "application/json",
      "Content-Type": "application/json",
    },
  });
};

export const getFeatureGatesByPlanEnabled = (featureId): Promise<boolean> => {
  return withTracking(
    "getFeatureGates",
    `${API_PREFIX}/featureGateByPlan/${featureId}/enabled`,
    {
      method: "get",
      headers: {
        Authorization: `Bearer ${getJwt()}`,
        Accept: "application/json",
        "Content-Type": "application/json",
      },
    }
  ).then((x) => x.enabled);
};

//
// tag regexes set
//
export const createTagRegexesSet = (name, tagRegexesId, type): Promise<any> => {
  return withTracking("createTagRegexesSet", `${API_PREFIX}/tagRegexesSet`, {
    method: "post",
    headers: {
      Authorization: `Bearer ${getJwt()}`,
      Accept: "application/json",
      "Content-Type": "application/json",
    },
    body: JSON.stringify({
      name,
      tagRegexesId,
      type,
    }),
  });
};

export const createTagRegexWithSet = (name, tag, tagLabel, regexes, rule, type): Promise<any> => {
  return withTracking("createTagRegexWithSet", `${API_PREFIX}/tagRegexesSet/tagRegexWithSet`, {
    method: "post",
    headers: {
      Authorization: `Bearer ${getJwt()}`,
      Accept: "application/json",
      "Content-Type": "application/json",
    },
    body: JSON.stringify({
      name,
      tag,
      tagLabel,
      regexes,
      rule,
      type,
    }),
  });
};

// Download the CSV of (categoryGroup, category, keyword) rows, optionally
// filtered to a single category-group name. Bearer auth is required so we
// fetch with credentials then trigger a Blob download — anchor-clicking the
// URL directly would skip the Authorization header.
export const downloadCategoryKeywordsCsv = async (groupName?: string): Promise<void> => {
  const qs = groupName ? `?name=${encodeURIComponent(groupName)}` : "";
  const url = `${API_PREFIX}/tagRegexes/downloadKeywordsCsv${qs}`;
  const rsp = await fetch(url, {
    method: "GET",
    headers: {
      Authorization: `Bearer ${getJwt()}`,
    },
  });
  if (!rsp.ok) {
    throw new Error(`Download failed (${rsp.status})`);
  }
  const blob = await rsp.blob();
  const objectUrl = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = objectUrl;
  const safeName = (groupName || "all-groups").replace(/[^a-z0-9-_]+/gi, "_");
  a.download = `category-keywords-${safeName}.csv`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(objectUrl);
};

export const cloneTagRegexesSet = (sourceName: string, newName: string, type: string): Promise<any> => {
  return withTracking("cloneTagRegexesSet", `${API_PREFIX}/tagRegexesSet/clone`, {
    method: "post",
    headers: {
      Authorization: `Bearer ${getJwt()}`,
      Accept: "application/json",
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ sourceName, newName, type }),
  });
};

export const updateEmail = ({ email }): Promise<any> => {
  return withTracking("updateEmail", `${API_PREFIX}/user/updateEmail`, {
    method: "post",
    headers: {
      Authorization: `Bearer ${getJwt()}`,
      Accept: "application/json",
      "Content-Type": "application/json",
    },
    body: JSON.stringify({
      email,
    }),
  });
};



export const getUserRatings = (): Promise<any> => {
  return withTracking("updateEmail", `${API_PREFIX}/user/ratings`, {
    method: "get",
    headers: {
      Authorization: `Bearer ${getJwt()}`,
      Accept: "application/json",
      "Content-Type": "application/json",
    },
  }).then(rsp => rsp.jobRatings);
};

export const requestBadRatingUpdate = (): Promise<any> => {
  return withTracking("requestBadRatingUpdate", `${API_PREFIX}/user/requestBadRatingUpdate`, {
    method: "post",
    headers: {
      Authorization: `Bearer ${getJwt()}`,
      Accept: "application/json",
      "Content-Type": "application/json",
    },
    body: JSON.stringify({}),
  });
};


export const updateName = ({ firstName, lastName }): Promise<any> => {
  return withTracking("updateEmail", `${API_PREFIX}/user/updateName`, {
    method: "post",
    headers: {
      Authorization: `Bearer ${getJwt()}`,
      Accept: "application/json",
      "Content-Type": "application/json",
    },
    body: JSON.stringify({
      firstName,
      lastName,
    }),
  });
};

// Persists per-user notification opt-outs. Today only `approvalEmailNotifications`
// (invoice-approval emails) is supported; the endpoint accepts a partial body so
// future toggles (mentions, dunning, weekly summary) can be added without
// changing the call shape on the UI side.
export const updateNotificationSettings = (
  settings: { approvalEmailNotifications?: boolean }
): Promise<any> => {
  return withTracking(
    "updateNotificationSettings",
    `${API_PREFIX}/user/updateNotificationSettings`,
    {
      method: "post",
      headers: {
        Authorization: `Bearer ${getJwt()}`,
        Accept: "application/json",
        "Content-Type": "application/json",
      },
      body: JSON.stringify(settings),
    }
  );
};

export const createGuestAccount = ({
  email,
  firstName,
  lastName,
  password,
}: {
  email: string;
  firstName?: string;
  lastName?: string;
  password?: string;
}): Promise<any> => {
  return withTracking("updateEmail", `${API_PREFIX}/user/updateGuestAccount`, {
    method: "post",
    headers: {
      Authorization: `Bearer ${getJwt()}`,
      Accept: "application/json",
      "Content-Type": "application/json",
    },
    body: JSON.stringify({
      email,
      ...(firstName ? { firstName } : {}),
      ...(lastName ? { lastName } : {}),
      ...(password ? { password } : {}),
    }),
  });
};

export const getSandboxToken = (segment: string): Promise<{ token: string; segment: string }> => {
  return fetch(`${UNAUTH_API_PREFIX}/sandbox/${segment}`).then(r => r.json());
};

export const updateSegment = (data): Promise<any> => {
  return withTracking("updateSegment", `${API_PREFIX}/user/updateSegment`, {
    method: "post",
    headers: {
      Authorization: `Bearer ${getJwt()}`,
      Accept: "application/json",
      "Content-Type": "application/json",
    },
    body: JSON.stringify(data),
  });
};

export const deleteTagRegexesSet = (
  setName,
  type: TagRegexesType
): Promise<any> => {
  return withTracking(
    "deleteTagRegexesSet",
    `${API_PREFIX}/tagRegexesSet/${setName}`,
    {
      method: "delete",
      headers: {
        Authorization: `Bearer ${getJwt()}`,
        Accept: "application/json",
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        type,
      }),
    }
  );
};

//
// tag regexes
//
export const createTagRegexes = (
  tag: string,
  tagLabel: string,
  regexes: string,
  rule: TagCategoryRule
): Promise<any> => {
  return withTracking("createTagRegexes", `${API_PREFIX}/tagRegexes`, {
    method: "post",
    headers: {
      Authorization: `Bearer ${getJwt()}`,
      Accept: "application/json",
      "Content-Type": "application/json",
    },
    body: JSON.stringify({
      tag,
      tagLabel,
      regexes,
      rule,
    }),
  });
};

export const createTagRegexesInvoice = (
  tag: string,
  regexes: string,
  rule: TagInvoiceRule
): Promise<any> => {
  return withTracking("createTagRegexesInvoice", `${API_PREFIX}/tagRegexes/invoice`, {
    method: "post",
    headers: {
      Authorization: `Bearer ${getJwt()}`,
      Accept: "application/json",
      "Content-Type": "application/json",
    },
    body: JSON.stringify({
      tag,
      regexes,
      rule,
    }),
  });
};

export const createTagRule = (rule: TagRule): Promise<any> => {
  return withTracking("createTagRule", `${API_PREFIX}/tagRules`, {
    method: "post",
    headers: {
      Authorization: `Bearer ${getJwt()}`,
      Accept: "application/json",
      "Content-Type": "application/json",
    },
    body: JSON.stringify({
      rule,
    }),
  });
};

export const getJobEmail = (jobType): Promise<any> => {
  return withTracking("getJobEmail", `${API_PREFIX}/jobEmail?jobType=${jobType}`, {
    method: "get",
    headers: {
      Authorization: `Bearer ${getJwt()}`,
      Accept: "application/json",
      "Content-Type": "application/json",
    },
  }).then(rsp => rsp.jobEmail);
};

export const updateJobEmail = (
  id,
  email: string
): Promise<any> => {
  return withTracking("updateJobEmail", `${API_PREFIX}/jobEmail/${id}`, {
    method: "post",
    headers: {
      Authorization: `Bearer ${getJwt()}`,
      Accept: "application/json",
      "Content-Type": "application/json",
    },
    body: JSON.stringify({
      email
    }),
  });
};

export const updateTagRegexes = (
  id,
  tag: string,
  tagLabel: string,
  regexes: string,
  rule: TagCategoryRule
): Promise<any> => {
  return withTracking("updateTagRegexes", `${API_PREFIX}/tagRegexes/${id}`, {
    method: "post",
    headers: {
      Authorization: `Bearer ${getJwt()}`,
      Accept: "application/json",
      "Content-Type": "application/json",
    },
    body: JSON.stringify({
      tag,
      tagLabel,
      regexes,
      rule
    }),
  });
};

export const updateTagRegexesInvoice = (
  id,
  rule: TagInvoiceRule
): Promise<any> => {
  return withTracking("updateTagRegexesInvoice", `${API_PREFIX}/tagRegexes/${id}/invoice`, {
    method: "post",
    headers: {
      Authorization: `Bearer ${getJwt()}`,
      Accept: "application/json",
      "Content-Type": "application/json",
    },
    body: JSON.stringify({
      rule,
    }),
  });
};

export const createTagRegexesTransaction = (
  tag: string,
  regexes: string,
  rule: TagTransactionRule
): Promise<any> => {
  return withTracking("createTagRegexesTransaction", `${API_PREFIX}/tagRegexes/transaction`, {
    method: "post",
    headers: {
      Authorization: `Bearer ${getJwt()}`,
      Accept: "application/json",
      "Content-Type": "application/json",
    },
    body: JSON.stringify({
      tag,
      regexes,
      rule,
    }),
  });
};

export const updateTagRegexesTransaction = (
  id,
  rule: TagTransactionRule
): Promise<any> => {
  return withTracking("updateTagRegexesTransaction", `${API_PREFIX}/tagRegexes/${id}/transaction`, {
    method: "post",
    headers: {
      Authorization: `Bearer ${getJwt()}`,
      Accept: "application/json",
      "Content-Type": "application/json",
    },
    body: JSON.stringify({
      rule,
    }),
  });
};

export const updateTagRule = (rule: TagRule): Promise<any> => {
  return withTracking("updateTagRule", `${API_PREFIX}/tagRules/${rule.id}`, {
    method: "post",
    headers: {
      Authorization: `Bearer ${getJwt()}`,
      Accept: "application/json",
      "Content-Type": "application/json",
    },
    body: JSON.stringify({
      rule,
    }),
  });
};

export const deleteTagRegexes = (id): Promise<any> => {
  return withTracking("deleteTagRegexes", `${API_PREFIX}/tagRegexes/${id}`, {
    method: "delete",
    headers: {
      Authorization: `Bearer ${getJwt()}`,
      Accept: "application/json",
      "Content-Type": "application/json",
    },
  });
};

export const runInvoiceRules = (jobId, integrationId, docType, transactionLineId?: number): Promise<any> => {
  return withTracking("runInvoiceRules", `${API_PREFIX}/tagRegexes/runInvoiceRules`, {
    method: "post",
    headers: {
      Authorization: `Bearer ${getJwt()}`,
      Accept: "application/json",
      "Content-Type": "application/json",
    },
    body: JSON.stringify({
      jobId,
      integrationId,
      docType,
      ...(transactionLineId && { transactionLineId }),
    }),
  });
}


export const runInvoiceRulesTagLevel = (l1, l2, integrationId, docType, transactionLineId?: number): Promise<any> => {
  return withTracking("runInvoiceRulesTagLevel", `${API_PREFIX}/tagRegexes/runInvoiceRules`, {
    method: "post",
    headers: {
      Authorization: `Bearer ${getJwt()}`,
      Accept: "application/json",
      "Content-Type": "application/json",
    },
    body: JSON.stringify({
      l1, l2,
      integrationId,
      docType,
      ...(transactionLineId && { transactionLineId }),
    }),
  });
}

export const fetchTeams = (contractId): Promise<any> => {
  return withTracking("fetchTeams", `${API_PREFIX}/team/${contractId}/user`, {
    method: "get",
    headers: {
      Authorization: `Bearer ${getJwt()}`,
      Accept: "application/json",
      "Content-Type": "application/json",
    },
  }).then((x) => x.teams);
};

export const fetchTeamMembers = (teamId: string, contractId: string): Promise<any> => {
  return withTracking("fetchTeamMembers", `${API_PREFIX}/team/${contractId}/${teamId}/members`, {
    method: "get",
    headers: {
      Authorization: `Bearer ${getJwt()}`,
      Accept: "application/json",
      "Content-Type": "application/json",
    },
  }).then((x) => x.members);
};

export const addTeamMember = (teamId: string, contractId: string, email: string, role: string): Promise<any> => {
  return withTracking("addTeamMember", `${API_PREFIX}/team/${contractId}/${teamId}/members`, {
    method: "post",
    headers: {
      Authorization: `Bearer ${getJwt()}`,
      Accept: "application/json",
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ email, role }),
  }).then((x) => x.member);
};

export const removeTeamMember = (teamId: string, contractId: string, userId: string): Promise<any> => {
  return withTracking("removeTeamMember", `${API_PREFIX}/team/${contractId}/${teamId}/members/${userId}`, {
    method: "delete",
    headers: {
      Authorization: `Bearer ${getJwt()}`,
      Accept: "application/json",
      "Content-Type": "application/json",
    },
  });
};

export const updateTeamMemberRole = (teamId: string, contractId: string, userId: string, role: string): Promise<any> => {
  return withTracking("updateTeamMemberRole", `${API_PREFIX}/team/${contractId}/${teamId}/members/${userId}/role`, {
    method: "put",
    headers: {
      Authorization: `Bearer ${getJwt()}`,
      Accept: "application/json",
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ role }),
  }).then((x) => x.member);
};

export const createTeam = (name: string, contractId): Promise<any> => {
  return withTracking("createTeam", `${API_PREFIX}/team/${contractId}`, {
    method: "post",
    headers: {
      Authorization: `Bearer ${getJwt()}`,
      Accept: "application/json",
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ name }),
  });
};


export const makeAdmin = ({ userId, contractId }: { userId: string, contractId: string }): Promise<any> => {
  return withTracking("makeAdmin", `${API_PREFIX}/team/${contractId}/makeAdmin`, {
    method: "post",
    headers: {
      Authorization: `Bearer ${getJwt()}`,
      Accept: "application/json",
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ userId, contractId }),
  });
};

export const removeAdmin = ({ userId, contractId }: { userId: string, contractId: string }): Promise<any> => {
  return withTracking("removeAdmin", `${API_PREFIX}/team/${contractId}/removeAdmin`, {
    method: "post",
    headers: {
      Authorization: `Bearer ${getJwt()}`,
      Accept: "application/json",
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ userId, contractId }),
  });
};

export const getContractAdmins = (contractId: string): Promise<any> => {
  return withTracking("getContractAdmins", `${API_PREFIX}/team/contractAdmins/${contractId}`, {
    method: "get",
    headers: {
      Authorization: `Bearer ${getJwt()}`,
      Accept: "application/json",
      "Content-Type": "application/json",
    },
  });
};

export const cancelAnnualBilledMonthlyPlan = ({
  userId,
  preview,
}: {
  userId: string;
  preview: boolean;
}): Promise<any> => {
  return withTracking(
    "cancelAnnualBilledMonthlyPlan",
    `${API_PREFIX}/support/cancelAnnualBilledMonthly`,
    {
      method: "POST",
      headers: {
        Authorization: `Bearer ${getJwt()}`,
        Accept: "application/json",
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ userId, preview }),
    }
  );
};

export const supportSwitchAdmins = ({
  oldAdminEmail,
  newAdminEmail,
}: {
  oldAdminEmail: string;
  newAdminEmail: string;
}): Promise<any> => {
  return withTracking(
    "supportSwitchAdmins",
    `${API_PREFIX}/support/switchAdmin`,
    {
      method: "POST",
      headers: {
        Authorization: `Bearer ${getJwt()}`,
        Accept: "application/json",
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ oldAdminEmail, newAdminEmail }),
    }
  );
};


export const supportAddStripeCredit = ({
  email,
  amount,
  note
}: {
  email: string;
  amount: number;
  note: string;
}): Promise<any> => {
  return withTracking(
    "supportAddStripeCredit",
    `${API_PREFIX}/support/addStripeCredit`,
    {
      method: "POST",
      headers: {
        Authorization: `Bearer ${getJwt()}`,
        Accept: "application/json",
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ email, amount, note }),
    }
  );
};

export const supportAddCreditForUnusedMonths = ({
  email,

}: {
  email: string;

}): Promise<any> => {
  return withTracking(
    "supportAddCreditForUnusedMonths",
    `${API_PREFIX}/support/addCreditForUnusedMonths`,
    {
      method: "POST",
      headers: {
        Authorization: `Bearer ${getJwt()}`,
        Accept: "application/json",
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ email }),
    }
  );
};

export const supportGetRecentActions = (limit = 20): Promise<any> => {
  return withTracking(
    "supportGetRecentActions",
    `${API_PREFIX}/support/recentActions?limit=${limit}`,
    {
      method: "GET",
      headers: {
        Authorization: `Bearer ${getJwt()}`,
        Accept: "application/json",
      },
    }
  );
};

export const supportGetRefundBrief = (email: string): Promise<any> => {
  return withTracking(
    "supportGetRefundBrief",
    `${API_PREFIX}/support/refundBrief?email=${encodeURIComponent(email)}`,
    {
      method: "GET",
      headers: {
        Authorization: `Bearer ${getJwt()}`,
        Accept: "application/json",
      },
    }
  );
};

export const supportIssueRefund = (payload: {
  userId: number;
  chargeId: string;
  amount: number;
  reason?: string;
  note?: string;
}): Promise<any> => {
  return withTracking(
    "supportIssueRefund",
    `${API_PREFIX}/support/refundIssue`,
    {
      method: "POST",
      headers: {
        Authorization: `Bearer ${getJwt()}`,
        Accept: "application/json",
        "Content-Type": "application/json",
      },
      body: JSON.stringify(payload),
    }
  );
};

export const supportEscalateRefund = (payload: {
  userId: number;
  jobIds?: string[];
  summary: string;
  refundTicketId?: string;
  priority?: number;
}): Promise<any> => {
  return withTracking(
    "supportEscalateRefund",
    `${API_PREFIX}/support/refundEscalate`,
    {
      method: "POST",
      headers: {
        Authorization: `Bearer ${getJwt()}`,
        Accept: "application/json",
        "Content-Type": "application/json",
      },
      body: JSON.stringify(payload),
    }
  );
};

export const supportMoveAccountToContract = ({
  contractId,
  email,
  userId,
}: {
  contractId: number;
  email?: string;
  userId?: number;
}): Promise<any> => {
  return withTracking(
    "supportMoveAccountToContract",
    `${API_PREFIX}/support/moveAccountToContract`,
    {
      method: "POST",
      headers: {
        Authorization: `Bearer ${getJwt()}`,
        Accept: "application/json",
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ contractId, email, userId }),
    }
  );
};

export const supportAutocompleteUsers = ({
  q,
  limit = 10,
}: {
  q: string;
  limit?: number;
}): Promise<{ users: Array<{ id: number; email: string; firstName: string; lastName: string }> }> => {
  const params = new URLSearchParams();
  if (q) params.append("q", q);
  params.append("limit", limit.toString());

  return withTracking(
    "supportAutocompleteUsers",
    `${API_PREFIX}/support/autocomplete/users?${params.toString()}`,
    {
      method: "GET",
      headers: {
        Authorization: `Bearer ${getJwt()}`,
        Accept: "application/json",
      },
    }
  );
};

export const supportAutocompleteContracts = ({
  q,
  limit = 10,
}: {
  q: string;
  limit?: number;
}): Promise<{ contracts: Array<{ id: number; name: string; logo: string | null; admin: { id: number; email: string; firstName: string; lastName: string } }> }> => {
  const params = new URLSearchParams();
  if (q) params.append("q", q);
  params.append("limit", limit.toString());

  return withTracking(
    "supportAutocompleteContracts",
    `${API_PREFIX}/support/autocomplete/contracts?${params.toString()}`,
    {
      method: "GET",
      headers: {
        Authorization: `Bearer ${getJwt()}`,
        Accept: "application/json",
      },
    }
  );
};

export const supportCreateSeat = ({
  contractId,
  email,
  firstName,
  lastName,
}: {
  contractId: number;
  email: string;
  firstName: string;
  lastName: string;
}): Promise<{ message?: string }> => {
  return withTracking(
    "supportCreateSeat",
    `${API_PREFIX}/support/createSeat`,
    {
      method: "POST",
      headers: {
        Authorization: `Bearer ${getJwt()}`,
        Accept: "application/json",
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ contractId, email, firstName, lastName }),
    }
  );
};

export const assignEmailToGuest = ({
  guestEmail,
  newEmail,
  firstName,
  lastName
}: {
  guestEmail: string;
  newEmail: string;
  firstName: string;
  lastName: string;

}): Promise<any> => {
  return withTracking(
    "assignEmailToGuest",
    `${API_PREFIX}/support/assignEmailToGuest`,
    {
      method: "POST",
      headers: {
        Authorization: `Bearer ${getJwt()}`,
        Accept: "application/json",
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ guestEmail, newEmail, firstName, lastName }),
    }
  );
};

export const createQboTaxCode = (integrationId: string, taxCodeName: string) => {
  return withTracking(
    "createQboTaxCode",
    `${API_PREFIX}/qbo/${integrationId}/taxcode`,
    {
      method: "POST",
      headers: {
        Authorization: `Bearer ${getJwt()}`,
        Accept: "application/json",
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ name: taxCodeName }),
    }
  ).then((rsp) => rsp.taxCode);
};

export const getAuditLogStatus = (): Promise<{ enabled: boolean }> => {
  return withTracking("GetAuditLogStatus", `${API_PREFIX}/auditLog/status`, {
    method: "GET",
    headers: {
      Authorization: `Bearer ${getJwt()}`,
      Accept: "application/json",
    },
  });
};

export const enableAuditLog = (enabled: boolean) => {
  return withTracking("EnableAuditLog", `${API_PREFIX}/auditLog/enable`, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${getJwt()}`,
      Accept: "application/json",
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ enabled }),
  });
};

export const exportAuditLog = () => {
  return withTracking("ExportAuditLog", `${API_PREFIX}/auditLog/export`, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${getJwt()}`,
      Accept: "application/json",
      "Content-Type": "application/json",
    },
  });
};


export const setup2FA = (): Promise<{ secret: string; otpauth_url: string }> => {
  return withTracking("setup2FA", `${API_PREFIX}/2fa/setup`, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${getJwt()}`,
      Accept: "application/json",
      "Content-Type": "application/json",
    },
  });
};

export const confirm2FA = (token: string): Promise<void> => {
  return withTracking("confirm2FA", `${API_PREFIX}/2fa/confirm`, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${getJwt()}`,
      Accept: "application/json",
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ token }),
  });
};

export const disable2FA = (): Promise<void> => {
  return withTracking("disable2FA", `${API_PREFIX}/2fa/disable`, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${getJwt()}`,
      Accept: "application/json",
      "Content-Type": "application/json",
    },
  });
};

export const getInvoiceApprovalRules = (): Promise<InvoiceApprovalRule[]> => {
  return withTracking(
    "getInvoiceApprovalRules",
    `${API_PREFIX}/invoice/approvalRules`,
    {
      method: "GET",
      headers: {
        Authorization: `Bearer ${getJwt()}`,
        Accept: "application/json",
        "Content-Type": "application/json",
      },
    }
  );
};

export const createInvoiceApprovalRule = (
  rule: InvoiceApprovalRule
): Promise<InvoiceApprovalRule> => {
  return withTracking(
    "createApprovalRule",
    `${API_PREFIX}/invoice/approvalRules`,
    {
      method: "POST",
      headers: {
        Authorization: `Bearer ${getJwt()}`,
        Accept: "application/json",
        "Content-Type": "application/json",
      },
      body: JSON.stringify(rule),
    }
  );
};

export const updateInvoiceApprovalRule = (
  ruleId: number,
  rule: InvoiceApprovalRule
): Promise<InvoiceApprovalRule> => {
  return withTracking(
    "updateApprovalRule",
    `${API_PREFIX}/invoice/approvalRules/${ruleId}`,
    {
      method: "PUT",
      headers: {
        Authorization: `Bearer ${getJwt()}`,
        Accept: "application/json",
        "Content-Type": "application/json",
      },
      body: JSON.stringify(rule),
    }
  );
};

export const deleteInvoiceApprovalRule = (
  ruleId: number
): Promise<void> => {
  return withTracking(
    "deleteApprovalRule",
    `${API_PREFIX}/invoice/approvalRules/${ruleId}`,
    {
      method: "DELETE",
      headers: {
        Authorization: `Bearer ${getJwt()}`,
        Accept: "application/json",
        "Content-Type": "application/json",
      },
    }
  );
};

export const getInvoiceApprovalHistory = (
  transactionLineId: number
): Promise<any> => {
  return withTracking(
    "getInvoiceApprovalHistory",
    `${API_PREFIX}/invoice-approval/history/${transactionLineId}`,
    {
      method: "GET",
      headers: {
        Authorization: `Bearer ${getJwt()}`,
        Accept: "application/json",
        "Content-Type": "application/json",
      },
    }
  );
};

export const getJobsRequiringApproval = (): Promise<any[]> => {
  return withTracking(
    "getJobsRequiringApproval",
    `${API_PREFIX}/invoice-approval/jobsRequiringApproval`,
    {
      method: "GET",
      headers: {
        Authorization: `Bearer ${getJwt()}`,
        Accept: "application/json",
        "Content-Type": "application/json",
      },
    }
  );
};

// Transaction Line Approval Status
export const updateInvoiceApprovalStatus = (
  transactionLineId: number,
  status: 'approved' | 'rejected',
  comment: string,
): Promise<{
  approvalStatus: InvoiceApprovalStatus;
  isApprover: boolean;
  needsApproval: boolean;
  approvalProgress: ApprovalProgress;
}> => {
  return withTracking(
    "updateApprovalStatus",
    `${API_PREFIX}/invoice/transactionLines/${transactionLineId}/approval`,
    {
      method: "PUT",
      headers: {
        Authorization: `Bearer ${getJwt()}`,
        Accept: "application/json",
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ status, comment }),
    }
  );
};

// Bulk approve/reject N invoice TLs in a single round trip. Server loops
// the per-row logic and returns per-id success/failure (mirrors
// bulkMarkDuplicate's shape) so partial failures are visible.
export const bulkInvoiceApproval = (
  ids: number[],
  status: 'approved' | 'rejected',
  comment?: string | null,
): Promise<{
  total: number;
  successCount: number;
  failureCount: number;
  results: Array<
    | { ok: true; id: number; status: 'approved' | 'rejected'; approvalStatus: InvoiceApprovalStatus }
    | { ok: false; id: number; status: 'unauthorized' | 'not_found' | 'invalid_state' | 'error'; error: string }
  >;
}> => {
  const body: any = { ids, status };
  if (typeof comment !== "undefined") {
    body.comment = comment;
  }
  return withTracking(
    "bulkInvoiceApproval",
    `${API_PREFIX}/invoice/transactionLines/bulkApproval`,
    {
      method: "POST",
      headers: {
        Authorization: `Bearer ${getJwt()}`,
        Accept: "application/json",
        "Content-Type": "application/json",
      },
      body: JSON.stringify(body),
    },
  );
};

export const getInvoiceApprovalStatus = (
  transactionLineId: number
): Promise<{
  approvalStatus: InvoiceApprovalStatus;
  isApprover: boolean;
  needsApproval: boolean;
  approvalProgress: ApprovalProgress;
}> => {
  return withTracking(
    "getApprovalStatus",
    `${API_PREFIX}/invoice/transactionLines/${transactionLineId}/approval`,
    {
      method: "GET",
      headers: {
        Authorization: `Bearer ${getJwt()}`,
        Accept: "application/json",
        "Content-Type": "application/json",
      },
    }
  );
};


export interface InvoiceTimelineEvent {
  type:
    | "extracted"
    | "vendor_matched"
    | "duplicate_detected"
    | "approval_required"
    | "approval_submitted"
    | "approval_voted"
    | "exported"
    | "po_matched"
    | "bank_matched"
    | "category_changed"
    | "due_date_set";
  timestamp: string;
  actor: {
    type: "system" | "user";
    userId?: number;
    name?: string;
    email?: string;
  } | null;
  summary: string;
  detail?: Record<string, any>;
}

export const getInvoiceTimeline = (
  transactionLineId: number
): Promise<{ transactionLineId: number; events: InvoiceTimelineEvent[] }> => {
  return withTracking(
    "getInvoiceTimeline",
    `${API_PREFIX}/invoice/transactionLines/${transactionLineId}/timeline`,
    {
      method: "GET",
      headers: {
        Authorization: `Bearer ${getJwt()}`,
        Accept: "application/json",
        "Content-Type": "application/json",
      },
    }
  );
};


export const getApprovalHistory = (
  transactionLineId: number
): Promise<any> => {
  return withTracking(
    "getApprovalHistory",
    `${API_PREFIX}/invoice/transactionLines/${transactionLineId}/history`,
    {
      method: "GET",
      headers: {
        Authorization: `Bearer ${getJwt()}`,
        Accept: "application/json",
        "Content-Type": "application/json",
      },
    }
  );
};



export const submitForApproval = (
  transactionLineId: number
): Promise<{
  approvalStatus: InvoiceApprovalStatus;
  isApprover: boolean;
  needsApproval: boolean;
  approvalProgress: ApprovalProgress;
}> => {
  return withTracking(
    "submitForApproval",
    `${API_PREFIX}/invoice/transactionLines/${transactionLineId}/submitForApproval`,
    {
      method: "POST",
      headers: {
        Authorization: `Bearer ${getJwt()}`,
        Accept: "application/json",
        "Content-Type": "application/json",
      },
      body: ''
    }
  );
};


// Get potential matches for a transaction line
export const getPotentialMatches = (
  transactionLineId: number,
  filters?: {
    vendorName?: string;
    documentType?: string;
    similarityThreshold?: number;
    limit?: number;
  }
): Promise<PotentialMatches> => {
  const queryParams = new URLSearchParams();

  if (filters) {
    if (filters.vendorName) queryParams.append('vendorName', filters.vendorName);
    if (filters.documentType) queryParams.append('documentType', filters.documentType);
    if (filters.similarityThreshold) queryParams.append('similarityThreshold', filters.similarityThreshold.toString());
    if (filters.limit) queryParams.append('limit', filters.limit.toString());
  }

  const queryString = queryParams.toString();
  const url = `${API_PREFIX}/invoice/transactionLines/${transactionLineId}/matches${queryString ? `?${queryString}` : ''}`;

  return withTracking(
    "getPotentialMatches",
    url,
    {
      method: "GET",
      headers: {
        Authorization: `Bearer ${getJwt()}`,
        Accept: "application/json",
        "Content-Type": "application/json",
      },
    }
  );
};

// Create a new match group
export const createMatchGroup = (
  transactionLineIds: number[],
  requiredMatches?: number
): Promise<MatchGroup> => {
  return withTracking(
    "createMatchGroup",
    `${API_PREFIX}/invoice/matchGroups`,
    {
      method: "POST",
      headers: {
        Authorization: `Bearer ${getJwt()}`,
        Accept: "application/json",
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        transactionLineIds,
        requiredMatches
      })
    }
  );
};

// Update match group status
export const updateMatchGroupStatus = (
  matchGroupId: number,
  status: 'confirmed' | 'rejected',
  comment?: string
): Promise<MatchGroup> => {
  return withTracking(
    "updateMatchGroupStatus",
    `${API_PREFIX}/invoice/matchGroups/${matchGroupId}/status`,
    {
      method: "PUT",
      headers: {
        Authorization: `Bearer ${getJwt()}`,
        Accept: "application/json",
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        status,
        comment
      })
    }
  );
};

// Get match groups for a transaction line
export const getMatchGroups = (
  transactionLineId: number
): Promise<MatchGroup[]> => {
  return withTracking(
    "getMatchGroups",
    `${API_PREFIX}/invoice/transactionLines/${transactionLineId}/matchGroups`,
    {
      method: "GET",
      headers: {
        Authorization: `Bearer ${getJwt()}`,
        Accept: "application/json",
        "Content-Type": "application/json",
      },
    }
  );
};


/*
Tags
*/


export const getTags = (): Promise<Tag[]> => {
  return withTracking(
    "getTags",
    `${API_PREFIX}/tag`,
    {
      method: "GET",
      headers: {
        Authorization: `Bearer ${getJwt()}`,
        Accept: "application/json",
        "Content-Type": "application/json",
      },
    }
  );
};

export const createTag = (data: { name: string; color: string }): Promise<Tag> => {
  return withTracking(
    "createTag",
    `${API_PREFIX}/tag`,
    {
      method: "POST",
      headers: {
        Authorization: `Bearer ${getJwt()}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify(data),
    }
  );
};

export const tagItem = (tagId: number, taggableId: number, taggableType: string): Promise<void> => {
  return withTracking(
    "tagItem",
    `${API_PREFIX}/tag/tag`,
    {
      method: "POST",
      headers: {
        Authorization: `Bearer ${getJwt()}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ tagId, taggableId, taggableType }),
    }
  );
};

export const untagItem = (tagId: number, taggableType: string, taggableId: number): Promise<void> => {
  return withTracking(
    "untagItem",
    `${API_PREFIX}/tag/tag/${tagId}/${taggableType}/${taggableId}`,
    {
      method: "DELETE",
      headers: {
        Authorization: `Bearer ${getJwt()}`,
      },
    }
  );
};

export const getEntityTags = (entityType: string, entityId: number): Promise<Tag[]> => {
  return withTracking(
    "getEntityTags",
    `${API_PREFIX}/tag/${entityType}/${entityId}`,
    {
      method: "GET",
      headers: {
        Authorization: `Bearer ${getJwt()}`,
      },
    }
  );
};

export const getTagTree = (includeDocuments: boolean = false) => {
  const queryParam = includeDocuments ? '?includeDocuments=true' : '';
  return withTracking(
    "GetTagTree",
    `${API_PREFIX}/tagLevel/tree${queryParam}`,
    {
      headers: {
        Authorization: `Bearer ${getJwt()}`,
      },
    }
  ).then((rsp) => rsp.tagLevels);
};

export const assignJobsToTagLevel = (tagLevelId: string, jobIds: any[]) => {
  return withTracking(
    "GetTagLevel",
    `${API_PREFIX}/tagLevel/${tagLevelId}/jobs`,
    {
      method: 'POST',

      headers: {
        Authorization: `Bearer ${getJwt()}`,
      },
      body: JSON.stringify({ jobIds }),

    }
  );
};

export const deleteEntityFromTagLevel = (tagLevelId: string, entityId) => {
  return withTracking(
    "deleteEntityFromTagLevel",
    `${API_PREFIX}/tagLevel/${tagLevelId}/entity/${entityId}`,
    {
      method: 'DELETE',

      headers: {
        Authorization: `Bearer ${getJwt()}`,
      },

    }
  );
};


export const deleteDocumentFromJob = (jobId, documnetId) => {
  return withTracking(
    "deleteDocumentFromJob",
    `${API_PREFIX}/job/${jobId}/documents/${documnetId}`,
    {
      method: 'DELETE',

      headers: {
        Authorization: `Bearer ${getJwt()}`,
      },
    }
  );
};

// Create new tag level
export const createTagLevel = (tagLevelData: TagLevelCreate) => {
  return withTracking(
    "CreateTagLevel",
    `${API_PREFIX}/tagLevel`,
    {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${getJwt()}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(tagLevelData),
    }
  );
};

// Update existing tag level
export const updateTagLevel = (tagLevelId: string, tagLevelData: TagLevelUpdate) => {
  return withTracking(
    "UpdateTagLevel",
    `${API_PREFIX}/tagLevel/${tagLevelId}`,
    {
      method: 'PUT',
      headers: {
        Authorization: `Bearer ${getJwt()}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(tagLevelData),
    }
  );
};

// Delete tag level
export const deleteTagLevel = (tagLevelId: string) => {
  return withTracking(
    "DeleteTagLevel",
    `${API_PREFIX}/tagLevel/${tagLevelId}`,
    {
      method: 'DELETE',
      headers: {
        Authorization: `Bearer ${getJwt()}`,
      },
    }
  );
};

// --- Project I/O (tagLevel) API ---
// Base path: /api/v1/protected/tagLevel

/** Queue a single GDrive folder watch polling pass. Returns 202 Accepted. */
export const triggerGDriveWatch = (): Promise<GDriveWatchQueuedResponse> => {
  return withTracking(
    "TriggerGDriveWatch",
    `${API_PREFIX}/tagLevel/gdrive-watch`,
    {
      method: "POST",
      headers: {
        Authorization: `Bearer ${getJwt()}`,
        "Content-Type": "application/json",
      },
    }
  );
};

/** List all input configs for a project (projectId = TagLevel id with type 'project'). */
export const getInputConfigs = (projectId: number): Promise<InputConfig[]> => {
  return withTracking(
    "GetInputConfigs",
    `${API_PREFIX}/tagLevel/${projectId}/input-configs`,
    {
      headers: {
        Authorization: `Bearer ${getJwt()}`,
      },
    }
  );
};

export const getProjectProcessedFiles = ({
  projectId,
  offset = 0,
  limit = 20,
  date,
}: {
  projectId: number;
  offset?: number;
  limit?: number;
  date?: string;
}): Promise<ProjectProcessedFilesResponse> => {
  const params = new URLSearchParams();
  params.set("offset", String(Math.max(0, offset)));
  params.set("limit", String(Math.max(1, limit)));
  if (date && date.trim()) {
    params.set("date", date.trim());
  }
  return withTracking(
    "GetProjectProcessedFiles",
    `${API_PREFIX}/tagLevel/${projectId}/processed-files?${params.toString()}`,
    {
      headers: {
        Authorization: `Bearer ${getJwt()}`,
      },
    },
  );
};

/** Create one input config. */
export const createInputConfig = (
  projectId: number,
  body: CreateInputConfigRequest
): Promise<InputConfig> => {
  return withTracking(
    "CreateInputConfig",
    `${API_PREFIX}/tagLevel/${projectId}/input-configs`,
    {
      method: "POST",
      headers: {
        Authorization: `Bearer ${getJwt()}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify(body),
    }
  );
};

/** Update one input config (partial update). */
export const updateInputConfig = (
  projectId: number,
  id: number,
  body: Partial<CreateInputConfigRequest>
): Promise<InputConfig> => {
  return withTracking(
    "UpdateInputConfig",
    `${API_PREFIX}/tagLevel/${projectId}/input-configs/${id}`,
    {
      method: "PUT",
      headers: {
        Authorization: `Bearer ${getJwt()}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify(body),
    }
  );
};

/** Delete one input config. Returns 204 No Content. */
export const deleteInputConfig = (
  projectId: number,
  id: number
): Promise<void> => {
  return withTracking(
    "DeleteInputConfig",
    `${API_PREFIX}/tagLevel/${projectId}/input-configs/${id}`,
    {
      method: "DELETE",
      headers: {
        Authorization: `Bearer ${getJwt()}`,
      },
    }
  ).then(() => undefined);
};

/** List all output configs for a project. */
export const getOutputConfigs = (projectId: number): Promise<OutputConfig[]> => {
  return withTracking(
    "GetOutputConfigs",
    `${API_PREFIX}/tagLevel/${projectId}/output-configs`,
    {
      headers: {
        Authorization: `Bearer ${getJwt()}`,
      },
    }
  );
};

/** List webhook event types for webhook output config (config.events). */
export const getOutputConfigEvents = (
  projectId: number
): Promise<WebhookEventType[]> => {
  return withTracking(
    "GetOutputConfigEvents",
    `${API_PREFIX}/tagLevel/${projectId}/output-configs/events`,
    {
      headers: {
        Authorization: `Bearer ${getJwt()}`,
      },
    }
  );
};

/** Create one output config. */
export const createOutputConfig = (
  projectId: number,
  body: CreateOutputConfigRequest
): Promise<OutputConfig> => {
  return withTracking(
    "CreateOutputConfig",
    `${API_PREFIX}/tagLevel/${projectId}/output-configs`,
    {
      method: "POST",
      headers: {
        Authorization: `Bearer ${getJwt()}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify(body),
    }
  );
};

/** Update one output config (partial update). */
export const updateOutputConfig = (
  projectId: number,
  id: number,
  body: Partial<CreateOutputConfigRequest>
): Promise<OutputConfig> => {
  return withTracking(
    "UpdateOutputConfig",
    `${API_PREFIX}/tagLevel/${projectId}/output-configs/${id}`,
    {
      method: "PUT",
      headers: {
        Authorization: `Bearer ${getJwt()}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify(body),
    }
  );
};

/** Delete one output config. Returns 204 No Content. */
export const deleteOutputConfig = (
  projectId: number,
  id: number
): Promise<void> => {
  return withTracking(
    "DeleteOutputConfig",
    `${API_PREFIX}/tagLevel/${projectId}/output-configs/${id}`,
    {
      method: "DELETE",
      headers: {
        Authorization: `Bearer ${getJwt()}`,
      },
    }
  ).then(() => undefined);
};

/** List folders in Drive (project must have Drive integration). Optional parentId; optional q for server-side name search in that folder. */
export const getDriveFolders = (
  projectId: number,
  parentId?: string,
  q?: string
): Promise<DriveFoldersResponse> => {
  const params = new URLSearchParams();
  if (parentId != null && parentId !== "") {
    params.set("parentId", parentId);
  }
  const qt = typeof q === "string" ? q.trim() : "";
  if (qt.length > 0) {
    params.set("q", qt);
  }
  const qs = params.toString();
  const query = qs ? `?${qs}` : "";
  return withTracking(
    "GetDriveFolders",
    `${API_PREFIX}/tagLevel/${projectId}/drive/folders${query}`,
    {
      headers: {
        Authorization: `Bearer ${getJwt()}`,
      },
    }
  );
};

/** Create a folder in Drive. Use returned id as config.folderId in input/output config. */
export const createDriveFolder = (
  projectId: number,
  body: CreateDriveFolderRequest
): Promise<CreateDriveFolderResponse> => {
  return withTracking(
    "CreateDriveFolder",
    `${API_PREFIX}/tagLevel/${projectId}/drive/folders`,
    {
      method: "POST",
      headers: {
        Authorization: `Bearer ${getJwt()}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify(body),
    }
  );
};

/** List OneDrive folders (optional q = server-side search in current folder). */
export const getOneDriveFolders = (
  projectId: number,
  parentId?: string,
  q?: string
): Promise<DriveFoldersResponse> => {
  const params = new URLSearchParams();
  if (parentId != null && parentId !== "") {
    params.set("parentId", parentId);
  }
  const qt = typeof q === "string" ? q.trim() : "";
  if (qt.length > 0) {
    params.set("q", qt);
  }
  const qs = params.toString();
  const query = qs ? `?${qs}` : "";
  return withTracking(
    "GetOneDriveFolders",
    `${API_PREFIX}/tagLevel/${projectId}/onedrive/folders${query}`,
    {
      headers: {
        Authorization: `Bearer ${getJwt()}`,
      },
    }
  );
};

/** Create a folder in OneDrive. */
export const createOneDriveFolder = (
  projectId: number,
  body: CreateDriveFolderRequest
): Promise<CreateDriveFolderResponse> => {
  return withTracking(
    "CreateOneDriveFolder",
    `${API_PREFIX}/tagLevel/${projectId}/onedrive/folders`,
    {
      method: "POST",
      headers: {
        Authorization: `Bearer ${getJwt()}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify(body),
    }
  );
};

export const getDropboxFolders = (
  projectId: number,
  parentId?: string,
  q?: string
): Promise<DriveFoldersResponse> => {
  const params = new URLSearchParams();
  if (parentId != null && parentId !== "") {
    params.set("parentId", parentId);
  }
  const qt = typeof q === "string" ? q.trim() : "";
  if (qt.length > 0) {
    params.set("q", qt);
  }
  const qs = params.toString();
  const query = qs ? `?${qs}` : "";
  return withTracking(
    "GetDropboxFolders",
    `${API_PREFIX}/tagLevel/${projectId}/dropbox/folders${query}`,
    {
      headers: {
        Authorization: `Bearer ${getJwt()}`,
      },
    }
  );
};

export const createDropboxFolder = (
  projectId: number,
  body: CreateDriveFolderRequest
): Promise<CreateDriveFolderResponse> => {
  return withTracking(
    "CreateDropboxFolder",
    `${API_PREFIX}/tagLevel/${projectId}/dropbox/folders`,
    {
      method: "POST",
      headers: {
        Authorization: `Bearer ${getJwt()}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify(body),
    }
  );
};

export const getBoxFolders = (
  projectId: number,
  parentId?: string,
  q?: string
): Promise<DriveFoldersResponse> => {
  const params = new URLSearchParams();
  if (parentId != null && parentId !== "") {
    params.set("parentId", parentId);
  }
  const qt = typeof q === "string" ? q.trim() : "";
  if (qt.length > 0) {
    params.set("q", qt);
  }
  const qs = params.toString();
  const query = qs ? `?${qs}` : "";
  return withTracking(
    "GetBoxFolders",
    `${API_PREFIX}/tagLevel/${projectId}/box/folders${query}`,
    {
      headers: {
        Authorization: `Bearer ${getJwt()}`,
      },
    }
  );
};

export const createBoxFolder = (
  projectId: number,
  body: CreateDriveFolderRequest
): Promise<CreateDriveFolderResponse> => {
  return withTracking(
    "CreateBoxFolder",
    `${API_PREFIX}/tagLevel/${projectId}/box/folders`,
    {
      method: "POST",
      headers: {
        Authorization: `Bearer ${getJwt()}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify(body),
    }
  );
};

// Move project to a different folder
export const moveProject = (projectId: number, newFolderId: number) => {
  return withTracking(
    "MoveProject",
    `${API_PREFIX}/tagLevel/${projectId}/move`,
    {
      method: 'PUT',
      headers: {
        Authorization: `Bearer ${getJwt()}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ newFolderId }),
    }
  );
};

// Move documents to a different project
export const moveDocuments = (targetProjectId: number, sourceProjectId: number, documentIds: number[]) => {
  return withTracking(
    "MoveDocuments",
    `${API_PREFIX}/tagLevel/${targetProjectId}/documents/move`,
    {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${getJwt()}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ sourceProjectId, documentIds }),
    }
  );
};

export interface TeamTagLevelPermission {
  teamId: number;
  tagLevelId: number;
  role: 'READ' | 'WRITE' | 'ADMIN';
  Team?: {
    id: string;
    name: string;
  };
}

export interface UserTagLevelPermissionResponse {
  userId: string;
  tagLevelId: number;
  role: 'READ' | 'WRITE' | 'ADMIN';
  User?: {
    id: string;
    email: string;
    firstName?: string;
    lastName?: string;
  };
}

export interface TagLevelPermissionsResponse {
  teamPermissions: TeamTagLevelPermission[];
  userPermissions: UserTagLevelPermissionResponse[];
}

// Set permission for a team on a tag level (folder/project)
export const setTeamTagLevelPermission = ({ tagId, teamId, role }: { tagId: number; teamId: number; role: 'READ' | 'WRITE' | 'ADMIN' }) => {
  return withTracking(
    "SetTagPermission",
    `${API_PREFIX}/tagLevel/permissions/${tagId}`,
    {
      method: "POST",
      body: JSON.stringify({
        teamId,
        role,
      }),
      headers: {
        Authorization: `Bearer ${getJwt()}`,
        Accept: "application/json",
        "Content-Type": "application/json",
      },
    }
  );
};

// Get all permissions for a tag level
export const getTagLevelPermissions = (tagId: number) => {
  return withTracking(
    "GetTagPermissions",
    `${API_PREFIX}/tagLevel/permissions/${tagId}`,
    {
      method: "GET",
      headers: {
        Authorization: `Bearer ${getJwt()}`,
        Accept: "application/json",
      },
    }
  );
};

// Delete a team's permission for a tag level
export const deleteTagLevelPermission = ({ tagId, teamId }: { tagId: number; teamId: number }) => {
  return withTracking(
    "DeleteTagPermission",
    `${API_PREFIX}/tagLevel/permissions/${tagId}/team/${teamId}`,
    {
      method: "DELETE",
      headers: {
        Authorization: `Bearer ${getJwt()}`,
        Accept: "application/json",
      },
    }
  );
};

export type TagLevelRole = 'READ' | 'WRITE' | 'ADMIN';

export interface UserTagLevelPermission {
  userId: string;
  tagLevelId: number;
  role: TagLevelRole;
}

// Set permission for a user on a tag level (folder/project)
export const setUserTagLevelPermission = ({ tagId, userId, role }: { tagId: number; userId: string; role: TagLevelRole }) => {
  return withTracking(
    "SetUserTagPermission",
    `${API_PREFIX}/tagLevel/permissions/${tagId}`,
    {
      method: "POST",
      body: JSON.stringify({
        userId,
        role,
      }),
      headers: {
        Authorization: `Bearer ${getJwt()}`,
        Accept: "application/json",
        "Content-Type": "application/json",
      },
    }
  );
};

// Delete a user's permission for a tag level
export const deleteUserTagLevelPermission = ({ tagId, userId }: { tagId: number; userId: string }) => {
  return withTracking(
    "DeleteUserTagPermission",
    `${API_PREFIX}/tagLevel/permissions/${tagId}/user/${userId}`,
    {
      method: "DELETE",
      headers: {
        Authorization: `Bearer ${getJwt()}`,
        Accept: "application/json",
      },
    }
  );
};

// ============================================================================
// Project Users API - Assign individual users to L1/L2 projects
// ============================================================================

export type ProjectUserRole = 'READ' | 'WRITE' | 'ADMIN' | 'UPLOAD';

export interface ProjectUser {
  id: string;
  userId: string;
  l1TagLevelId: number;
  l2TagLevelId: number;
  role: ProjectUserRole;
  User?: {
    id: string;
    email: string;
    firstName?: string;
    lastName?: string;
  };
}

export interface CreateProjectUserParams {
  userId: string;
  l1TagLevelId: number;
  l2TagLevelId: number;
  role: ProjectUserRole;
}

// Add a user to a project (L1/L2 combination)
export const createProjectUser = (params: CreateProjectUserParams) => {
  return withTracking(
    "CreateProjectUser",
    `${API_PREFIX}/tagLevel/project-users`,
    {
      method: "POST",
      body: JSON.stringify(params),
      headers: {
        Authorization: `Bearer ${getJwt()}`,
        Accept: "application/json",
        "Content-Type": "application/json",
      },
    }
  );
};

// Get all users assigned to a project
export const getProjectUsers = ({ l1TagLevelId, l2TagLevelId }: { l1TagLevelId: number; l2TagLevelId: number }): Promise<ProjectUser[]> => {
  return withTracking(
    "GetProjectUsers",
    `${API_PREFIX}/tagLevel/project-users/${l1TagLevelId}/${l2TagLevelId}`,
    {
      method: "GET",
      headers: {
        Authorization: `Bearer ${getJwt()}`,
        Accept: "application/json",
      },
    }
  );
};

// Update a project user's role
export const updateProjectUser = ({ projectUserId, role }: { projectUserId: string; role: ProjectUserRole }) => {
  return withTracking(
    "UpdateProjectUser",
    `${API_PREFIX}/tagLevel/project-users/${projectUserId}`,
    {
      method: "PUT",
      body: JSON.stringify({ role }),
      headers: {
        Authorization: `Bearer ${getJwt()}`,
        Accept: "application/json",
        "Content-Type": "application/json",
      },
    }
  );
};

// Remove a user from a project
export const deleteProjectUser = (projectUserId: string) => {
  return withTracking(
    "DeleteProjectUser",
    `${API_PREFIX}/tagLevel/project-users/${projectUserId}`,
    {
      method: "DELETE",
      headers: {
        Authorization: `Bearer ${getJwt()}`,
        Accept: "application/json",
      },
    }
  );
};

export interface TrialUserResponse {
  trialUsers: {
    userId: string;
    email: string;
    firstName: string;
    lastName: string;
    trialStart: string;
    trialEnd: string;
    daysRemaining: number;
    totalJobs: number;
    totalExports: number;
    engagementRate: number;
    daysInTrial: {
      date: string;
      jobsCreated: boolean;
      exportsDownloaded: boolean;
    }[];
    surveyResponses: {
      surveyId: string;
      data: {
        segment: string;
        company_size: string;
        document_volume: string;
        document_frequency: string;
      };
    }[];
    supportTickets: {
      id: string;
      subject: string;
      status: string;
      priority: string;
      createdAt: string;
    }[];
    hasCompletedOnboarding: boolean;
    hasReachedOutToSupport: boolean;
    averageJobsPerActiveDay: number;
    exportRate: number;
  }[];
  totalCount: number;
  filterOptions: {
    segments: string[];
    companySizes: string[];
    documentVolumes: string[];
    documentFrequencies: string[];
  };
  activeFilters: {
    segments: string[];
    companySizes: string[];
    documentVolumes: string[];
    documentFrequencies: string[];
  };
  pagination: {
    currentPage: number;
    totalPages: number;
    limit: number;
    totalCount: number;
  };
}



export const getTrialUsers = (params: TableQueryParams): Promise<TrialUserResponse> => {
  let queryParams = params ? `?options=${encodeURIComponent(JSON.stringify(params))}` : '';
  return withTracking(
    'getTrialUsers',
    `${API_PREFIX}/support/trials${queryParams}`,
    {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${getJwt()}`,
      },
    }
  );
};

export const getChurnedUsers = (params: TableQueryParams): Promise<TrialUserResponse> => {
  let queryParams = params ? `?options=${encodeURIComponent(JSON.stringify(params))}` : '';
  return withTracking(
    'getTrialUsers',
    `${API_PREFIX}/support/churnedUsers${queryParams}`,
    {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${getJwt()}`,
      },
    }
  );
};


export const getBids = (): Promise<any> => {
  return withTracking(
    'getBids',
    `${API_PREFIX}/qbo/bids`,
    {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${getJwt()}`,
      },
    }
  );
};


// Get all webhooks for a contract
export const getWebhooks = () => {
  return withTracking(
    "GetWebhooks",
    `${API_PREFIX}/webhooks`,
    {
      method: "GET",
      headers: {
        Authorization: `Bearer ${getJwt()}`,
        Accept: "application/json",
      },
    }
  );
};

// Create a new webhook
export const createWebhook = ({
  url,
  events
}: {
  url: string;
  events: string[]
}) => {
  return withTracking(
    "CreateWebhook",
    `${API_PREFIX}/webhooks`,
    {
      method: "POST",
      body: JSON.stringify({
        url,
        events,
      }),
      headers: {
        Authorization: `Bearer ${getJwt()}`,
        Accept: "application/json",
        "Content-Type": "application/json",
      },
    }
  );
};

// Delete a webhook
export const deleteWebhook = (webhookId: string) => {
  return withTracking(
    "DeleteWebhook",
    `${API_PREFIX}/webhooks/${webhookId}`,
    {
      method: "DELETE",
      headers: {
        Authorization: `Bearer ${getJwt()}`,
        Accept: "application/json",
      },
    }
  );
};

// Get delivery logs for a webhook
export const getWebhookDeliveries = ({
  webhookId,
  limit,
  offset,
  status
}: {
  webhookId: string;
  limit?: number;
  offset?: number;
  status?: 'pending' | 'success' | 'failed'
}) => {
  const params = new URLSearchParams();
  if (limit) params.append('limit', limit.toString());
  if (offset) params.append('offset', offset.toString());
  if (status) params.append('status', status);

  return withTracking(
    "GetWebhookDeliveries",
    `${API_PREFIX}/webhooks/${webhookId}/deliveries?${params.toString()}`,
    {
      method: "GET",
      headers: {
        Authorization: `Bearer ${getJwt()}`,
        Accept: "application/json",
      },
    }
  );
};

// Retry a failed webhook delivery
export const retryWebhookDelivery = (deliveryId: string) => {
  return withTracking(
    "RetryWebhookDelivery",
    `${API_PREFIX}/webhooks/deliveries/${deliveryId}/retry`,
    {
      method: "POST",
      headers: {
        Authorization: `Bearer ${getJwt()}`,
        Accept: "application/json",
      },
    }
  );
};

// Test a webhook
export const testWebhook = (webhookId: string, eventType: 'bank' | 'invoice') => {
  return withTracking(
    "TestWebhook",
    `${API_PREFIX}/webhooks/${webhookId}/test`,
    {
      method: "POST",
      headers: {
        Authorization: `Bearer ${getJwt()}`,
        Accept: "application/json",
      },
      body: JSON.stringify({ eventType })
    }
  );
};

export const regenerateWebhookSecret = (webhookId: string) => {
  return withTracking(
    "RegenerateWebhookSecret",
    `${API_PREFIX}/webhooks/${webhookId}/regenerate-secret`,
    {
      method: "POST",
      headers: {
        Authorization: `Bearer ${getJwt()}`,
        Accept: "application/json",
      },
    }
  );
};

// Get all transaction line matches for a contract, optionally filtered by source transaction line
export const getTransactionLineMatches = (
  matchType: string,
  sourceTransactionLineId?: number
) => {
  const queryParams = sourceTransactionLineId
    ? `&sourceTransactionLineId=${sourceTransactionLineId}`
    : '';

  return withTracking(
    "getTransactionLineMatches",
    `${API_PREFIX}/transactionLineMatches/?matchType=${matchType}${queryParams}`,
    {
      method: "GET",
      headers: {
        Authorization: `Bearer ${getJwt()}`,
        Accept: "application/json",
        "Content-Type": "application/json",
      },
    }
  );
};

// Create a new transaction line match
export const createTransactionLineMatch = (
  matches: Array<{
    sourceTransactionLineId: number;
    destinationTransactionLineId: number;
  }>,
  matchType: string,
  metadata?: any
) => {
  return withTracking(
    "createTransactionLineMatch",
    `${API_PREFIX}/transactionLineMatches/`,
    {
      method: "POST",
      body: JSON.stringify({
        matches,
        metadata,
        matchType
      }),
      headers: {
        Authorization: `Bearer ${getJwt()}`,
        Accept: "application/json",
        "Content-Type": "application/json",
      },
    }
  );
};

// Delete a transaction line match
export const deleteTransactionLineMatch = (matchId: number) => {
  return withTracking(
    "deleteTransactionLineMatch",
    `${API_PREFIX}/transactionLineMatches/${matchId}`,
    {
      method: "DELETE",
      headers: {
        Authorization: `Bearer ${getJwt()}`,
        Accept: "application/json",
        "Content-Type": "application/json",
      },
    }
  );
};

export const bulkMarkDuplicate = (
  ids: number[],
  duplicateOfTransactionLineId: number | null,
  duplicateOfDocumentId?: number | null,
) => {
  const body: any = { ids, duplicateOfTransactionLineId };
  if (typeof duplicateOfDocumentId !== "undefined") {
    body.duplicateOfDocumentId = duplicateOfDocumentId;
  }
  return withTracking(
    "bulkMarkDuplicate",
    `${API_PREFIX}/invoice/transactionLines/bulkDuplicate`,
    {
      method: "POST",
      headers: {
        Authorization: `Bearer ${getJwt()}`,
        Accept: "application/json",
        "Content-Type": "application/json",
      },
      body: JSON.stringify(body),
    },
  );
};

export const deleteTransactionLine = (transactionLineId: number) => {
  return withTracking(
    "deleteTransactionLine",
    `${API_PREFIX}/job/0/transactionLines/${transactionLineId}`,
    {
      method: "DELETE",
      headers: {
        Authorization: `Bearer ${getJwt()}`,
        Accept: "application/json",
        "Content-Type": "application/json",
      },
    }
  );
};

export const bulkDeleteTransactionLines = (
  ids: number[],
): Promise<{ deleted: number }> => {
  return withTracking(
    "bulkDeleteTransactionLines",
    `${API_PREFIX}/job/0/transactionLines/bulkDelete`,
    {
      method: "POST",
      headers: {
        Authorization: `Bearer ${getJwt()}`,
        Accept: "application/json",
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ ids }),
    },
  );
};


export const createTransactionTag = (transactionLineId: number, tagData: { tag: string }) => {
  return withTracking(
    "create_transaction_tag",
    `${API_PREFIX}/transactionTags`,
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${getJwt()}`,
      },
      body: JSON.stringify({
        transactionLineId,
        ...tagData,
      }),
    }
  );
};

export const getTransactionTags = (transactionLineId: number): Promise<any[]> => {
  return withTracking(
    "get_transaction_tags",
    `${API_PREFIX}/transactionTags/transaction/${transactionLineId}`,
    {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${getJwt()}`,
      },
    }
  );
};

export const getAllTransactionTags = (): Promise<any[]> => {
  return withTracking(
    "get_transaction_tags",
    `${API_PREFIX}/transactionTags/all`,
    {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${getJwt()}`,
      },
    }
  );
};


export const deleteTransactionTag = (tagId: number) => {
  return withTracking(
    "delete_transaction_tag",
    `${API_PREFIX}/transactionTags/${tagId}`,
    {
      method: "DELETE",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${getJwt()}`,
      },
    }
  );
};

// Mobile App Login
export const generateMobileLoginToken = (): Promise<{ token: string; expiresAt: string }> => {
  return withTracking(
    "generate_mobile_login_token",
    `${API_PREFIX}/auth/mobile/generate-token`,
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${getJwt()}`,
      },
    }
  );
};

// Desktop Login (QR Code) - Generate token (public endpoint)
export const generateDesktopLoginToken = (): Promise<{
  token: string;
  qrUrl: string;
  expiresAt: number;
  expiresIn: number;
}> => {
  return withTracking(
    "generate_desktop_login_token",
    `${UNAUTH_API_PREFIX}/auth/desktop-login/generate`,
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
    }
  );
};

// Desktop Login - Check status (polling, public endpoint)
export const checkDesktopLoginStatus = (
  token: string
): Promise<{
  status: "pending" | "approved" | "expired" | "not_found";
  token?: string;
  user?: any;
  expiresAt?: number;
}> => {
  return withTracking(
    "check_desktop_login_status",
    `${UNAUTH_API_PREFIX}/auth/desktop-login/status`,
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ token }),
    }
  );
};

// TagLevel Integrations
export const getTagLevelIntegrationsByTagLevel = (tagLevelId: number): Promise<any[]> => {
  return withTracking(
    "get_tag_level_integrations_by_tag",
    `${API_PREFIX}/tagLevelIntegration/tag-level/${tagLevelId}`,
    {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${getJwt()}`,
      },
    }
  );
};

export const createTagLevelIntegration = (data: {
  tagLevelId: number;
  integrationId: number;
  metadata?: Record<string, any>;
}): Promise<any> => {
  return withTracking(
    "create_tag_level_integration",
    `${API_PREFIX}/tagLevelIntegration`,
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${getJwt()}`,
      },
      body: JSON.stringify(data),
    }
  );
};

export const deleteTagLevelIntegrationByIds = (
  tagLevelId: number,
  integrationId: number
): Promise<any> => {
  return withTracking(
    "delete_tag_level_integration_by_ids",
    `${API_PREFIX}/tagLevelIntegration/tag-level/${tagLevelId}/integration/${integrationId}`,
    {
      method: "DELETE",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${getJwt()}`,
      },
    }
  );
};

// Generic tag-level settings
export const getTagLevelSetting = (
  tagId: number,
  key: string
): Promise<{ setting: { key: string; value: any } | null }> => {
  return withTracking(
    "get_tag_level_setting",
    `${API_PREFIX}/tagLevel/${tagId}/settings/${encodeURIComponent(key)}`,
    {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${getJwt()}`,
      },
    }
  );
};

export const upsertTagLevelSetting = (
  tagId: number,
  key: string,
  value: any
): Promise<{ setting: { key: string; value: any } }> => {
  return withTracking(
    "upsert_tag_level_setting",
    `${API_PREFIX}/tagLevel/${tagId}/settings/${encodeURIComponent(key)}`,
    {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${getJwt()}`,
      },
      body: JSON.stringify({ value }),
    }
  );
};

export const projectAiChatSendMessage = ({
  jobId,
  sessionId,
  message,
}: {
  jobId: string | number;
  sessionId: string;
  message: string;
}): Promise<{ content: string; sessionId: string }> => {
  return withTracking(
    "project_ai_chat_send_message",
    `${API_PREFIX}/projectAiChat/${jobId}/messages`,
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${getJwt()}`,
      },
      body: JSON.stringify({ sessionId, message }),
    }
  );
};

export const projectAiChatGetHistory = ({
  jobId,
  sessionId,
}: {
  jobId: string | number;
  sessionId: string;
}): Promise<{
  messages: Array<{ role: "user" | "assistant"; content: string; createdAt: string }>;
}> => {
  return withTracking(
    "project_ai_chat_get_history",
    `${API_PREFIX}/projectAiChat/${jobId}/sessions/${sessionId}/history`,
    {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${getJwt()}`,
      },
    }
  );
};

export default {
  createGoogleDocsSpreadsheet,
  getTour,
  withTracking,
  withTrackingHelper,
  getSubscription,
  getCredits,
  getInvoices,
  getTemplates,
  uploadFile,
  getIntegrations,
  getIntegrationFeatureFlags,
  createJob,
  getJobs,
  getJobData,
  getJobPositions,
  downloadJobPositionsExcel,
  getJob,
  exportJob,
  getJobExports,
  importTemplate,
  downloadSpreadsheet,
  savePreferenceServer,
  loadPreferenceServer,
  getTemplateAddedColumnMapping,
  addTemplateAddedColumnMapping,
  deleteTemplateAddedColumnMapping,
  deleteDocument,
  uploadCompressedFile,
  getTemplateFieldNames,
  getWordsInRectangle,
  inviteByEmail,
  getJobDocuments,
  getBadRatings,
  markReviewed,
  markJobReviewed,
  getDocumentFraudSignals,
  getDocumentFraudScore,
  getInvoiceApprovalHistory,
  updateInvoiceApprovalStatus,
  bulkInvoiceApproval,
  getInvoiceApprovalStatus,
  getJobsRequiringApproval,
};

//
// Forms typed-table reporting (Phase 1 UI)
// Backed by GET /forms and GET /forms/1099-summary on the api side.
//
const jsonHeaders = () => ({
  Authorization: `Bearer ${getJwt()}`,
  Accept: "application/json",
  "Content-Type": "application/json",
});

export type Form1099Recipient = {
  recipientTin: string | null;
  recipientName: string;
  formCount: number;
  totalAmount: number;
  aboveThreshold: boolean;
  formTypes: string[];
};

export type Form1099SummaryResponse = {
  taxYear: number;
  formType: string;
  threshold: number;
  summary: {
    totalRecipients: number;
    totalAmount: number;
    recipientsAboveThreshold: number;
    formCount: number;
  };
  byRecipient: Form1099Recipient[];
  missingTinCount: number;
};

export type FormRow = {
  id: number;
  documentId: number;
  jobId: number;
  formType: string;
  taxYear: number | null;
  recipientTin: string | null;
  recipientName: string | null;
  payerTin: string | null;
  payerName: string | null;
  totalAmount: string | number | null;
  pageNumber: number | null;
  createdAt: string;
};

export type FormsListResponse = {
  rows: FormRow[];
  count: number;
  limit: number;
  offset: number;
};

export const get1099Summary = (params: {
  taxYear: number;
  formType?: string;
  threshold?: number;
  vendorId?: number;
}): Promise<Form1099SummaryResponse> => {
  const qs = queryString.stringify(
    {
      taxYear: params.taxYear,
      formType: params.formType,
      threshold: params.threshold,
      vendorId: params.vendorId,
    },
    { skipNull: true, skipEmptyString: true }
  );
  return withTracking(
    "get1099Summary",
    `${API_PREFIX}/forms/1099-summary?${qs}`,
    { method: "GET", headers: jsonHeaders() }
  );
};

export const listForms = (
  params: {
    limit?: number;
    offset?: number;
    formType?: string;
    taxYear?: number;
    search?: string;
  } = {}
): Promise<FormsListResponse> => {
  const qs = queryString.stringify(params, {
    skipNull: true,
    skipEmptyString: true,
  });
  const url = qs ? `${API_PREFIX}/forms?${qs}` : `${API_PREFIX}/forms`;
  return withTracking("listForms", url, {
    method: "GET",
    headers: jsonHeaders(),
  });
};

// --- QuickBooks Desktop Chart of Accounts (per-project / tagLevel setting) ---

export interface QbdAccount {
  name: string;
  type?: string;
}

export interface QbdAccountsResponse {
  accounts: QbdAccount[];
}

export const getQbdAccounts = (
  tagLevelId: number | string
): Promise<QbdAccountsResponse> => {
  return withTracking(
    "GetQbdAccounts",
    `${API_PREFIX}/tagLevel/${tagLevelId}/qbd-accounts`,
    {
      method: "GET",
      headers: {
        Authorization: `Bearer ${getJwt()}`,
        Accept: "application/json",
      },
    }
  );
};

export const setQbdAccounts = (
  tagLevelId: number | string,
  raw: string
): Promise<{ accounts: QbdAccount[]; count: number }> => {
  return withTracking(
    "SetQbdAccounts",
    `${API_PREFIX}/tagLevel/${tagLevelId}/qbd-accounts`,
    {
      method: "PUT",
      headers: {
        Authorization: `Bearer ${getJwt()}`,
        Accept: "application/json",
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ raw }),
    }
  );
};

export const deleteQbdAccounts = (tagLevelId: number | string): Promise<void> => {
  return withTracking(
    "DeleteQbdAccounts",
    `${API_PREFIX}/tagLevel/${tagLevelId}/qbd-accounts`,
    {
      method: "DELETE",
      headers: {
        Authorization: `Bearer ${getJwt()}`,
      },
    }
  );
};

export interface IifPreflightResponse {
  totalLines: number;
  uniqueAccountNames: string[];
  unmatchedAccountNames: string[];
  coaCount: number;
}

export const getIifPreflight = (
  tagLevelId: number | string,
  documentIds: string[],
  mode: "invoiceMode" | "bankMode",
  txnType?: "BILL" | "INVOICE"
): Promise<IifPreflightResponse> => {
  return withTracking(
    "IifPreflight",
    `${API_PREFIX}/tagLevel/${tagLevelId}/iif-preflight`,
    {
      method: "POST",
      headers: {
        Authorization: `Bearer ${getJwt()}`,
        Accept: "application/json",
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ documentIds, mode, txnType }),
    }
  );
};
